/* boot diagnostics: a black canvas must explain itself, and tell the operator's machine why */
(function(){
  const report = (what, detail)=>{
    try{ fetch('/v1/log', { method:'POST', headers:{'content-type':'text/plain'},
      body: what + ' :: ' + detail + ' :: ' + navigator.userAgent }); }catch(e){}
    let b = document.getElementById('bootErr');
    if(!b){
      b = document.createElement('div'); b.id = 'bootErr';
      b.style.cssText = 'position:fixed;left:50%;top:44%;transform:translateX(-50%);z-index:99;max-width:min(760px,88vw);'
        + 'background:rgba(24,16,16,.95);border:1px solid rgba(214,120,100,.5);border-radius:4px;padding:16px 20px;'
        + 'font:400 12px/1.6 ui-monospace,Menlo,monospace;color:#f0d8d0;white-space:pre-wrap;box-shadow:0 20px 60px rgba(0,0,0,.7)';
      document.body.appendChild(b);
    }
    b.textContent = (b.textContent ? b.textContent + '\n\n' : '') + what + '\n' + detail;
  };
  addEventListener('error', e=>{
    report('BOOT ERROR', (e.message||'') + '  @ ' + (e.filename||'') + ':' + (e.lineno||'') +
      (e.error && e.error.stack ? '\n' + e.error.stack.split('\n').slice(0,4).join('\n') : ''));
  });
  addEventListener('unhandledrejection', e=>{
    const r = e.reason;
    report('UNHANDLED REJECTION', (r && (r.stack || r.message)) || String(r));
  });
})();
import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { SSAOPass } from 'three/addons/postprocessing/SSAOPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { RGBELoader } from 'three/addons/loaders/RGBELoader.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';
import { Ease, SpringVec3, followShot, establishing, attractDrift, meetShot } from './cinema.js';
import { buildPost } from './post.js';
import * as SF from './surfaces.js';
import { buildWorld } from './world.js';
import { dressAll } from './dressing.js';
import { buildSound } from './sound.js';
import { buildInteract } from './interact.js';
import { initLikeness, attachLikeness, LIKENESS } from './likeness.js';
/* ---------- renderer / scene / camera ---------- */
let renderer;
try {
  renderer = new THREE.WebGLRenderer({ antialias:false, preserveDrawingBuffer:true });  // composer owns AA
} catch (e) {
  const m = document.createElement('div');
  m.style.cssText = 'position:fixed;inset:0;z-index:99;display:flex;align-items:center;justify-content:center;'
    + 'padding:8vw;text-align:center;background:#0c0c10;color:#e6dfcc;'
    + 'font:400 14px/1.7 Futura,Avenir,system-ui,sans-serif';
  m.innerHTML = '<div><div style="letter-spacing:.4em;font-size:22px;margin-bottom:18px">AGENTSEE</div>'
    + 'This browser could not open a 3D context.<br>Chrome or Safari will run it. '
    + 'In Firefox, check that hardware acceleration and WebGL are enabled.</div>';
  document.body.appendChild(m);
  throw e;
}
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.setSize(innerWidth, innerHeight);
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.AgXToneMapping;             // ACES skews warm practicals to white; AgX holds tungsten
renderer.toneMappingExposure = 1.15;
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
renderer.shadowMap.autoUpdate = false;                   // the sun is static: refresh on our schedule,
let shadowTick = 0;                                      // and stop the DOF depth pass redoing it
document.body.prepend(renderer.domElement);
SF.initSurfaces(renderer);
initLikeness(THREE);
const LIKENESS_ON = location.search.includes('likeness');   // sculpted heads: fit still lands at the neck, opt-in for now

const scene = new THREE.Scene();
scene.background = new THREE.Color(0x101013);
scene.fog = new THREE.FogExp2(0x15141b, 0.0052);        // scales itself: clear up close, haze at floor scale

const VIEW = ()=>{                                       // a hidden/zero-size tab must never poison the projection
  const w = Math.max(1, innerWidth||1), h = Math.max(1, innerHeight||1);
  return { w, h, a: w/h };
};
const camera = new THREE.PerspectiveCamera(36, VIEW().a, 0.5, 400);
const controls = new OrbitControls(camera, renderer.domElement);
controls.enableDamping = true; controls.dampingFactor = 0.07;
controls.screenSpacePanning = false;
controls.minPolarAngle = 0.04; controls.maxPolarAngle = 1.44;    // top-down to just above the horizon, never under

/* ---------- image-based ambience + post chain: SSAO grounds it, bloom warms the lamps ---------- */
const pmrem = new THREE.PMREMGenerator(renderer);
scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
scene.environmentIntensity = 0.07;
new RGBELoader().load('/assets/env.hdr', tex => {                 // real HDRI ambience (Poly Haven, CC0)
  tex.mapping = THREE.EquirectangularReflectionMapping;
  scene.environment = tex; scene.environmentIntensity = 0.55;  // the HDRI does the ambient work — flat fill kills realism
  scene.environmentRotation.set(0, -0.9, 0);                   // swing its bright quadrant onto the sun azimuth
}, undefined, ()=>{});
const FAST = location.search.includes('fast');            // ?fast → skip heavy passes (headless checks, weak GPUs)
const SIT_X = parseFloat(new URLSearchParams(location.search).get('sitx') || '-1.42');
/* the film look: DOF with a focus pull, bloom, grade, grain, vignette */
const POST = buildPost(renderer, scene, camera, {
  quality: FAST ? 'fast' : 'cinematic',
  pixelRatio: 1.5,                                            // fill is the budget, not geometry
  bloom: { threshold: 0.86, strength: 0.34, radius: 0.55 },   // only the practicals halate, never the mid-tones
  film:  { vignette: 0.0, grain: 0.030, aberration: 0.0008 }, // the CSS vignette already does that job
  grade: { lift: [0.004, 0.002, 0.001], splitStrength: 0.14,  // a warm grade, not a warm wash
           contrast: 1.18, saturation: 1.05, gain: [0.98, 1.00, 1.04] },
});
const composer = POST.composer;

/* ---------- lights ---------- */
const hemi = new THREE.HemisphereLight(0x4a5570, 0x241d16, 0.34);  // cool sky, warm floor bounce
scene.add(hemi);

const key = new THREE.DirectionalLight(0xffc98a, 3.4);           // 22° dusk sun, raking in through both tall walls
key.position.set(-40, 24, -34);
key.castShadow = true;
key.shadow.mapSize.set(2048, 1024);                             // fitted to the floor: finer texels for a third of the memory
key.shadow.camera.left = -30; key.shadow.camera.right = 25;
key.shadow.camera.top = 13.5; key.shadow.camera.bottom = -12.5;
key.shadow.camera.near = 29;  key.shadow.camera.far = 90;
key.shadow.bias = -0.0002; key.shadow.normalBias = 0.022;
scene.add(key); scene.add(key.target); key.target.position.set(4, 0, 4);

const fill = new THREE.DirectionalLight(0x8fa6c4, 0.55);         // cool sky return
fill.position.set(38, 20, 16); scene.add(fill);
const rim = new THREE.DirectionalLight(0xd9a86e, 0.62);          // warm backlight — figures get an edge
rim.position.set(30, 14, -34); scene.add(rim);

const LAMP_POS = [];
function lamp(x,y,z,color,i,d){ const p=new THREE.PointLight(color,i,d,1.8); p.position.set(x,y,z); scene.add(p); LAMP_POS.push([x,y,z]); return p; }
const LAMP_BEN = lamp(-19, 2.6, -1, 0xffc890, 30, 12);   // Ben's office
const LAMP_CW  = lamp(-6, 2.7, 0, 0xffbe85, 22, 13);     // corridor west
lamp(13, 2.7, 0, 0xffbe85, 16, 13);     // corridor east
lamp(-7, 2.6, -8, 0xffbe85, 18, 10);    // library
lamp(8.5, 2.5, -7.6, 0xffd6a0, 16, 7);  // the iPhone
lamp(-11, 2.4, 7, 0xffc79a, 10, 9);     // courtroom
lamp(-3, 2.4, 7, 0xffc79a, 10, 9);      // buffett
lamp(5, 2.4, 7, 0xffc79a, 10, 9);       // janna
lamp(13, 2.4, 7, 0xffc79a, 10, 9);      // loudfair
lamp(1, 2.4, -8.6, 0xffc79a, 9, 9);     // wozniak bench
lamp(15.5, 2.4, -8.6, 0xffc79a, 8, 9);  // jobs
lamp(27.5, 2.6, 0, 0xffc79a, 9, 9);     // grants table

/* ---------- materials ---------- */
const MATS = {};
function M(hex, rough=0.92, metal=0, extra){
  const k = hex+'|'+rough+'|'+metal+'|'+(extra?JSON.stringify(extra):'');
  if(!MATS[k]){ MATS[k] = new THREE.MeshStandardMaterial(Object.assign({color:hex, roughness:rough, metalness:metal}, extra||{})); }
  return MATS[k];
}
/* real scanned surfaces (Poly Haven, CC0) */
const LOAD = new THREE.LoadingManager();                 // one manager so the opening card can show real progress
const texLoader = new THREE.TextureLoader(LOAD);
const PBR_TEX = {};
function pbrMat(slug, rx=1, rz=1, tint=0xffffff, rough=1){
  const load = (f, cs)=>{
    const key = slug+'/'+f;
    if(!PBR_TEX[key]){ const t = texLoader.load('/assets/tex/'+slug+'/'+f);
      t.wrapS = t.wrapT = THREE.RepeatWrapping; if(cs) t.colorSpace = THREE.SRGBColorSpace;
      t.anisotropy = 16; PBR_TEX[key] = t; }
    const t = PBR_TEX[key].clone(); t.needsUpdate = true; t.repeat.set(rx, rz); return t;
  };
  const m = new THREE.MeshStandardMaterial({
    color: tint, map: load('Diffuse.jpg', true), normalMap: load('nor_gl.jpg'),
    roughnessMap: load('Rough.jpg'), roughness: rough });
  m.aoMap = load('arm.jpg');                                  // the packed AO shipped with every set, unused until now
  m.aoMapIntensity = 0.85;
  return m;
}
const WALL   = pbrMat('grey_plaster_02', 2.4, 0.7, 0x76716a);
const WALL_T = pbrMat('painted_plaster_wall', 4.5, 1.0, 0x6b665f);
const CAP    = M(0x9d9682, .9);
const WOODD  = pbrMat('oak_veneer_01', 1.4, 0.9, 0x8a7458, .8);
const WOODL  = pbrMat('oak_veneer_01', 1.1, 0.7, 0xa88e6c, .75);
const PAPER  = M(0xcfc8b6, .95);
const BOT_B  = M(0x9d9890, .8), BOT_J = M(0x565349, .7), BOT_H = M(0xaaa49a, .75);

/* ---------- helpers ---------- */
const world = new THREE.Group(); scene.add(world);
function B(w,h,d,mat,x,y,z,ry,parent,cast=true){
  const m = new THREE.Mesh(new THREE.BoxGeometry(w,h,d), mat);
  m.position.set(x,y,z); if(ry) m.rotation.y = ry;
  m.castShadow = cast; m.receiveShadow = true;
  (parent||world).add(m); return m;
}
function Cyl(r,h,mat,x,y,z,parent,seg=14){
  const m = new THREE.Mesh(new THREE.CylinderGeometry(r,r,h,seg), mat);
  m.position.set(x,y,z); m.castShadow = true; m.receiveShadow = true;
  (parent||world).add(m); return m;
}

/* ---------- ground / plinth / slab (steps aside when you orbit underneath) ---------- */
const UNDERSIDE = { parts:[], light:null };
{
  const g = new THREE.Mesh(new THREE.PlaneGeometry(500,500), M(0x131316, 1));
  g.rotation.x = -Math.PI/2; g.position.y = -0.92; g.receiveShadow = true; world.add(g);
  const p1 = B(59, 0.9, 30, M(0x1b1b20,.95), 3.5, -0.47, 0, 0, world, false);           // plinth — model on a table
  const p2 = B(55.6, 0.42, 27.6, M(0x232327,.96), 3.5, -0.21, 0, 0, world, false);      // slab, top = y 0
  UNDERSIDE.parts = [g, p1, p2];
  const up = new THREE.DirectionalLight(0xd9cdb4, 2.0);                                  // uplight so the belly view reads
  up.position.set(3.5, -30, 8); up.target.position.set(3.5, 0, 0);
  up.visible = false; world.add(up); world.add(up.target);
  UNDERSIDE.light = up;
}
function floorPatch(x1,z1,x2,z2,mat,y=0.02){
  return B(x2-x1-0.15, 0.04, z2-z1-0.15, mat, (x1+x2)/2, y, (z1+z2)/2, 0, world, false);
}

/* ---------- plan ---------- */
/* corridor z -2..2 x -15..25 · Ben cap x -23..-15 · grants cap x 25..30 */
const ROOMS = [
  { id:'ben',      name:'BEN · LOCKED',      rect:[-23,-12.5,-15,12.5], lab:[-18.5,5.3,1.2],  ls:1.25, floor:0x282219,
    job:['Ben — the human','Reads, decides, says YES. The only person who walks in and out, carrying messages, is ATG. Nothing else reaches this room.'] },
  { id:'efficacy', name:'OBAMA',          rect:[-15,-12.5,-11,-2],  lab:[-13,4.85,-7.2], floor:0x232328,
    job:['Obama — the resident','His own oval office: the desk at the head, the standards behind it, the sitting set. Still the filter and the job hunt. Never applies, never sends — shortlists go through ATG.'] },
  { id:'shakespeare', name:'SHAKESPEARE',    rect:[-11,-12.5,-3,-2],   lab:[-7,4.85,-7.2], floor:0x2b241c,
    job:['Shakespeare — the library','Copy room. Writes everything: letters, pages, scripts. Drafts pile on the table. Writes only, never sends.'] },
  { id:'wozniak',  name:'STEVE WOZNIAK',     rect:[-3,-12.5,5,-2],     lab:[1,4.85,-7.2], floor:0x232328,
    job:['Steve Wozniak — the bench','Room packed with machines. Builds and fixes — code, hardware, plumbing. Ships nothing except through the door.'] },
  { id:'ive',      name:'JONY IVE',          rect:[5,-12.5,12,-2],     lab:[8.5,4.85,-7.2], floor:0x27272b,
    job:['Jony Ive — one object','Almost empty room. The first iPhone on a stand. Takes things away until only the essential is left.'] },
  { id:'jobs',     name:'STEVE JOBS',        rect:[12,-12.5,19,-2],    lab:[15.5,4.85,-7.2], floor:0x27272b,
    job:['Steve Jobs — product room','Sparse, co-founder. Taste and veto. Overflow from anyone still goes through ATG.'] },
  { id:'assange',  name:'JULIAN ASSANGE',    rect:[19,-12.5,25,-2],    lab:[22,4.85,-7.2], lc:0x9a9284, floor:0x1e1e22,
    job:['Julian Assange — stood down','Jail room while small. Can walk the corridor to ATG’s door — rarely to Ben’s — and still never emails Ben. Empty chair until called.'] },
  { id:'grants',   name:'GRANTS POD',        rect:[25,-12.5,30,12.5],  lab:[27.5,4.9,0], floor:0x26231e,
    job:['Grants pod — shared table','Janna + Buffett split R&D, Wrench the legalities, SJ the defensible costs, Shakespeare writes, Jony does decks. Never lodges. FY25 parked.'] },
  { id:'wrench',   name:'BRYAN WRENCH',      rect:[-15,2,-7,12.5],     lab:[-11,4.5,7.4], floor:0x2a221a,
    job:['Bryan Wrench — the courtroom','Bar, bench, files — and a proper suit. Drafts pleadings and positions. Drafts only — never files, never sends. Can’t get into Ben’s office: he deals with ATG like everyone else.'] },
  { id:'buffett',  name:'WARREN BUFFETT',    rect:[-7,2,1,12.5],       lab:[-3,4.5,7.4], floor:0x2b241c,
    job:['Warren Buffett — books & markets','Watches every dollar. Xero is a terminal on his desk, not a person. Never pays, never lodges. Peck folded in — his chair sits empty.'] },
  { id:'janna',    name:'JANNA',             rect:[1,2,9,12.5],        lab:[5,4.5,7.4], floor:0x232328,
    job:['Janna — day desk','The mail room. Janna herself sits at the EA desk right outside Ben\'s door — email lands with her first; nothing leaves until it has walked the chain and Ben has said YES.'] },
  { id:'loudfair', name:'LOUDFAIR',          rect:[9,2,17,12.5],       lab:[13,4.5,7.4], floor:0x202025,
    job:['Loudfair — agency lab','Glass side room. Web-dev and outreach drafts for the outside world. No door of its own to Ben.'] },
  { id:'rnd',      name:'PETE GAY',  rect:[17,2,21,12.5],  lab:[19,4.5,7.4], floor:0x232026,
    job:['Pete Gay — creative director','London. Tattooed sleeves, matching tracksuit, mad artist. Art on every wall. Pull him onto anything mildly creative — otherwise he\'s in here doing weird shit.'] },
  { id:'fe',       name:'FE',   sub:'STOOD DOWN', rect:[21,2,25,12.5],  lab:[23,4.3,7.4], lc:0x6f695e, ls:.72, floor:0x1f1f23,
    job:['FE seat — stood down','The nameless front-end chair. Empty until the work returns.'] },
];
const R = {}; ROOMS.forEach(r=>R[r.id]=r);

/* floors */
const floorMesh = {};
ROOMS.forEach(r=>{
  const w = r.rect[2]-r.rect[0], d = r.rect[3]-r.rect[1];
  const slug = (r.id==='assange'||r.id==='fe') ? 'concrete_floor_02' : 'herringbone_parquet';
  const tint = (r.id==='assange'||r.id==='fe') ? 0x7d7a74 : 0xa6907a;
  floorMesh[r.id] = floorPatch(r.rect[0], r.rect[1], r.rect[2], r.rect[3], pbrMat(slug, w/2.6, d/2.6, tint, .95));
});
floorPatch(-15,-2,25,2, pbrMat('concrete_floor_02', 14, 1.4, 0x8b867e, .95));   // corridor
B(39.2, 0.03, 2.2, pbrMat('dirty_carpet', 12, 0.8, 0x6e6157), 4.8, 0.055, 0, 0, world, false); // runner

/* ---------- walls ---------- */
const WALL_SEGS = [];                                        // 2D collision lines for walk mode
function wallSeg(x1,z1,x2,z2,h,mat,t=0.26,cap=true){
  const dx = x2-x1, dz = z2-z1, len = Math.hypot(dx,dz);
  const cx = (x1+x2)/2, cz = (z1+z2)/2, ry = Math.atan2(dz,dx);
  const g = new THREE.Group(); g.position.set(cx,0,cz); g.rotation.y = -ry;
  B(len+t, h, t, mat, 0, h/2, 0, 0, g);
  if(cap) B(len+t+0.04, 0.06, t+0.08, CAP, 0, h+0.02, 0, 0, g, false);
  world.add(g); WALL_SEGS.push([x1,z1,x2,z2]); return g;
}
const LOW = 0.95, STUB = 0.55, TALL = 2.25;
/* perimeter */
wallSeg(-23,-12.5,-23,12.5, TALL, WALL_T, 0.4, false);
wallSeg(-23,-12.5, 30,-12.5, TALL, WALL_T, 0.4, false);
wallSeg( 30,-12.5, 30, 12.5, STUB, WALL);
wallSeg(-23, 12.5, 30, 12.5, STUB, WALL);
/* Ben's east boundary: low panels + tall door monolith at the corridor mouth */
wallSeg(-15,-12.5,-15,-2.2, LOW, WALL);
wallSeg(-15, 2.2,-15, 12.5, LOW, WALL);
{
  const mono = new THREE.Group(); mono.position.set(-15,0,0); world.add(mono);
  B(0.5, 2.6, 1.15, WALL_T, 0, 1.3, -1.62, 0, mono);
  B(0.5, 2.6, 1.15, WALL_T, 0, 1.3,  1.62, 0, mono);
  B(0.5, 0.42, 2.1, WALL_T, 0, 2.39, 0, 0, mono);          // header
  B(0.56, 0.07, 4.44, CAP, 0, 2.62, 0, 0, mono, false);    // bone cut cap
}
/* the coral door — the whole org (hinged) */
const doorMat = new THREE.MeshStandardMaterial({ color:0xff6f61, roughness:.62, emissive:0xff6f61, emissiveIntensity:.55 });
const doorPivot = new THREE.Group(); doorPivot.position.set(-14.97, 0, 0.95); world.add(doorPivot);
B(0.14, 2.16, 1.9, doorMat, 0, 1.08, -0.95, 0, doorPivot);
B(0.05, 0.05, 0.3, M(0x2b2b30,.5,.6), 0.11, 1.05, -0.33, 0, doorPivot);   // handle
let doorOpenT = 0;                                                        // 0 shut · 1 open
/* corridor walls with door gaps */
function corridorWall(z, rooms){
  rooms.forEach(rm=>{
    const x1 = rm.rect[0], x2 = rm.rect[2];
    const cx = (x1+x2)/2;
    if(rm.id==='assange'){ // barred front
      wallSeg(x1, z, cx-1.2, z, LOW, WALL);
      wallSeg(cx+1.2, z, x2, z, LOW, WALL);
      for(let bx=cx-1.05; bx<=cx+1.06; bx+=0.27){ const bar=Cyl(0.032, 2.0, M(0x6d717a,.35,.65), bx, 1.0, z); bar.castShadow=false; }
      const rail=B(2.44, 0.07, 0.1, M(0x6d717a,.35,.65), cx, 2.0, z); rail.castShadow=false;
      return;
    }
    wallSeg(x1, z, cx-0.85, z, LOW, WALL);
    wallSeg(cx+0.85, z, x2, z, LOW, WALL);
  });
}
corridorWall(-2, [R.efficacy, R.shakespeare, R.wozniak, R.ive, R.jobs, R.assange]);
corridorWall( 2, [R.wrench, R.buffett, R.janna, R.loudfair, R.rnd, R.fe]);
/* partitions */
[-11,-3,5,12,19].forEach(x=>wallSeg(x,-12.5,x,-2, LOW, WALL));
[-7,1,9,17,21].forEach(x=>wallSeg(x,2,x,12.5, LOW, WALL));
wallSeg(25,-12.5,25,-2, LOW, WALL);
wallSeg(25,2,25,12.5, LOW, WALL);

/* Loudfair glass above its low walls */
{
  const glass = new THREE.MeshStandardMaterial({ color:0xa8aaa2, roughness:.15, metalness:0, transparent:true, opacity:.13, depthWrite:false });
  const frame = M(0x44443c,.6,.3);
  function pane(x1,z1,x2,z2){
    const len = Math.hypot(x2-x1,z2-z1), cx=(x1+x2)/2, cz=(z1+z2)/2, ry=-Math.atan2(z2-z1,x2-x1);
    const g = new THREE.Group(); g.position.set(cx,0,cz); g.rotation.y=ry; world.add(g);
    const p = B(len, 0.95, 0.05, glass, 0, 1.5, 0, 0, g, false); p.receiveShadow=false;
    B(len, 0.06, 0.09, frame, 0, 2.0, 0, 0, g, false);
    for(let i=-len/2; i<=len/2; i+=1.3) B(0.06,0.95,0.08, frame, i, 1.5, 0, 0, g, false);
  }
  pane(9,2.3,9,12.3); pane(17,2.3,17,12.3); pane(9.2,2,12.05,2); pane(13.95,2,16.8,2);
}

/* ---------- shared furniture ---------- */
function desk(w,d,x,z,ry=0,parent){
  const g = new THREE.Group(); g.position.set(x,0,z); g.rotation.y = ry; (parent||world).add(g);
  B(w, 0.07, d, WOODD, 0, 0.74, 0, 0, g);
  const lx = w/2-0.08, lz = d/2-0.08;
  [[-lx,-lz],[lx,-lz],[-lx,lz],[lx,lz]].forEach(p=>B(0.07,0.72,0.07, WOODD, p[0], 0.36, p[1], 0, g));
  return g;
}
function chair_boxed(x,z,ry=0,mat){
  const g = new THREE.Group(); g.position.set(x,0,z); g.rotation.y = ry; world.add(g);
  const m = mat||M(0x2e2b27,.9);
  B(0.46,0.06,0.44, m, 0, 0.46, 0, 0, g);
  B(0.46,0.5,0.06, m, 0, 0.74, -0.21, 0, g);
  [[-0.19,-0.18],[0.19,-0.18],[-0.19,0.18],[0.19,0.18]].forEach(p=>B(0.05,0.45,0.05, m, p[0],0.22,p[1],0,g));
  return g;
}
function papers(x,z,n=4,ry=0){
  for(let i=0;i<n;i++){
    B(0.3,0.014,0.42, PAPER, x+(Math.sin(i*3.7)*0.3), 0.79+i*0.005, z+(Math.cos(i*2.9)*0.22), ry+Math.sin(i*7)*0.5, world);
  }
}
function stack(x,z,n=3){ for(let i=0;i<n;i++) B(0.3,0.05,0.42, PAPER, x, 0.805+i*0.052, z, i*0.22, world); }
const SCREENS = [];
function monitor(x,z,ry=0,tex){
  const g = new THREE.Group(); g.position.set(x,0.775,z); g.rotation.y=ry; world.add(g);
  B(0.16,0.05,0.14, BOT_J, 0, 0.02, 0, 0, g); B(0.05,0.16,0.05, BOT_J, 0, 0.1, 0, 0, g);
  B(0.56,0.35,0.03, M(0x1c1c1f,.5,.2), 0, 0.33, 0, 0, g);
  const sm = tex ? new THREE.MeshStandardMaterial({ map:tex, emissive:0xffffff, emissiveMap:tex, emissiveIntensity:1.6, roughness:.4 })
                 : new THREE.MeshStandardMaterial({ color:0x27211a, emissive:0xc89a5e, emissiveIntensity:1.5, roughness:.4 });
  B(0.5,0.29,0.012, sm, 0, 0.33, 0.021, 0, g, false);
  SCREENS.push({ m:sm, base: sm.emissiveIntensity });
  return g;
}
function shelf_boxed(x,z,ry,w=2.6){
  const g = new THREE.Group(); g.position.set(x,0,z); g.rotation.y=ry; world.add(g);
  B(w,1.9,0.05, WOODD, 0, 0.95, -0.16, 0, g);
  [0.12,0.7,1.28,1.86].forEach(y=>B(w,0.05,0.34, WOODD, 0, y, 0, 0, g));
  B(0.05,1.9,0.34, WOODD, -w/2+0.02, 0.95, 0, 0, g); B(0.05,1.9,0.34, WOODD, w/2-0.02, 0.95, 0, 0, g);
  return g;
}
const SPINES = [0x6e4a3a,0x5a5a46,0x77624a,0x4a4f5a,0x6b5566,0x815c48,0x4f5a4e,0x8a7a5c];
const bookGeo = new THREE.BoxGeometry(0.12,0.42,0.28);
function books_boxed(shelfGroup, w=2.6){
  const rows=[0.36,0.94,1.52]; const count = rows.length*Math.floor(w/0.13)+4;
  const im = new THREE.InstancedMesh(bookGeo, new THREE.MeshStandardMaterial({roughness:.9}), count);
  let i=0; const d=new THREE.Object3D(); const col=new THREE.Color();
  rows.forEach(y=>{
    for(let bx=-w/2+0.12; bx<w/2-0.1; bx+=0.135){
      if(Math.sin(bx*13.7+y*9)>0.82){continue;}
      d.position.set(bx, y+0.21*(0.86+Math.sin(bx*31)*0.1), 0);
      d.scale.set(1, 0.86+Math.sin(bx*23+y)*0.14, 1);
      d.updateMatrix(); im.setMatrixAt(i, d.matrix);
      im.setColorAt(i, col.setHex(SPINES[Math.abs(Math.floor(bx*17+y*5))%SPINES.length]));
      i++;
    }
  });
  im.count = i; im.instanceMatrix.needsUpdate = true; if(im.instanceColor) im.instanceColor.needsUpdate = true;
  im.castShadow = false; shelfGroup.add(im);
}
/* — realistic office kit — */
const CANDLES = [], BLINKS = [], TICKERS = [];
function taskChair(x,z,ry=0,big=false){
  const g = new THREE.Group(); g.position.set(x,0,z); g.rotation.y=ry; world.add(g);
  const m = M(0x27272b,.85), s = big?1.15:1;
  for(let i=0;i<5;i++){ const leg=B(0.3*s,0.035,0.05, m, 0,0.035,0, 0, g); leg.rotation.y = i/5*Math.PI*2; leg.position.x = Math.cos(i/5*Math.PI*2)*0.12*s; leg.position.z = Math.sin(i/5*Math.PI*2)*0.12*s; }
  Cyl(0.03,0.36, m, 0,0.22,0, g, 8);
  B(0.46*s,0.06,0.44*s, m, 0,0.45,0, 0, g);
  B(0.44*s,(big?0.62:0.5),0.06, m, 0,(big?0.8:0.74),-0.21*s, 0, g);
  return g;
}
function keyboard(x,z,ry=0){ B(0.4,0.02,0.14, M(0x2e2e33,.7), x,0.79,z, ry); B(0.07,0.02,0.1, M(0x2e2e33,.7), x+0.3,0.79,z+0.03, ry); }
function mug(x,z,c=0x6e4a3a){ Cyl(0.035,0.09, M(c,.8), x,0.825,z, world, 10); }
function plant_boxed(x,z,s=1){
  const g = new THREE.Group(); g.position.set(x,0,z); g.scale.setScalar(s); world.add(g);
  Cyl(0.16,0.26, M(0x3a3229,.9), 0,0.13,0, g, 10);
  [[0,0.42,0,0.17],[-0.1,0.35,0.08,0.12],[0.11,0.36,-0.06,0.13],[0.02,0.52,0.06,0.11]].forEach(p=>{
    const b=new THREE.Mesh(new THREE.SphereGeometry(p[3],10,8), M(0x4c5a40,.95)); b.position.set(p[0],p[1],p[2]); b.castShadow=true; g.add(b); });
  return g;
}
function pendant(x,z,h=2.45){
  const g = new THREE.Group(); g.position.set(x,0,z); world.add(g);
  Cyl(0.012,3.6, M(0x2b2b30,.7), 0,h+1.8,0, g, 6);
  const sh = new THREE.Mesh(new THREE.CylinderGeometry(0.06,0.19,0.14,12,1,true), M(0x2f2a24,.7,.3)); sh.position.set(0,h,0); g.add(sh);
  const gl = new THREE.Mesh(new THREE.CylinderGeometry(0.055,0.055,0.02,10), new THREE.MeshStandardMaterial({color:0xffd9a0, emissive:0xffc37a, emissiveIntensity:2.6}));
  gl.position.set(0,h-0.06,0); g.add(gl);
}
function bin(x,z){ Cyl(0.14,0.3, M(0x3c3c42,.8), x,0.15,z, world, 10); }
function crumples(x,z,n=4){ for(let i=0;i<n;i++){ const s=new THREE.Mesh(new THREE.SphereGeometry(0.05+Math.random()*0.03,7,6), PAPER); s.scale.y=0.7; s.position.set(x+(Math.random()-0.5)*0.7, 0.05, z+(Math.random()-0.5)*0.7); s.castShadow=true; world.add(s); } }
function filingCab(x,z,ry=0){
  const g = new THREE.Group(); g.position.set(x,0,z); g.rotation.y=ry; world.add(g);
  B(0.5,1.1,0.5, M(0x45454c,.7,.25), 0,0.55,0, 0, g);
  [0.25,0.55,0.85].forEach(y=>{ B(0.44,0.015,0.02, M(0x2b2b30,.6), 0,y,0.255, 0, g); B(0.12,0.03,0.02, M(0x63635e,.5,.4), 0,y+0.09,0.255, 0, g); });
  return g;
}
function couch_boxed(x,z,ry=0,w=1.9){
  const g = new THREE.Group(); g.position.set(x,0,z); g.rotation.y=ry; world.add(g);
  const m = M(0x3a342c,.95);
  B(w,0.34,0.85, m, 0,0.17,0, 0, g); B(w,0.5,0.22, m, 0,0.52,-0.32, 0, g);
  B(0.22,0.52,0.85, m, -w/2+0.11,0.32,0, 0, g); B(0.22,0.52,0.85, m, w/2-0.11,0.32,0, 0, g);
  B(w-0.5,0.09,0.55, M(0x413a31,.95), 0,0.38,0.05, 0, g);
  return g;
}
function dustSheet(x,z,w,d){
  const m = new THREE.MeshStandardMaterial({color:0xb9b2a2, roughness:1, transparent:true, opacity:.92});
  const s = B(w,0.78,d, m, x,0.42,z, 0.06);
  s.scale.y = 1; return s;
}
function candle(x,y,z,r=0.028,glow=0xffc37a){
  Cyl(r,0.14, M(0xd6d0be,.9), x,y+0.07,z, world, 8);
  const f = new THREE.Mesh(new THREE.SphereGeometry(0.02,8,6), new THREE.MeshStandardMaterial({color:0xffe2a8, emissive:glow, emissiveIntensity:2.2}));
  f.position.set(x,y+0.17,z); world.add(f); CANDLES.push({m:f.material, base:2.2, ph:Math.random()*7});
  return f;
}

/* ---------- figures: rigged, two poses, costumes, imposed heads ---------- */
/* kinds: bot · ben · atg (Alexander) · suit (Wrench, Buffett) · turtle (Jobs) */
const RIGS = [];
const VOICES = {
  ben:{ name:'BEN', line:'Bring me fewer decisions, better ones.' },
  atg:{ name:'ALEXANDER THE GREAT', line:'Every message walks through me. That is the whole org.', off:[3.6,2.2] },
  wrench:{ name:'BRYAN WRENCH', line:'I draft. I never file. The door decides.' },
  shakespeare:{ name:'SHAKESPEARE', line:'I write it once more, better. Sending is not my office.', off:[2.8,2.6] },
  janna:{ name:'JANNA', line:'It lands here first. It leaves only on his YES.', off:[0.9,-0.35] },   // the EA desk faces the door
  jobs:{ name:'STEVE JOBS', line:'No. And no. The third one — maybe.' },
  ive:{ name:'JONY IVE', line:'Take everything away until only the idea remains.' },
  efficacy:{ name:'OBAMA', line:'Let me be clear — three of these deserve his eyes. The rest can wait.' },
  wozniak:{ name:'STEVE WOZNIAK', line:'It works. Now let me make it simple enough to be true.', off:[3.0,2.4] },
  buffett:{ name:'WARREN BUFFETT', line:'I watch every dollar. I move none of them.' },
  loudfair:{ name:'LOUDFAIR', line:'We make it loud. He makes it go.' },
  pete:{ name:'PETE GAY', line:'Mate, it\'s nearly there. It just needs to be weirder.', off:[-1.4,-2.2] },
};
const voiceTargets = [];
function addVoiceTarget(rig, id){
  const m = new THREE.Mesh(new THREE.CylinderGeometry(0.5,0.5,1.9,8),
    new THREE.MeshBasicMaterial({ transparent:true, opacity:0, depthWrite:false }));
  m.position.y = 0.95; m.userData.voice = id; m.userData.rig = rig;
  if(!rig.id) rig.id = id;                                  // seated figures speak too, not just the walkers
  rig.root.add(m); voiceTargets.push(m);
}
const SKIN = { jobs:0xc9ab8d, ive:0xcbb098, woz:0xb5906c, bard:0xb6906e, buffett:0xc9ac8f, slick:0xb08b66, bun:0xa87f63 };
/* painted faces — the actual people */
function rgba(hex,a){ const n=parseInt(hex.slice(1),16); return 'rgba('+(n>>16)+','+((n>>8)&255)+','+(n&255)+','+a+')'; }
const FACE_CACHE = {};
function faceTex(id, o){
  if(FACE_CACHE[id]) return FACE_CACHE[id];
  const c = document.createElement('canvas'); c.width=c.height=1024; const g=c.getContext('2d');
  g.scale(2,2);
  const skin=o.skin, dark=o.shade;
  g.fillStyle=skin; g.fillRect(0,0,512,512);
  if(o.hairBack){ g.fillStyle=o.hairBack;                                   // hair wrapping the back of the skull
    const low=o.hairLow||300;
    g.fillRect(0,60,120,low-60); g.fillRect(392,60,120,low-60);
    if(!o.bald) g.fillRect(0,0,512,130);
    g.filter='blur(10px)'; g.fillRect(100,80,30,low-100); g.fillRect(382,80,30,low-100); g.filter='none'; }
  g.filter='blur(20px)';                                                    // soft facial structure
  g.fillStyle=rgba(dark,.5);
  g.beginPath(); g.ellipse(146,258,26,74,0,0,7); g.fill();
  g.beginPath(); g.ellipse(366,258,26,74,0,0,7); g.fill();
  g.beginPath(); g.ellipse(256,436,96,42,0,0,7); g.fill();
  g.fillStyle='rgba(255,255,255,.09)';
  g.beginPath(); g.ellipse(256,254,15,48,0,0,7); g.fill();
  g.beginPath(); g.ellipse(256,166,90,34,0,0,7); g.fill();
  g.fillStyle=rgba(o.blush||'#a05a44',.12);
  g.beginPath(); g.ellipse(196,306,30,22,0,0,7); g.fill();
  g.beginPath(); g.ellipse(316,306,30,22,0,0,7); g.fill();
  g.filter='none';
  if(!o.photo){
  const ey=230;                                                             // eyes
  [[-1],[1]].forEach(sd=>{ const sx=256+sd[0]*44;
    g.fillStyle='#ded7c8'; g.beginPath(); g.ellipse(sx,ey,17.5,10.5,0,0,7); g.fill();
    g.fillStyle=o.eye; g.beginPath(); g.arc(sx,ey+1,8.6,0,7); g.fill();
    g.fillStyle=rgba('#100c08',.9); g.beginPath(); g.arc(sx,ey+1,4.2,0,7); g.fill();
    g.fillStyle='rgba(255,255,255,.85)'; g.beginPath(); g.arc(sx-2.6,ey-2.2,1.7,0,7); g.fill();
    g.strokeStyle='rgba(52,36,26,.9)'; g.lineWidth=3.4;
    g.beginPath(); g.ellipse(sx,ey-2,18,10,0,Math.PI+0.35,-0.35); g.stroke();
    g.strokeStyle='rgba(52,36,26,.3)'; g.lineWidth=2.2;
    g.beginPath(); g.ellipse(sx,ey+3,16,9,0,0.5,Math.PI-0.5); g.stroke();
    g.strokeStyle=rgba(dark,.45); g.lineWidth=2.4; g.filter='blur(1px)';
    g.beginPath(); g.ellipse(sx,ey-9,19,11,0,Math.PI+0.4,-0.4); g.stroke(); g.filter='none';
    const bl=o.browThick||5.2;                                              // brows
    g.strokeStyle=o.brow; g.lineWidth=bl; g.lineCap='round';
    g.beginPath(); g.moveTo(sx-sd[0]*4-24*sd[0]*0, sx?0:0); g.closePath();
    g.beginPath(); g.moveTo(sx-26*sd[0], ey-24+(o.browDrop||0));
    g.quadraticCurveTo(sx, ey-33+(o.browArch||0), sx+25*sd[0], ey-27);
    g.stroke();
    if(o.wispy){ g.lineWidth=2; g.strokeStyle=rgba('#d8d2c6',.8);
      for(let i=0;i<7;i++){ const t=i/6, bx=sx-26*sd[0]+52*sd[0]*t;
        g.beginPath(); g.moveTo(bx, ey-25-(Math.sin(t*3)*4)); g.lineTo(bx+3*sd[0], ey-33-(Math.sin(t*3)*4)); g.stroke(); } }
  });
  g.strokeStyle=rgba(dark,.6); g.lineWidth=3; g.lineCap='round';            // nose
  g.beginPath(); g.arc(243,287,6,Math.PI*0.4,Math.PI*1.25); g.stroke();
  g.beginPath(); g.arc(269,287,6,Math.PI*1.75,Math.PI*0.6); g.stroke();
  g.filter='blur(3px)'; g.fillStyle=rgba(dark,.3);
  g.beginPath(); g.ellipse(256,289,17,7,0,0,7); g.fill(); g.filter='none';
  const my=334+(o.mouthY||0), sm=(o.smile||0);                              // mouth
  g.strokeStyle='rgba(74,42,34,.8)'; g.lineWidth=3.6;
  g.beginPath(); g.moveTo(226,my); g.quadraticCurveTo(256,my+7+sm, 286,my); g.stroke();
  g.filter='blur(2px)'; g.fillStyle=rgba(o.lip||'#a8685a',.4);
  g.beginPath(); g.ellipse(256,my+13,24,8,0,0,7); g.fill(); g.filter='none';
  if(o.age){ const a=o.age;                                                 // years
    g.strokeStyle=rgba(dark,.34*a); g.lineWidth=2.4; g.filter='blur(1px)';
    [156,174,190].forEach((yy,i)=>{ g.beginPath(); g.moveTo(196+i*6,yy); g.quadraticCurveTo(256,yy-7, 316-i*6,yy); g.stroke(); });
    [[-1],[1]].forEach(sd=>{ const sx=256+sd[0]*66;
      for(let i=0;i<3;i++){ g.beginPath(); g.moveTo(sx,228+i*3-3); g.lineTo(sx+sd[0]*13, 222+i*7); g.stroke(); } });
    g.beginPath(); g.moveTo(232,296); g.quadraticCurveTo(222,318, 230,338); g.stroke();
    g.beginPath(); g.moveTo(280,296); g.quadraticCurveTo(290,318, 282,338); g.stroke();
    g.filter='none'; }
  if(o.beard){                                                              // painted beard / stubble
    if(o.beardFull){
      g.fillStyle=rgba(o.beard,.92); g.filter='blur(4px)';
      g.beginPath(); g.moveTo(150,270); g.quadraticCurveTo(160,420,256,452);
      g.quadraticCurveTo(352,420,362,270); g.lineTo(362,320); g.quadraticCurveTo(352,440,256,462);
      g.quadraticCurveTo(160,440,150,320); g.closePath(); g.fill();
      g.beginPath(); g.ellipse(256,398,66,58,0,0,7); g.fill();
      g.beginPath(); g.ellipse(256,314,40,14,0,0,7); g.fill();                       // moustache
      g.filter='none';
      g.fillStyle=skin; g.beginPath(); g.ellipse(256,336,20,7,0,0,7); g.fill();      // keep the mouth
    } else {
      g.fillStyle=rgba(o.beard,.5);
      for(let i=0;i<900;i++){ const an=Math.random()*Math.PI, rr=58+Math.random()*46;
        const px=256+Math.cos(an)*rr*1.35, py=330+Math.sin(an)*rr*0.85;
        if(py>296 && py<452 && Math.abs(px-256)<110 && !(Math.abs(px-256)<24 && py<348))
          g.fillRect(px,py,2.1,2.1); } }
  }
  }
  const t = new THREE.CanvasTexture(c); t.colorSpace=THREE.SRGBColorSpace; t.anisotropy=8;
  if(o.photo){                                                              // the actual face, whole head re-keyed to it
    const img = new Image();
    img.onload = ()=>{
      const iw = img.width, ih = img.height;
      const fw = o.fw||.6, sw = iw*fw, sh = sw*1.34;
      const sx = (iw-sw)/2 + (o.fx||0)*iw, sy = ih*(o.fy!==undefined?o.fy:.14);
      /* sample the photo's own skin so the entire skull matches it */
      const tmp = document.createElement('canvas'); tmp.width = tmp.height = 48;
      const tg = tmp.getContext('2d');
      tg.drawImage(img, sx+sw*0.36, sy+sh*0.2, sw*0.28, sh*0.1, 0, 0, 48, 48);
      const dd = tg.getImageData(0,0,48,48).data; let r=0,gg=0,b=0;
      for(let i=0;i<dd.length;i+=4){ r+=dd[i]; gg+=dd[i+1]; b+=dd[i+2]; }
      const n = dd.length/4; r=(r/n)|0; gg=(gg/n)|0; b=(b/n)|0;
      const skin = `rgb(${r},${gg},${b})`;
      const dark = `rgb(${(r*0.58)|0},${(gg*0.54)|0},${(b*0.52)|0})`;
      /* full repaint in the photo's palette */
      g.fillStyle = skin; g.fillRect(0,0,512,512);
      if(o.hairBack){ g.fillStyle = o.hairBack; const low = o.hairLow||300;
        g.fillRect(0,60,120,low-60); g.fillRect(392,60,120,low-60);
        if(!o.bald) g.fillRect(0,0,512,130);
        g.filter='blur(10px)'; g.fillRect(100,80,30,low-100); g.fillRect(382,80,30,low-100); g.filter='none'; }
      g.filter='blur(22px)';
      g.fillStyle = dark; g.globalAlpha = .4;
      g.beginPath(); g.ellipse(140,262,26,76,0,0,7); g.fill();
      g.beginPath(); g.ellipse(372,262,26,76,0,0,7); g.fill();
      g.beginPath(); g.ellipse(256,452,110,46,0,0,7); g.fill();
      g.globalAlpha = 1; g.filter='none';
      const CX=256, CY=256, RX=104, RY=132;
      g.save(); g.beginPath(); g.ellipse(CX,CY,RX,RY,0,0,7); g.clip();
      g.drawImage(img, sx, sy, sw, sh, CX-RX, CY-RY, RX*2, RY*2);
      g.restore();
      g.save(); g.translate(CX,CY); g.scale(1, RY/RX);                      // wide soft feather, photo-matched
      const grad = g.createRadialGradient(0,0, RX*0.62, 0,0, RX*1.18);
      grad.addColorStop(0,'rgba(0,0,0,0)');
      grad.addColorStop(0.55,'rgba(0,0,0,0)');
      grad.addColorStop(0.85, skin.replace('rgb','rgba').replace(')',',0.5)'));
      grad.addColorStop(1, skin.replace('rgb','rgba').replace(')',',0.97)'));
      g.fillStyle=grad; g.beginPath(); g.arc(0,0,RX*1.22,0,7); g.fill(); g.restore();
      g.save(); g.globalCompositeOperation='overlay'; g.fillStyle='rgba(186,138,92,0.1)';   // sit it in the room light
      g.beginPath(); g.ellipse(CX,CY,RX,RY,0,0,7); g.fill(); g.restore();
      t.needsUpdate = true;
    };
    img.src = '/assets/faces/' + o.photo;
  }
  FACE_CACHE[id] = t; return t;
}
const FACES = {
  jobs:{ skin:'#c9a483', shade:'#8d6a4e', eye:'#5a4632', brow:'#55504a', browThick:4.4, age:.75,
    beard:'#6f6a62', hairBack:'#6f6a62', hairLow:270, bald:true, smile:-2, lip:'#9a6a58',
    photo:'jobs.jpg', fw:.8, fy:.05 },
  woz:{ skin:'#c99a76', shade:'#8a6247', eye:'#4f3d28', brow:'#4a3d2c', browThick:6, age:.35,
    beard:'#4f4438', beardFull:true, hairBack:'#4f4438', hairLow:330, smile:4,
    photo:'woz.jpg', fw:.5, fy:.15 },
  buffett:{ skin:'#d4ac91', shade:'#96705a', eye:'#5d7086', brow:'#c9c2b4', browThick:6.5, wispy:true,
    age:1, hairBack:'#d8d2c6', hairLow:255, bald:true, smile:5, blush:'#b06a52',
    photo:'buffett.jpg', fw:.64, fy:.16 },
  ive:{ skin:'#c9a888', shade:'#8d6c52', eye:'#57687a', brow:'#6f665c', browThick:4.6, age:.45,
    beard:'#8a8078', bald:true, hairBack:null, smile:0,
    photo:'ive.jpg', fw:.6, fy:.08 },
  bard:{ skin:'#c39c78', shade:'#886349', eye:'#4a3a26', brow:'#3f3223', browThick:5, age:.3,
    beard:'#4a3a2c', beardFull:true, hairBack:'#4a3a2c', hairLow:300, smile:1,
    photo:'bard.jpg', fw:.44, fy:.17, fx:-0.02 },
  slick:{ skin:'#bd9573', shade:'#84604a', eye:'#463522', brow:'#241f1b', browThick:5.6, age:.2,
    hairBack:'#241f1b', hairLow:290, smile:-1 },
  bun:{ skin:'#c59d7e', shade:'#8a664c', eye:'#4f3a2a', brow:'#38291d', browThick:4.2, age:0,
    hairBack:'#3a2e24', hairLow:330, smile:4, lip:'#b06a5e', blush:'#b06552' },
  ben:{ skin:'#c19673', shade:'#85614a', eye:'#43331f', brow:'#2b2723', browThick:5, age:.25,
    beard:'#5a4a3c', hairBack:'#2b2723', hairLow:300, smile:1 },
  atg:{ skin:'#b98f68', shade:'#7d5c42', eye:'#3d2e1c', brow:'#2e241a', browThick:5.8, age:.3,
    beard:'#6b5136', smile:-2, photo:'atg.jpg', fw:.5, fy:.13, fx:.04 },
};
function makeRig(kind='bot', scale=1, head=null, opts={}){
  const cloth = opts.cloth!==undefined ? M(opts.cloth,.92) :
    { atg:M(0x7d7364,.85), suit:M(0x2c2c31,.9), ben:M(0x35415a,.98), turtle:M(0x232326,.95), bot:BOT_B }[kind];
  const legsM = opts.legs!==undefined ? M(opts.legs,.92) :
    { atg:M(0x6b6255,.85), suit:M(0x2c2c31,.9), ben:M(0x3a352c,1), turtle:M(0x46536b,.95), bot:BOT_B }[kind];
  const faceId = head && FACES[head] ? head : (FACES[kind] ? kind : null);
  const skinHex = faceId ? FACES[faceId].skin : null;
  const skinM = faceId ? M(parseInt(FACES[faceId].skin.slice(1),16),.85) : BOT_H;
  const headM = faceId ? new THREE.MeshStandardMaterial({ map:faceTex(faceId, FACES[faceId]), roughness:.72 }) :
    { atg:M(0xb08b66,.8), suit:M(0xb08b66,.8), ben:M(0xb08b66,.8), turtle:M(0xc9ab8d,.8), bot:BOT_H }[kind];
  const wide = !!opts.wide;
  const BRONZE = M(0x8a6f42,.45,.55), OXBLOOD = M(0x66332c,.9), BONE = M(0xd6d0c2,.9);
  const root = new THREE.Group(); root.scale.setScalar(scale); world.add(root);
  const refs = { root, kind };
  function sphere(r,m,x,y,z,parent,seg=20){ const s=new THREE.Mesh(new THREE.SphereGeometry(r,seg,Math.max(10,seg-6)),m); s.position.set(x,y,z); s.castShadow=true; parent.add(s); return s; }
  function headGroup(parent, y){
    const h = new THREE.Group(); h.position.set(0,y,0); parent.add(h);
    Cyl(0.042,0.09, skinM===BOT_H?headM:skinM, 0, 0.03, 0, h, 12);                 // neck
    const skull = new THREE.Mesh(new THREE.SphereGeometry(0.115, 40, 30), headM);
    skull.scale.set(0.87, 1.02, 0.95); skull.position.set(0, 0.17, 0);
    skull.rotation.y = -Math.PI/2;                                                 // face texture fronts +z
    skull.castShadow = true; h.add(skull);
    if(faceId && !(FACES[faceId] && FACES[faceId].photo)){                         // ears (photos carry their own)
      sphere(0.028, skinM, -0.104, 0.16, -0.012, h, 12); sphere(0.028, skinM, 0.104, 0.16, -0.012, h, 12);
    }
    const DK = M(0x2b2b30,.5,.35);
    const photoHead = !!(faceId && FACES[faceId] && FACES[faceId].photo);   // real portrait carries the face
    const roundGlasses = (r=0.05)=>{
      [[-0.055],[0.055]].forEach(px=>{ const g1=new THREE.Mesh(new THREE.TorusGeometry(r,0.006,10,26), DK);
        g1.position.set(px[0],0.185,0.104); h.add(g1); });
      B(0.026,0.007,0.01, DK, 0,0.19,0.111, 0, h);
      [[-0.1],[0.1]].forEach(px=>{ const a=B(0.07,0.007,0.008, DK, px[0],0.19,0.06, 0, h); a.rotation.y=Math.PI/2; });
    };
    /* hair, sculpted over the painted skull */
    const hairM = c=>M(c,.96);
    if(head==='jobs'){ if(!photoHead) roundGlasses(0.048);
      const fr = sphere(0.113, hairM(0x6f6a62), 0, 0.115, -0.045, h, 26); fr.scale.set(1.02,0.55,0.9); }
    if(head==='woz'){ if(!photoHead) roundGlasses(0.05);
      const hr = sphere(0.118, hairM(0x4f4438), 0, 0.185, -0.02, h, 26); hr.scale.set(1.05,0.8,1.05);
      if(!photoHead){ const bd = sphere(0.105, hairM(0x4f4438), 0, 0.04, 0.035, h, 26); bd.scale.set(0.95,0.85,0.85); } }
    if(head==='buffett'){
      if(!photoHead){
        [[-0.055],[0.055]].forEach(px=>{
          [[0,0.024,0.08,0.006],[0,-0.024,0.08,0.006],[-0.038,0,0.006,0.052],[0.038,0,0.006,0.052]].forEach(f=>
            B(f[2],f[3],0.007, DK, px[0]+f[0],0.183+f[1],0.104, 0, h)); });
        B(0.026,0.007,0.01, DK, 0,0.192,0.108, 0, h);
        [[-0.1],[0.1]].forEach(px=>{ const a=B(0.07,0.007,0.008, DK, px[0],0.19,0.058, 0, h); a.rotation.y=Math.PI/2; });
      }
      [[-0.098],[0.098]].forEach(px=>{ const p=sphere(0.048, hairM(0xd8d2c6), px[0], 0.135, -0.02, h, 16); p.scale.set(0.55,0.75,0.95); });
      const cv = sphere(0.112, hairM(0xd8d2c6), 0, 0.225, -0.05, h, 22); cv.scale.set(0.9,0.22,0.8); }
    if(head==='bard'){
      const ruff=new THREE.Mesh(new THREE.TorusGeometry(0.088,0.034,12,26), M(0xd6d0c2,.95));
      ruff.rotation.x=Math.PI/2; ruff.position.y=-0.005; h.add(ruff);
      const bk = sphere(0.115, hairM(0x4a3a2c), 0, 0.16, -0.06, h, 24); bk.scale.set(1.0,0.85,0.75);
      if(!photoHead){ const bd=new THREE.Mesh(new THREE.ConeGeometry(0.034,0.1,10), hairM(0x4a3a2c));
        bd.rotation.x=2.65; bd.position.set(0,0.045,0.108); h.add(bd); }
      sphere(0.012, M(0xc9a227,.3,.8), 0.112, 0.135, 0.0, h, 10); }
    if(head==='slick'){ const cp = sphere(0.114, M(0x241f1b,.55), 0, 0.21, -0.02, h, 26); cp.scale.set(0.98,0.5,1.02); }
    if(head==='bun'){ const cp = sphere(0.117, hairM(0x3a2e24), 0, 0.2, -0.02, h, 26); cp.scale.set(1.0,0.68,1.0);
      sphere(0.05, hairM(0x3a2e24), 0, 0.285, -0.075, h, 14); }
    if(head==='ive'){ /* clean-shaven, bald — the texture carries it */ }
    if(kind==='ben' && !head){ const cp = sphere(0.116, hairM(0x2b2723), 0, 0.205, -0.025, h, 26); cp.scale.set(1.0,0.6,1.0); }
    if(kind==='suit' && !head && faceId==='slick'){ }
    if(kind==='atg'){
      const hm = sphere(0.127, BRONZE, 0, 0.255, -0.012, h, 26); hm.scale.set(1.0,0.66,1.05);  // open-face helmet
      B(0.04,0.15,0.42, OXBLOOD, 0, 0.4, -0.02, 0, h);                                         // crest
      B(0.21,0.04,0.05, BRONZE, 0, 0.218, 0.095, 0, h);                                        // brow band
      B(0.026,0.12,0.024, BRONZE, 0, 0.165, 0.114, 0, h);                                      // nose guard
      [[-0.1],[0.1]].forEach(px=>{ const c2=B(0.05,0.14,0.035, BRONZE, px[0], 0.09, 0.05, 0, h); c2.rotation.y=px[0]<0?0.55:-0.55; }); // cheek guards
    }
    if(head==='headset'){
      const band=new THREE.Mesh(new THREE.TorusGeometry(0.122,0.012,8,24), DK); band.position.y=0.18; h.add(band);
      [[-0.118],[0.118]].forEach(px=>sphere(0.03, DK, px[0], 0.17, 0, h, 10));
      const mic=B(0.09,0.011,0.011, DK, 0.07,0.1,0.07, 0, h); mic.rotation.y=-0.5;
      B(0.12,0.05,0.09, M(0x33333a,.5,.3), 0, 0.185, 0.096, 0, h); }                           // visor
    if(head==='beanie'){ const cp = sphere(0.118, hairM(0x5a4a3a), 0, 0.2, -0.005, h, 22); cp.scale.set(1.0,0.66,1.0);
      B(0.12,0.05,0.09, M(0x33333a,.5,.3), 0, 0.175, 0.098, 0, h); }
    if(head==='phones'){
      const band=new THREE.Mesh(new THREE.TorusGeometry(0.124,0.015,8,24), DK); band.position.y=0.18; h.add(band);
      [[-0.12],[0.12]].forEach(px=>sphere(0.042, DK, px[0], 0.165, 0, h, 10));
      B(0.12,0.05,0.09, M(0x33333a,.5,.3), 0, 0.185, 0.096, 0, h); }
    return h;
  }
  /* torso + arms, shared builder — real proportions (~1.7 tall), smooth capsule anatomy */
  const shoeM = M(opts.shoes||0x26231f,.8,.1);
  const tieM  = M(opts.tie||0x513030,.9);
  function Cap(r,len,m,x,y,z,parent,rz=0,rx=0){
    const g=new THREE.Mesh(new THREE.CapsuleGeometry(r,len,6,16), m);
    g.position.set(x,y,z); g.rotation.z=rz; g.rotation.x=rx; g.castShadow=true; parent.add(g); return g;
  }
  function torso(up, seated){
    const cw = wide?0.48:0.42, cd = wide?0.29:0.24;                          // chest
    const ch = Cap(cd/2*1.02, cw-cd, cloth, 0, seated?0.32:0.34, 0, up, Math.PI/2);
    ch.scale.set(1,1,0.92);
    const wa = Cap((cd-0.03)/2*1.05, cw-cd-0.1, cloth, 0, seated?0.1:0.11, 0.004, up, Math.PI/2);
    wa.scale.set(1,1,0.9);
    Cap(cd/2*0.9, 0.16, cloth, 0, seated?0.21:0.22, 0.002, up, 0);           // sternum join
    if(wide) sphere(0.175, cloth, 0, seated?0.13:0.14, 0.05, up, 20).scale.set(1.12,0.85,0.92);   // the years of good lunches
    if(kind==='suit'){                                                       // lapels, shirt, tie
      B(0.11, 0.05, 0.05, BONE, 0, (seated?0.42:0.44), 0.095, 0, up);
      const lp1=B(0.085,0.24,0.015, M(0x232327,.85), -0.075,(seated?0.32:0.34),cd/2+0.003, 0, up); lp1.rotation.z=0.16;
      const lp2=B(0.085,0.24,0.015, M(0x232327,.85), 0.075,(seated?0.32:0.34),cd/2+0.003, 0, up); lp2.rotation.z=-0.16;
      B(0.052,0.3,0.018, tieM, 0, (seated?0.28:0.3), cd/2+0.012, 0, up);
      B(0.06,0.045,0.02, tieM, 0, (seated?0.43:0.45), cd/2+0.012, 0, up);
    }
    if(kind==='turtle') Cyl(0.062,0.07, cloth, 0, seated?0.48:0.5, 0, up, 14);   // the mock turtleneck
    if(kind==='atg'){
      B(cw+0.02, 0.62, 0.05, OXBLOOD, 0, seated?0.14:0.12, -0.15, 0, up);    // cloak
      B(cw+0.015, 0.3, cd+0.03, BRONZE, 0, seated?0.31:0.33, 0, 0, up);      // cuirass
      B(cw, 0.05, cd+0.02, OXBLOOD, 0, seated?0.03:0.02, 0, 0, up);          // belt
      for(let i=-2;i<=2;i++) B(0.055,0.14,0.02, OXBLOOD, i*0.065, seated?-0.06:-0.08, cd/2+0.01, 0, up);  // pteruges
    }
  }
  function arms(up, seated){
    const sleeve = (kind==='bot' && !opts.cloth) ? cloth : cloth;
    const bare = opts.tee;                                                   // short sleeves → skin forearms
    const mk = (sx)=>{
      const a = new THREE.Group(); a.position.set(sx*(wide?0.275:0.25), seated?0.44:0.46, 0); up.add(a);
      sphere(0.05, sleeve, 0, -0.015, 0, a, 14);                             // deltoid
      Cap(0.048, 0.2, sleeve, 0, -0.15, 0, a);
      sphere(0.045, bare?skinM:sleeve, 0, -0.28, 0.005, a, 12);              // elbow
      Cap(0.04, 0.18, bare?skinM:sleeve, 0, -0.41, 0.01, a);
      sphere(0.045, skinM===BOT_H?BOT_J:skinM, 0, -0.54, 0.012, a, 12).scale.set(0.85,1.1,0.7);  // hand
      return a;
    };
    return [mk(-1), mk(1)];
  }
  /* — standing — */
  const stand = new THREE.Group(); root.add(stand);
  const legL = new THREE.Group(); legL.position.set(-0.095,0.88,0); stand.add(legL);
  const legR = new THREE.Group(); legR.position.set(0.095,0.88,0); stand.add(legR);
  [legL,legR].forEach(l=>{
    Cap(0.068,0.3, legsM, 0,-0.2,0, l);                                      // thigh
    sphere(0.06, legsM, 0,-0.42,0.008, l, 12);                               // knee
    Cap(0.055,0.28, legsM, 0,-0.62,0.005, l);
    B(0.095,0.05,0.2, shoeM, 0,-0.855,0.05, 0, l);
    sphere(0.048, shoeM, 0,-0.85,0.13, l, 10).scale.set(1,0.55,1);           // toe
  });
  const hip = Cap(0.105,0.13, legsM, 0,0.95,0, stand, Math.PI/2); hip.scale.set(1,1,0.9);
  const upper = new THREE.Group(); upper.position.set(0,1.02,0); stand.add(upper);
  torso(upper,false);
  const [armL, armR] = arms(upper,false);
  if(kind==='atg'){                                                          // the spear — ruler of the agents
    Cyl(0.018,2.05, M(0x4a3c2c,.8), -0.03,-0.28,0.09, armL, 8);
    const tip = new THREE.Mesh(new THREE.ConeGeometry(0.045,0.17,10), M(0x8a6f42,.4,.6));
    tip.position.set(-0.03,0.83,0.09); tip.castShadow=true; armL.add(tip);
  }
  const headS = headGroup(upper, 0.52);
  /* — seated — */
  const seat = new THREE.Group(); root.add(seat);
  [[-0.095],[0.095]].forEach(p=>{
    Cap(0.066,0.3, legsM, p[0], 0.52, 0.16, seat, 0, Math.PI/2);             // thighs
    sphere(0.058, legsM, p[0], 0.5, 0.34, seat, 12);                         // knees
    Cap(0.054,0.26, legsM, p[0], 0.27, 0.37, seat);
    B(0.095,0.05,0.19, shoeM, p[0], 0.03, 0.42, 0, seat);
  });
  const hs = Cap(0.105,0.12, legsM, 0, 0.53, -0.03, seat, Math.PI/2); hs.scale.set(1,1,0.9);
  const upSeat = new THREE.Group(); upSeat.position.set(0,0.6,0); seat.add(upSeat);
  torso(upSeat,true);
  const [sArmL, sArmR] = arms(upSeat,true);
  sArmL.rotation.x = -0.62; sArmR.rotation.x = -0.62;
  const foreL = sArmL, foreR = sArmR;                                        // typing refs point at arm groups now
  const headSeat = headGroup(upSeat, 0.5);
  Object.assign(refs, { stand, seat, legL, legR, armL, armR, upper, headS, upSeat, foreL, foreR, headSeat });
  refs.pose = 'stand';
  refs.setPose = p=>{ refs.pose=p; stand.visible = p==='stand'; seat.visible = p==='sit'; };
  refs.setPose('stand');
  refs.idle = Math.random()*6.28; refs.lean = 0;
  RIGS.push(refs);
  /* ---- Blender-built skinned body replaces the capsule puppet when its GLB arrives ---- */
  const charFile = CHAR_FILE[head] || CHAR_FILE[kind] || null;
  if(charFile === 'orb'){
    /* the agent as itself — Ben's LISA particle piece, seat-sized: no borrowed face.
       The real person plays on the room's TV; this is the intelligence at the desk. */
    root.remove(stand); root.remove(seat);
    const accent = new THREE.Color(ORB_ACCENT[head] || ORB_ACCENT[kind] || 0xbfc3cf);
    const g = buildOrb(accent);
    root.add(g.group);
    refs.orb = true; refs.orbG = g; refs.energy = 0.2; refs.energyUntil = 0;
    refs.setPose = p => { refs.pose = p; };
    refs.headS = g.gaze; refs.headSeat = g.gaze;
    refs.applyOrb = (dt, t)=>{
      const hot = performance.now() < refs.energyUntil;
      const target = hot ? 1 : (refs.meet ? 0.45 : 0.2);
      refs.energy += (target - refs.energy) * Math.min(1, dt*4.5);
      const u = g.mat.uniforms;
      u.uTime.value = t + g.seed;
      u.uEnergy.value = refs.energy;
      const baseY = refs.pose==='sit' ? 0.98 : 1.22;
      g.group.position.y = baseY + Math.sin(t*0.9 + g.seed)*0.045 + refs.energy*0.05;
      g.group.rotation.y = t*0.14;
      g.core.rotation.y = -t*0.35; g.core.rotation.z = t*0.2;
      g.inner.rotation.y = t*0.6; g.inner.rotation.x = -t*0.3;
      g.ring.rotation.z = t*0.24;
      const s = 1 + refs.energy*0.16;
      g.core.scale.setScalar(s);
      g.group.rotation.x = refs.isWalking ? 0.16 : 0;      // leans into the glide
      g.light.intensity = 0.7 + refs.energy*1.6 + Math.sin(t*2.2+g.seed)*0.06;
    };
  }
  else if(charFile){
    loadChar(charFile).then(({model, clips})=>{
      root.remove(stand); root.remove(seat);                 // puppet retires; anim writes to it become inert
      const tint = opts.tint || SEAT_TINT[head] || SEAT_TINT[kind] || 0x8a867e;
      const human = RPM_RE.test(charFile) || PR_RE.test(charFile) || HU_RE.test(charFile) || KEEP_TEXTURE[charFile];
      const outfit = OUTFIT_TINT[head] || OUTFIT_TINT[kind] || {};
      model.traverse(o=>{ if(o.isMesh){
        o.castShadow = true; o.frustumCulled = false;
        if(/icosphere/i.test(o.name)) o.visible = false;
        if(human){                                            // photographed skin — never tint a person
          o.receiveShadow = true;
          /* surface physics per body part — flat albedo alone reads like plastic */
          const mkPhys = ()=>{
            const src = o.material;
            const p = new THREE.MeshPhysicalMaterial({
              map:src.map, normalMap:src.normalMap, normalScale:src.normalScale,
              roughnessMap:src.roughnessMap, metalnessMap:src.metalnessMap,   // the scans ship real PBR — keep it
              aoMap:src.aoMap, aoMapIntensity:src.aoMapIntensity,
              alphaMap:src.alphaMap,
              color:src.color, transparent:src.transparent, alphaTest:src.alphaTest, side:src.side });
            p.name = src.name;
            o.material = p; return p;
          };
          const mn = (o.material && o.material.name) || '';
          const isSkin = /Wolf3D_(Head|Body)/i.test(o.name)
                      || /(_head|_body|_mat)$/i.test(mn);      // Rocketbox and Renderpeople name by material
          const isHairM = /_opacity$/i.test(mn);               // Rocketbox hair/lash alpha cards
          if(isHairM){
            const p = mkPhys();
            p.transparent = true; p.alphaTest = 0.35; p.side = THREE.DoubleSide;
            p.roughness = 0.44; p.metalness = 0.04; p.envMapIntensity = 1.25;
            p.sheen = 0.6; p.sheenRoughness = 0.4;
          } else if(isSkin){                                   // skin: soft sheen, a hint of translucency
            const p = mkPhys();
            p.roughness = 0.58; p.metalness = 0;
            p.sheen = 0.5; p.sheenRoughness = 0.75; p.sheenColor = new THREE.Color(0xffd9c6);
            p.clearcoat = 0.08; p.clearcoatRoughness = 0.85;
            p.envMapIntensity = 1.5;
            if(p.normalMap) p.normalScale = new THREE.Vector2(1.35, 1.35);
          } else if(/^Eye(Left|Right)$/i.test(o.name)){       // wet eyes catch the room — the biggest tell
            const p = mkPhys();
            p.roughness = 0.06; p.metalness = 0; p.clearcoat = 1; p.clearcoatRoughness = 0.02;
            p.envMapIntensity = 2.6;
          } else if(/Hair|Beard/i.test(o.name)){
            const p = mkPhys();
            p.roughness = 0.42; p.metalness = 0.05; p.envMapIntensity = 1.3;
            p.sheen = 0.65; p.sheenRoughness = 0.4;
          } else if(/Teeth/i.test(o.name)){
            o.material.roughness = 0.22; o.material.envMapIntensity = 1.4;
          } else if(/Outfit|Footwear/i.test(o.name)){         // cloth: matte, no plastic highlight
            o.material.roughness = Math.max(0.72, o.material.roughness||0.7);
            o.material.metalness = 0; o.material.envMapIntensity = 1.1;
          }
          if(/Outfit_Top/i.test(o.name) && outfit.top){
            o.material = o.material.clone(); o.material.color.setHex(outfit.top);
            if(outfit.plainTop){ o.material.map = null; o.material.roughness = 0.88; o.material.needsUpdate = true; }
          }
          else if(/Outfit_Bottom/i.test(o.name) && outfit.bottom){ o.material = o.material.clone(); o.material.color.setHex(outfit.bottom); }
          else if(/Outfit_Footwear/i.test(o.name) && outfit.shoes){ o.material = o.material.clone(); o.material.color.setHex(outfit.shoes); }
          return;
        }
        o.material = o.material.clone();
        if(/joints/i.test(o.material.name || o.name)) o.material.color.setHex(0x2b2b30);
        else { o.material.color.setHex(tint); o.material.roughness = 0.72; o.material.metalness = 0.12; }
      } });
      root.add(model);
      /* some scans ship a centimetre node scale — normalise anyone who lands the wrong size */
      model.updateMatrixWorld(true);
      if(PR_RE.test(charFile)){ let sk = null; model.traverse(o=>{ if(!sk && o.isSkinnedMesh) sk = o; });
        if(sk){
          const ws = new THREE.Vector3(); sk.getWorldScale(ws);
          const rs = root.scale.x || 1, eff = ws.x / rs;
          if(eff > 1e-5 && Math.abs(eff - 1) > 0.25){
            model.scale.multiplyScalar(1/eff);
            model.updateMatrixWorld(true);
          }
          /* and stand them on the floor — some exports put the origin at the waist */
          const bb = new THREE.Box3().setFromObject(sk);
          const foot = bb.min.y - root.position.y;
          if(Math.abs(foot) > 0.08) model.position.y -= foot;
        } }
      const bones = {};
      model.traverse(o=>{ if(o.isBone){ const s = o.name.split(':').pop().replace(/^mixamorig/,''); if(!bones[s]) bones[s] = o; } });
      if(head==='pete'){                                     // full sleeves, both arms (RPM bones are metre-scale — safe to dress)
        const INK = new THREE.MeshStandardMaterial({ color:0x2c3540, roughness:.75 });
        [['LeftArm',0.05,0.26],['RightArm',0.05,0.26],['LeftForeArm',0.042,0.23],['RightForeArm',0.042,0.23]].forEach(([bn,r,len])=>{
          const b = bones[bn]; if(!b) return;
          const s = new THREE.Mesh(new THREE.CylinderGeometry(r, r*0.88, len, 12), INK);
          s.position.y = len/2 + 0.015; s.castShadow = true; b.add(s);
        });
      }
      const mixer = new THREE.AnimationMixer(model);
      const act = n => { const c = clips.find(c=>new RegExp('^'+n+'$','i').test(c.name)); return c ? mixer.clipAction(c) : null; };
      const idleA = act('idle'), walkA = act('walk');
      const agreeA = act('agree'), shakeA = act('headShake'), danceA = act('dance');
      if(idleA){ idleA.play(); idleA.time = Math.random()*(idleA.getClip().duration||1);   // no lockstep breathing
        idleA.timeScale = 0.92 + Math.random()*0.16; }
      if(walkA){ walkA.play(); walkA.weight = 0; }
      /* talking gestures ride ON TOP of the idle instead of replacing it */
      const talkA = ['talk','talk2'].map(n=>{
        const c = clips.find(x=>new RegExp('^'+n+'$','i').test(x.name));
        if(!c) return null;
        const add = THREE.AnimationUtils.makeClipAdditive(c.clone());
        const a = mixer.clipAction(add);
        a.blendMode = THREE.AdditiveAnimationBlendMode; a.play(); a.weight = 0;
        a.time = Math.random()*add.duration;                  // never in lockstep across the floor
        return a;
      }).filter(Boolean);
      let talkW = 0, talkPick = 0;
      /* the face: RPM low-poly carries mouthOpen + mouthSmile, and the eyes are their own meshes */
      const morphs = [];
      let eyeL = null, eyeR = null;
      model.traverse(o=>{
        if(o.isMesh && o.morphTargetDictionary){
          const d = o.morphTargetDictionary;
          if(d.mouthOpen !== undefined || d.mouthSmile !== undefined)
            morphs.push({ inf:o.morphTargetInfluences, open:d.mouthOpen, smile:d.mouthSmile });
        }
        if(/^EyeLeft$/i.test(o.name)) eyeL = o;
        if(/^EyeRight$/i.test(o.name)) eyeR = o;
      });
      const eyeRest = eyeL ? { s: eyeL.scale.clone(), q: eyeL.quaternion.clone() } : null;
      /* Rocketbox carries no morphs but a full facial bone rig — better, if you drive it */
      const FB = [];
      ['RB_MJaw','RB_LEyeBlinkTop','RB_REyeBlinkTop','RB_LEyeBlinkBottom','RB_REyeBlinkBottom',
       'RB_LEye','RB_REye','RB_LInnerEyebrow','RB_RInnerEyebrow','RB_LMouthCorner','RB_RMouthCorner']
        .forEach(n=>{ if(bones[n]){ rest[n] = bones[n].quaternion.clone(); FB.push(n); } });
      const hasFaceBones = FB.length > 0;
      const face = { open:0, openT:0, smile:0, smileT:0, blink:0, nextBlink: 1.5+Math.random()*4,
        gaze:new THREE.Vector2(), gazeT:new THREE.Vector2(), nextSaccade: 1+Math.random()*3 };
      /* my painted head rides the Head bone */
      const holder = new THREE.Group(); root.add(holder);     // head lives in root space, follows the bone
      const hb = bones.Head || bones.Neck;
      model.updateMatrixWorld(true);
      const rootQ = new THREE.Quaternion(), boneQ = new THREE.Quaternion();
      root.getWorldQuaternion(rootQ);
      const qRestRelInv = new THREE.Quaternion();
      if(hb){ hb.getWorldQuaternion(boneQ); qRestRelInv.copy(rootQ.clone().invert().multiply(boneQ)).invert(); }
      const headInner = new THREE.Group(); holder.add(headInner);   // glance/nod target — mapped onto the Head bone
      if(kind==='atg'){                                             // Alexander keeps his bronze
        const helm = new THREE.Group(); helm.position.set(0, 0.135, 0.004); headInner.add(helm);  // sits ON the skull, not over the eyes
        const BR = M(0x8a6f42,.45,.55), OX = M(0x66332c,.9);
        const hm = new THREE.Mesh(new THREE.SphereGeometry(0.108,26,20), BR);
        hm.scale.set(1.06,0.92,1.10); hm.position.y = 0.055; hm.castShadow = true; helm.add(hm);
        B(0.035,0.13,0.34, OX, 0, 0.17, -0.03, 0, helm);             // the crest
        B(0.20,0.03,0.045, BR, 0, 0.038, 0.092, 0, helm);            // brow band — above the eyes, not on them
      }
      /* a real sculpted head, where one exists for this seat */
      { const lkF = LIKENESS_ON ? (opts.likeness || LIKENESS[head] || LIKENESS[kind]) : null;
        if(lkF) attachLikeness({ file:lkF, headInner, character:root, hideExisting: kind!=='atg' })
                  .then(l=>{ refs.likeness = l; }).catch(()=>{}); }
      const hv = new THREE.Vector3(), hq = new THREE.Quaternion(), rq = new THREE.Quaternion();
      const followHead = ()=>{
        if(!hb) return;
        hb.getWorldPosition(hv); root.worldToLocal(hv); holder.position.copy(hv);
        root.getWorldQuaternion(rq); hb.getWorldQuaternion(hq);
        holder.quaternion.copy(rq.invert().multiply(hq).multiply(qRestRelInv));
      };
      const rest = {};
      ['LeftForeArm','RightForeArm','LeftArm','RightArm','LeftUpLeg','RightUpLeg','LeftLeg','RightLeg','Spine1','Hips','Head','Neck']
        .forEach(n=>{ if(bones[n]) rest[n] = bones[n].quaternion.clone(); });
      /* hands: a flat splayed hand is the giveaway — real ones rest curled */
      const FING = [];
      ['Left','Right'].forEach(side=>['Thumb','Index','Middle','Ring','Pinky'].forEach((f,fi)=>{
        for(let j=1;j<=3;j++){
          const n = side+'Hand'+f+j, b = bones[n];
          if(b){ rest[n] = b.quaternion.clone(); FING.push({ n, fi, j, s: side==='Left' ? 1 : -1 }); }
        }
      }));
      refs.skinned = true; refs.bones = bones; refs.mixer = mixer; refs.headHolder = holder;
      refs.headS = headInner; refs.headSeat = headInner;
      let walkW = 0, sitB = refs.pose==='sit' ? 1 : 0;
      /* film motion: nothing in a body arrives at once — the chest trails the hips,
         the head trails the chest, the hands trail the arms, and weight is never even */
      let yawPrev = root.rotation.y, yawVel = 0;
      let lagChest = 0, lagHead = 0, lagArm = 0, lagHand = 0, wPhase = Math.random()*20, sigh = 6+Math.random()*14;
      const qt = new THREE.Quaternion(), eu = new THREE.Euler();
      const addRot = (n, x, y, z)=>{ const b = bones[n]; if(!b || !rest[n]) return;
        b.quaternion.copy(rest[n]).multiply(qt.setFromEuler(eu.set(x, y, z))); };
      /* nudge layers on top of whatever the mocap just wrote — addRot would erase it */
      const nudge = (n, x, y, z)=>{ const b = bones[n]; if(!b) return;
        b.quaternion.multiply(qt.setFromEuler(eu.set(x, y, z))); };
      refs.setPose = p => { refs.pose = p; };
      refs.setPose(refs.pose);
      refs.applySkinned = (dt, t)=>{
        const walking = !!refs.isWalking;
        walkW += ((walking?1:0) - walkW) * Math.min(1, dt*6);
        if(idleA && walkA){
          const duck = (refs.anim && refs.anim.kind==='dance') ? 0.08 : 1;   // the dance owns the body
          idleA.weight = (1-walkW)*duck; walkA.weight = walkW;
          const sp = refs.walkSpeed || (walking ? SPEED : 0);
          mixer.timeScale = 1 + walkW*(0.18 + (sp/SPEED - 1)*0.55);   // stride follows actual speed — no slide, no mince
        }
        /* gesturing while it speaks — additive, so the idle keeps breathing underneath */
        const talking = performance.now() < (refs.speakUntil||0);
        if(talkA.length){
          if(talking && talkW < 0.02) talkPick = Math.floor(Math.random()*talkA.length);
          talkW += ((talking?1:0) - talkW) * Math.min(1, dt*3.2);
          talkA.forEach((a,i)=> a.weight = i===talkPick ? talkW*0.85 : 0);
        }
        mixer.update(dt);
        /* --- the face --- */
        if(morphs.length || eyeL || hasFaceBones){
          const amp = !talking ? 0
            : refs.mouthSynth !== false                        // syllable envelope: mouths never hang open
              ? Math.max(0, Math.sin(t*13.5+refs.idle)*0.5+0.5) * (0.55+Math.sin(t*4.3+refs.idle)*0.35)
              : (refs.mouth || 0);
          face.openT = talking ? 0.12 + amp*0.55 : 0;
          face.open += (face.openT - face.open) * Math.min(1, dt*(talking?22:9));
          face.smileT = refs.smile || 0;
          face.smile += (face.smileT - face.smile) * Math.min(1, dt*4);
          for(const m of morphs){
            if(m.open !== undefined) m.inf[m.open] = face.open;
            if(m.smile !== undefined) m.inf[m.smile] = face.smile;
          }
          /* blink: the eyeball squashes to a line — reads right at conversation distance */
          face.nextBlink -= dt;
          if(face.nextBlink <= 0){ face.blink = 1; face.nextBlink = 1.8 + Math.random()*5.2; }
          if(face.blink > 0) face.blink = Math.max(0, face.blink - dt*7.5);
          const lid = 1 - Math.sin(Math.min(1, face.blink) * Math.PI) * 0.94;
          /* gaze: saccades when idle, straight at you in a meeting */
          face.nextSaccade -= dt;
          if(face.nextSaccade <= 0){
            const wide = refs.meet ? 0.10 : 0.34;
            face.gazeT.set((Math.random()*2-1)*wide, (Math.random()*2-1)*wide*0.5);
            face.nextSaccade = refs.meet ? 1.4 + Math.random()*2.6 : 0.7 + Math.random()*2.4;
          }
          face.gaze.lerp(face.gazeT, Math.min(1, dt*14));      // eyes snap, they don't glide
          for(const e of [eyeL, eyeR]){
            if(!e || !eyeRest) continue;
            e.scale.set(eyeRest.s.x, eyeRest.s.y*lid, eyeRest.s.z);
            e.quaternion.copy(eyeRest.q);
            e.rotateY(face.gaze.x); e.rotateX(face.gaze.y);
          }
          if(hasFaceBones){                                    // jaw, lids, eyes and brows, driven by bone
            const shut = 1 - lid;                              // 0 open, ~0.94 closed
            addRot('RB_MJaw', face.open*0.34, 0, 0);
            addRot('RB_LEyeBlinkTop', shut*0.5, 0, 0);  addRot('RB_REyeBlinkTop', shut*0.5, 0, 0);
            addRot('RB_LEyeBlinkBottom', -shut*0.22, 0, 0); addRot('RB_REyeBlinkBottom', -shut*0.22, 0, 0);
            addRot('RB_LEye', 0, face.gaze.x, 0); addRot('RB_REye', 0, face.gaze.x, 0);
            const brow = face.smile*0.10 + (talking ? 0.05 : 0);
            addRot('RB_LInnerEyebrow', -brow, 0, 0); addRot('RB_RInnerEyebrow', -brow, 0, 0);
            addRot('RB_LMouthCorner', 0, 0, face.smile*0.16);
            addRot('RB_RMouthCorner', 0, 0, -face.smile*0.16);
          }
        }
        /* sitting down and standing up are movements, not switches */
        const wantSit = (refs.pose==='sit' && !walking) ? 1 : 0;
        sitB += (wantSit - sitB) * Math.min(1, dt*3.6);
        if(sitB > 0.002){
          const e = sitB*sitB*(3-2*sitB);                      // ease so the hips settle rather than drop
          addRot('LeftUpLeg', SIT_X*e, 0, 0.06*e); addRot('RightUpLeg', SIT_X*e, 0, -0.06*e);
          addRot('LeftLeg', (-SIT_X-0.1)*e, 0, 0); addRot('RightLeg', (-SIT_X-0.1)*e, 0, 0);
          addRot('Spine1', 0.12*e, 0, 0);
          model.position.y = -0.42*e;
        } else model.position.y = 0;
        const an = refs.anim;
        /* real mocap emotes for nod / head-shake */
        if(an && !an._mo && (an.kind==='nod' || an.kind==='shake' || an.kind==='dance')){
          an._mo = true;
          const e = an.kind==='nod' ? agreeA : an.kind==='dance' ? danceA : shakeA;
          if(e){ e.reset().setLoop(THREE.LoopOnce, 1); e.clampWhenFinished = false;
            e.fadeIn(0.15).play(); if(idleA) idleA.fadeOut(0.15);
            const back = ()=>{ if(idleA) idleA.fadeIn(0.25); mixer.removeEventListener('finished', back); };
            mixer.addEventListener('finished', back); }
        }
        /* glances & meeting gaze land on the actual Head bone */
        if(!(an && (an.kind==='nod'||an.kind==='shake'||an.kind==='dance')))
          addRot('Head', headInner.rotation.x*0.85, headInner.rotation.y*0.85, 0);
        if(refs.style==='paint' && refs.pose==='stand' && !an && !walking){  // brush in hand, lost in it
          addRot('RightArm', 0.4, 0, -0.55);
          addRot('RightForeArm', -0.35 + Math.sin(t*3.1+refs.idle)*0.2, 0, -0.35);
          addRot('Spine1', 0.05, Math.sin(t*0.7)*0.06, 0);
        }
        if(an && an.kind==='type' && refs.pose==='sit'){
          addRot('LeftForeArm', 0, 0, 0.9 + Math.sin(an.t*22)*0.08);
          addRot('RightForeArm', 0, 0, -0.9 - Math.sin(an.t*22+1.3)*0.08);
        } else if(refs.style==='type' && refs.pose==='sit' && !an){
          addRot('LeftForeArm', 0, 0, 0.85 + Math.sin(t*13+refs.idle)*0.05);
          addRot('RightForeArm', 0, 0, -0.85 - Math.sin(t*13+refs.idle+1.2)*0.05);
        }
        if(an && an.kind==='argue' && refs.pose==='stand'){
          addRot('RightArm', 0, 0, -0.9 - Math.max(0,Math.sin(an.t*5))*0.5);
          addRot('RightForeArm', 0, 0, -0.7);
        }
        if(envCarrier === refs.id){
          addRot('RightArm', 0.25, 0, -0.55);
          addRot('RightForeArm', 0, 0, -1.35);
        }
        /* --- overlapping action: the drive is the root's angular velocity --- */
        { let dy = root.rotation.y - yawPrev;
          while(dy > Math.PI) dy -= 2*Math.PI; while(dy < -Math.PI) dy += 2*Math.PI;
          yawPrev = root.rotation.y;
          const inst = dy / Math.max(dt, 1e-3);
          yawVel += (inst - yawVel) * Math.min(1, dt*9);       // smooth the driver, not the result
          const drive = Math.max(-2.4, Math.min(2.4, yawVel));
          lagChest += (-drive*0.085 - lagChest) * Math.min(1, dt*7.5);   // ~130ms behind the hips
          lagHead  += ( lagChest*0.85 - lagHead)  * Math.min(1, dt*5.2); // ~190ms behind the chest
          lagArm   += (-drive*0.16  - lagArm)   * Math.min(1, dt*6.0);
          lagHand  += ( lagArm*0.75 - lagHand)  * Math.min(1, dt*4.4);
          nudge('Spine1', 0, lagChest, 0);
          nudge('Neck',   0, lagHead*0.55, 0);
          nudge('LeftArm',  0, 0,  lagArm*0.5);  nudge('RightArm',  0, 0, -lagArm*0.5);
          nudge('LeftHand', 0, lagHand, 0);      nudge('RightHand', 0, lagHand, 0);
        }
        /* --- weight shift: two incommensurate periods so it never reads as a metronome --- */
        if(!walking && refs.pose === 'stand'){
          wPhase += dt;
          const w = Math.sin(wPhase*0.29 + refs.idle) * Math.sin(wPhase*0.11 + refs.idle*0.7);
          nudge('Hips',   0, 0,  w*0.040);                     // hips drop toward the loaded leg
          nudge('Spine2', 0, 0, -w*0.026);                     // the shoulders counter it
          nudge('Neck',   0, 0,  w*0.012);                     // the head stays level, so it leans back
        }
        /* --- breath: deeper at rest, shallower and faster mid-sentence, with the odd sigh --- */
        { sigh -= dt;
          const deep = sigh < 0.9 ? (0.9 - Math.max(0, sigh)) * 1.5 : 0;
          if(sigh < 0) sigh = 9 + Math.random()*16;
          const rate = talking ? 1.9 : 1.05, amp = (talking ? 0.007 : 0.013) + deep*0.012;
          const br = Math.sin(t*rate + refs.idle);
          nudge('Spine2', br*amp, 0, 0);
          nudge('LeftShoulder',  0, 0, -br*amp*0.7);
          nudge('RightShoulder', 0, 0,  br*amp*0.7);
        }
        if(FING.length){                                      // resting curl, deeper at the tips, thumb barely
          const base = (refs.handCurl !== undefined ? refs.handCurl : 0.30)
                     + Math.sin(t*0.8 + refs.idle)*0.015;      // hands are never perfectly still
          for(const f of FING){
            const amt = base * (f.fi===0 ? 0.30 : 1) * (0.72 + f.j*0.2) * (f.fi===4 ? 1.18 : 1);
            addRot(f.n, 0, 0, f.s*amt);
          }
        }
        if(refs.style==='handsout' && refs.pose==='stand' && !an){          // wrists resting through the bars
          addRot('LeftArm', 0, 0.5, 0.98); addRot('RightArm', 0, -0.5, -0.98);
          addRot('LeftForeArm', 0, 0, 0.22); addRot('RightForeArm', 0, 0, -0.22);
          addRot('Head', 0.08, 0, 0);
        }
        model.updateMatrixWorld(true);
        followHead();
      };
    }).catch(()=>{});
  }
  return refs;
}
/* Ready Player Me sample avatars (readyplayerme/animation-library-demo, MIT) — real textured humans.
   a1 f casual · a2 m beard/orange puffer · a3 f dress+glasses · a4 m bald athletic · a5 m beard · a6 m smart jacket.
   Six faces across more seats than six — duplicates sit in far-apart wings and wear different outfit tints. */
/* pr: = photoreal Renderpeople scans · hu: = Microsoft Rocketbox (MIT) · a1..a6 = Ready Player Me.
   All three families are retargeted to the same skeleton, so one clip library drives everyone. */
const CHAR_FILE = { jobs:'orb', woz:'hu:kenji', buffett:'orb', bard:'orb', slick:'orb',
  bun:PR?'pr:carla':'hu:amara', ive:'a4', ben:'hu:hugh', atg:PR?'pr:eric':'hu:andre', headset:'orb',
  beanie:PR?'pr:claudia':'hu:ingrid', phones:'hu:elena', pete:'hu:marcus',
  bot:'Xbot', suit:'Xbot', guard:'Soldier', mascot:'RobotExpressive',
  cleaner:'a3', staff:'a6' };
const KEEP_TEXTURE = { Soldier:1, RobotExpressive:1 };      // real textures — never flat-tint these
const RPM_RE = /^a\d$/;
/* The Renderpeople scans are licensed to Ben but NOT redistributable, so they are opt-in
   with ?pr and never ship in a public build. Everything else here is MIT or CC0. */
const PR = location.search.includes('pr');
const PR_RE = /^pr:/;
const HU_RE = /^hu:/;                                   // Microsoft Rocketbox, retargeted to our skeleton (MIT)                                   // photoreal scans live in their own folder
/* namesake agents appear as themselves — Ben's particle piece, one accent per seat (never coral, never cyan) */
const ORB_ACCENT = { jobs:0xd4d6de, buffett:0xd9b36a, bard:0xc98d52, slick:0x8291c9, headset:0xa79ae0 };
/* Ben's LISA abstract piece (loudfair.com/contact, assets/custom/abstract.js) ported in-scene:
   fibonacci particle shell + wireframe core + orbital ring, speech-reactive energy. */
function buildOrb(accent){
  const SHELL = 900;
  const positions = new Float32Array(SHELL*3), phases = new Float32Array(SHELL),
        sizes = new Float32Array(SHELL), mixes = new Float32Array(SHELL);
  for(let i=0;i<SHELL;i++){
    const t = i/SHELL, phi = Math.acos(1-2*t), theta = Math.PI*(1+Math.sqrt(5))*i;
    const r = 0.20 + (Math.sin(i*12.9898)*0.5+0.5)*0.085;
    positions[i*3]   = r*Math.sin(phi)*Math.cos(theta);
    positions[i*3+1] = r*Math.sin(phi)*Math.sin(theta);
    positions[i*3+2] = r*Math.cos(phi);
    phases[i] = (i*0.6180339887)%1;
    sizes[i]  = 1.6 + ((i*0.7548776662)%1)*2.0;
    mixes[i]  = Math.pow((i*0.3934664)%1, 3.0)*0.85;
  }
  const pgeo = new THREE.BufferGeometry();
  pgeo.setAttribute('position', new THREE.BufferAttribute(positions,3));
  pgeo.setAttribute('aPhase', new THREE.BufferAttribute(phases,1));
  pgeo.setAttribute('aSize', new THREE.BufferAttribute(sizes,1));
  pgeo.setAttribute('aMix', new THREE.BufferAttribute(mixes,1));
  const mat = new THREE.ShaderMaterial({
    transparent:true, depthWrite:false, blending:THREE.AdditiveBlending,
    uniforms:{ uTime:{value:0}, uEnergy:{value:0.2}, uColor:{value:accent.clone()},
      uHot:{value:new THREE.Color(0xffffff)}, uPixelRatio:{value:Math.min(window.devicePixelRatio||1,2)} },
    vertexShader:`
      uniform float uTime; uniform float uEnergy; uniform float uPixelRatio;
      attribute float aPhase; attribute float aSize; attribute float aMix;
      varying float vAlpha; varying float vMix;
      vec3 flow(vec3 p, float t){
        return vec3(
          sin(p.y*14.0+t*0.40)+cos(p.z*11.0-t*0.30),
          sin(p.z*12.0+t*0.35)+cos(p.x*15.0-t*0.25),
          sin(p.x*13.0+t*0.30)+cos(p.y*12.0-t*0.40))*0.5;
      }
      void main(){
        vec3 p = position;
        float breathe = sin(uTime*0.55 + aPhase*6.28318)*0.5+0.5;
        p += flow(p, uTime + aPhase*3.0)*(0.012 + uEnergy*0.02);
        p *= 1.0 + breathe*(0.05 + uEnergy*0.22);
        vec4 mv = modelViewMatrix*vec4(p,1.0);
        gl_Position = projectionMatrix*mv;
        float twinkle = 0.6 + 0.4*sin(uTime*(1.4+aPhase*2.2)+aPhase*40.0);
        vAlpha = twinkle*(0.42 + uEnergy*0.58);
        vMix = aMix;
        gl_PointSize = min(aSize*(1.0 + breathe*0.8 + uEnergy*0.45)*uPixelRatio*(7.0/-mv.z), 22.0);
      }`,
    fragmentShader:`
      uniform vec3 uColor; uniform vec3 uHot;
      varying float vAlpha; varying float vMix;
      void main(){
        vec2 c = gl_PointCoord - 0.5;
        float d = length(c);
        if(d > 0.5) discard;
        float core = smoothstep(0.18, 0.0, d);
        float halo = smoothstep(0.5, 0.05, d);
        vec3 col = mix(uColor, uHot, clamp(vMix,0.0,1.0));
        col += uHot*core*0.55;
        gl_FragColor = vec4(col, (halo*0.85 + core*0.6)*vAlpha);
      }`,
  });
  const group = new THREE.Group();
  const points = new THREE.Points(pgeo, mat); points.frustumCulled = false; group.add(points);
  const core = new THREE.LineSegments(
    new THREE.WireframeGeometry(new THREE.IcosahedronGeometry(0.115,1)),
    new THREE.LineBasicMaterial({ transparent:true, opacity:0.35, color:accent }));
  group.add(core);
  const inner = new THREE.LineSegments(
    new THREE.WireframeGeometry(new THREE.IcosahedronGeometry(0.062,0)),
    new THREE.LineBasicMaterial({ transparent:true, opacity:0.5, color:accent }));
  group.add(inner);
  const ring = new THREE.LineLoop(
    new THREE.RingGeometry(0.31,0.31,96),
    new THREE.LineBasicMaterial({ transparent:true, opacity:0.22, color:accent }));
  ring.rotation.x = Math.PI*0.42; group.add(ring);
  const light = new THREE.PointLight(accent, 0.8, 3.2, 2); group.add(light);
  const gaze = new THREE.Group(); group.add(gaze);       // inert glance target — the generic loop writes here
  return { group, mat, core, inner, ring, light, gaze, seed: Math.random()*6.28 };
}
function orbPulse(id){
  const a = ACTORS[id];
  if(a && a.orb) a.energyUntil = performance.now() + 1400;
}
/* recolours the clothing — skin and hair never touched. plainTop drops the printed texture
   (the sample tees carry a vendor logo) and keeps the fabric normals: a clean solid shirt. */
const OUTFIT_TINT = {
  cleaner:{ top:0x7f8a3e, bottom:0x3a3d33, plainTop:true },  // hi-vis olive workwear
  staff:{ top:0x232733, bottom:0x232733, plainTop:true },   // security in dark uniform
  atg:{ top:0x7a4a34, bottom:0x4a3c2c, shoes:0x2a2624 },    // oxblood puffer, bronze helm, no rainbow sneakers
  ben:{ top:0x2e3a52, plainTop:true },                      // navy
  ive:{ top:0xcfd2d6, plainTop:true },                      // his plain light tee
  pete:{ top:0x27292d, bottom:0x27292d, plainTop:true, shoes:0xd8d6d2 },   // the matching black tracksuit, white creps
};
const SEAT_TINT = { jobs:0x4a4a52, woz:0x6b7f96, buffett:0x5c5c68, bard:0x77543a, slick:0x54545e,
  bun:0x8a6f7c, ive:0x9aa0a8, ben:0x54678e, atg:0x8a5a44, headset:0xa8a49c, beanie:0x8a7458, phones:0xa8a49c, bot:0xa8a49c, suit:0x54545e };
const CHAR_CACHE = {};
const gltfLoader = new GLTFLoader(LOAD);
/* ---------- photo-scanned props (Poly Haven, CC0) replace the box furniture ---------- */
const PROP_CACHE = {};
function loadProp(slug){
  if(!PROP_CACHE[slug]) PROP_CACHE[slug] = new Promise((res, rej)=>{
    gltfLoader.load('/assets/props/'+slug+'/'+slug+'.gltf', g=>res(g.scene), undefined, rej);
  });
  return PROP_CACHE[slug];
}
function prop(slug, x, z, ry=0, o={}){
  const g = new THREE.Group(); g.position.set(x, o.y||0, z); g.rotation.y = ry; world.add(g);
  loadProp(slug).then(src=>{
    const m = src.clone(true);
    m.traverse(n=>{ if(n.isMesh){ n.castShadow = true; n.receiveShadow = true; } });
    const box = new THREE.Box3().setFromObject(m);
    const size = box.getSize(new THREE.Vector3());
    let s = o.h ? o.h/size.y : (o.w ? o.w/Math.max(size.x, size.z) : 1);
    m.scale.setScalar(s*(o.s||1));
    const b2 = new THREE.Box3().setFromObject(m);
    m.position.y -= b2.min.y;
    m.position.x -= (b2.min.x+b2.max.x)/2;
    m.position.z -= (b2.min.z+b2.max.z)/2;
    g.add(m);
  }).catch(()=>{});
  return g;
}
/* RPM avatars carry no clips — the open animation library supplies them, bound by matching bone names */
let RPM_ANIMS = null;
function rpmAnims(){
  if(RPM_ANIMS) return RPM_ANIMS;
  const FILES = { idle:'M_Standing_Idle_001', walk:'M_Walk_001',
    agree:'M_Standing_Expressions_001', headShake:'M_Standing_Idle_Variations_002',
    talk:'M_Talking_Variations_001', talk2:'M_Talking_Variations_005',
    dance:'M_Dances_001', dance2:'M_Dances_005' };
  RPM_ANIMS = Promise.all(Object.entries(FILES).map(([name, file]) =>
    new Promise(res=>{
      gltfLoader.load('/assets/rpm/anim/'+file+'.glb', g=>{
        const c = g.animations[0]; if(!c){ res(null); return; }
        c.name = name;
        c.tracks = c.tracks.filter(t=>!/\.position$/.test(t.name) || /Hips\./.test(t.name));
        for(const t of c.tracks){                       // pin the hips — library clips carry root motion
          if(/Hips\.position$/.test(t.name)){
            const v = t.values;
            for(let i=0;i<v.length;i+=3){ v[i]=v[0]; v[i+2]=v[2]; }
          }
        }
        res(c);
      }, undefined, ()=>res(null));
    })
  )).then(cs=>cs.filter(Boolean));
  return RPM_ANIMS;
}
function loadChar(name){
  if(!CHAR_CACHE[name]) CHAR_CACHE[name] = HU_RE.test(name)
    ? Promise.all([
        new Promise((res, rej)=>gltfLoader.load('/assets/humans/'+name.slice(3)+'.glb', g=>res(g.scene), undefined, rej)),
        rpmAnims(),
      ]).then(([scene, clips])=>({ scene, clips }))
    : PR_RE.test(name)
    ? Promise.all([
        new Promise((res, rej)=>gltfLoader.load('/assets/photoreal/'+name.slice(3)+'.glb', g=>res(g.scene), undefined, rej)),
        rpmAnims(),
      ]).then(([scene, clips])=>({ scene, clips }))
    : RPM_RE.test(name)
    ? Promise.all([
        new Promise((res, rej)=>gltfLoader.load('/assets/rpm/'+name+'.glb', g=>res(g.scene), undefined, rej)),
        rpmAnims(),
      ]).then(([scene, clips])=>({ scene, clips }))
    : new Promise((res, rej)=>{
        gltfLoader.load('/assets/chars/'+name+'.glb',
          g => res({ scene:g.scene, clips:g.animations }), undefined, rej);
      });
  return CHAR_CACHE[name].then(({scene, clips})=>({ model: SkeletonUtils.clone(scene), clips }));
}
/* ---------- room TVs — the actual people play here (LISA contact-page pattern) ----------
   Source contract (same as the contact build): drop a square, muted, looping H.264 at
   public/assets/tv/<roomId>.mp4 and the room's TV plays it. No file → the TV runs an
   archival Ken-Burns loop of the person from the CC portrait set. */
const TVS = [];
function tvUnit(roomId, x, z, ry, fallbackImg, contain){
  const g = new THREE.Group(); g.position.set(x, 1.62, z); g.rotation.y = ry; world.add(g);
  B(0.12,0.3,0.05, M(0x1c1c20,.6,.4), 0,0,-0.05, 0, g);                    // wall mount
  const bezel = B(0.92,0.92,0.055, M(0x141417,.45,.5), 0,0,0, 0, g);
  bezel.castShadow = true;
  const smat = new THREE.MeshStandardMaterial({ color:0x000000, emissive:0xffffff,
    emissiveIntensity:0.0, roughness:0.32, metalness:0.05 });
  const screen = new THREE.Mesh(new THREE.PlaneGeometry(0.8,0.8), smat);
  screen.position.z = 0.0285; g.add(screen);
  const led = new THREE.Mesh(new THREE.CircleGeometry(0.008,8),
    new THREE.MeshStandardMaterial({ color:0x111111, emissive:0xc9694a, emissiveIntensity:1.2 }));
  led.position.set(0.4,-0.43,0.029); g.add(led);
  const glow = new THREE.PointLight(0xbfc4d2, 0, 1.9, 2); glow.position.z = 0.5; g.add(glow);
  const unit = { fall:null, phase:Math.random()*20, room:roomId, smat, glow, base:null };
  const arm = tex => { smat.emissiveMap = tex; smat.emissiveIntensity = 1.05; smat.needsUpdate = true;
    if(!unit.base) unit.base = tex;                          // what the set shows when nobody is talking
    SCREENS.push({ m:smat, base:1.05 }); glow.intensity = 0.55; };
  unit.play = tex => { smat.emissiveMap = tex; smat.needsUpdate = true; };
  unit.restore = ()=>{ if(unit.base){ smat.emissiveMap = unit.base; smat.needsUpdate = true; } };
  /* try Ben's drop-in video first */
  const v = document.createElement('video');
  v.muted = true; v.loop = true; v.playsInline = true; v.autoplay = true;
  v.addEventListener('canplay', ()=>{ if(unit.done) return; unit.done = true;
    v.play().catch(()=>{}); const vt = new THREE.VideoTexture(v); vt.colorSpace = THREE.SRGBColorSpace; arm(vt); }, { once:true });
  v.addEventListener('error', ()=>{ if(unit.done || !fallbackImg) return; unit.done = true;
    const c = document.createElement('canvas'); c.width = c.height = 512;
    const ctx = c.getContext('2d');
    const tex = new THREE.CanvasTexture(c); tex.colorSpace = THREE.SRGBColorSpace;
    const img = new Image();
    img.onload = ()=>{ unit.fall = { ctx, tex, img, contain }; arm(tex); };
    img.src = fallbackImg;
  }, { once:true });
  v.src = '/assets/tv/'+roomId+'.mp4';
  TVS.push(unit);
  return g;
}
function tvTick(t){
  for(const u of TVS){
    if(!u.fall) continue;
    if((u.fr = (u.fr||0)+1) & 1) continue;                                  // 30fps is plenty for a set
    const { ctx, img, tex, contain } = u.fall, S = 512;
    if(!img.naturalWidth) continue;
    const tt = t*0.5 + u.phase;
    ctx.fillStyle = '#08080a'; ctx.fillRect(0,0,S,S);
    const zoom = (contain?0.92:1.14) + Math.sin(tt*0.10)*0.055;             // slow Ken Burns cycle
    const base = contain ? Math.min(S/img.width, S/img.height) : Math.max(S/img.width, S/img.height);
    const sc = base*zoom, w = img.width*sc, h = img.height*sc;
    const px = (S-w)/2 + Math.sin(tt*0.061)*S*0.035, py = (S-h)/2 + Math.cos(tt*0.043)*S*0.03;
    ctx.drawImage(img, px, py, w, h);
    ctx.fillStyle = 'rgba(6,6,10,0.24)'; ctx.fillRect(0,0,S,S);             // broadcast grade
    const rg = ctx.createRadialGradient(S/2,S/2,S*0.32,S/2,S/2,S*0.72);
    rg.addColorStop(0,'rgba(0,0,0,0)'); rg.addColorStop(1,'rgba(0,0,0,0.5)');
    ctx.fillStyle = rg; ctx.fillRect(0,0,S,S);
    ctx.fillStyle = 'rgba(0,0,0,0.10)';
    for(let y=((t*34)|0)%4; y<S; y+=4) ctx.fillRect(0,y,S,1);               // drifting scanlines
    tex.needsUpdate = true;
  }
}
/* one per namesake room, on a clear side wall, angled into the room */
tvUnit('efficacy',    -14.7, -8.6, Math.PI/2, '/assets/faces/obama.jpg');
tvUnit('shakespeare', -10.7, -9.9, Math.PI/2, '/assets/faces/bard.jpg');
tvUnit('wozniak',      -2.7, -9.6, Math.PI/2, '/assets/faces/woz.jpg');
tvUnit('ive',           5.3, -9.8, Math.PI/2, '/assets/faces/ive.jpg');
tvUnit('jobs',         12.3, -9.6, Math.PI/2, '/assets/faces/jobs.jpg');
tvUnit('buffett',      -5.6, 12.2, Math.PI, '/assets/faces/buffett.jpg');
tvUnit('loudfair',     16.7,  9.0, -Math.PI/2, '/assets/tv/loudfair-logo.png', true);

/* ---------- a valve-era set, playing a reel that never repeats exactly ----------
   No video file: the picture is drawn every frame — a portrait that drifts, cut with
   title cards of the lines, over scanlines, roll bar and a warm phosphor glow. */
const CRTS = [];
function crtUnit(x, z, ry){
  const g = new THREE.Group(); g.position.set(x, 0, z); g.rotation.y = ry; world.add(g);
  B(0.62, 0.5, 0.44, WOODD, 0, 0.25, 0, 0, g);                       // the stand
  const body = B(0.86, 0.72, 0.66, M(0x4a3a2c,.78,.05), 0, 0.86, 0, 0, g); body.castShadow = true;
  B(0.9, 0.06, 0.7, M(0x6b5540,.7), 0, 1.25, 0, 0, g);               // veneer top
  [[-0.3],[0.3]].forEach(p=>Cyl(0.035, 0.16, M(0x2b2b30,.6,.4), p[0], 0.06, 0.2, g, 8));  // legs
  const c = document.createElement('canvas'); c.width = 384; c.height = 288;
  const ctx = c.getContext('2d');
  const tex = new THREE.CanvasTexture(c); tex.colorSpace = THREE.SRGBColorSpace;
  const screen = new THREE.Mesh(new THREE.PlaneGeometry(0.64, 0.48),
    new THREE.MeshStandardMaterial({ map:tex, emissive:0xffffff, emissiveMap:tex,
      emissiveIntensity:1.35, roughness:.28 }));
  screen.position.set(0, 0.88, 0.335); g.add(screen);
  SCREENS.push({ m:screen.material, base:1.35 });
  const glow = new THREE.PointLight(0xd9c8a8, 1.6, 3.2, 2);           // the set lights the room
  glow.position.set(0, 0.9, 0.6); g.add(glow);
  const img = new Image(); img.src = '/assets/faces/bard.jpg';
  CRTS.push({ ctx, tex, img, glow, ph: Math.random()*40 });
  return g;
}
const CRT_CARDS = [
  'ALL THE WORLD’S A STAGE',
  'BREVITY IS THE SOUL OF WIT',
  'THE PLAY’S THE THING',
  'WORDS, WORDS, WORDS',
  'WE FEW, WE HAPPY FEW',
];
function crtTick(t){
  for(const u of CRTS){
    if((u.fr = (u.fr||0)+1) % 2) continue;                            // 30fps is a generous frame rate for 1957
    const { ctx: g, tex } = u, W = 384, H = 288;
    const tt = t + u.ph;
    const shot = Math.floor(tt/7) % 4;                                 // the reel cuts every seven seconds
    const k = (tt/7) % 1;
    g.fillStyle = '#0d0b09'; g.fillRect(0, 0, W, H);
    if(shot === 1 || shot === 3){                                      // a title card, letter-boxed
      g.fillStyle = '#141009'; g.fillRect(0, 0, W, H);
      g.fillStyle = 'rgba(232,214,178,' + (0.5 + Math.sin(k*Math.PI)*0.5).toFixed(3) + ')';
      g.textAlign = 'center'; g.font = '600 21px Georgia, serif';
      const words = CRT_CARDS[(Math.floor(tt/7) + u.ph|0) % CRT_CARDS.length].split(' ');
      const lines = []; let ln = '';
      for(const w of words){ const test = ln ? ln+' '+w : w;
        if(test.length > 17){ lines.push(ln); ln = w; } else ln = test; }
      lines.push(ln);
      lines.forEach((l, i)=>g.fillText(l, W/2, H/2 - (lines.length-1)*15 + i*30));
    } else if(u.img.naturalWidth){                                     // the portrait, drifting
      const zoom = 1.22 + Math.sin(tt*0.11)*0.09;
      const base = Math.max(W/u.img.width, H/u.img.height) * zoom;
      const w = u.img.width*base, h = u.img.height*base;
      g.drawImage(u.img, (W-w)/2 + Math.sin(tt*0.07)*22, (H-h)/2 + Math.cos(tt*0.05)*16, w, h);
      g.fillStyle = 'rgba(30,20,10,0.30)'; g.fillRect(0, 0, W, H);
    }
    /* the tube: scanlines, a slow roll bar, vignette and a little static */
    g.fillStyle = 'rgba(0,0,0,0.30)';
    for(let y = ((t*26)|0) % 3; y < H; y += 3) g.fillRect(0, y, W, 1);
    const roll = ((tt*0.22) % 1.6 - 0.3) * H;
    const rg = g.createLinearGradient(0, roll, 0, roll + 46);
    rg.addColorStop(0, 'rgba(255,240,210,0)');
    rg.addColorStop(0.5, 'rgba(255,240,210,0.10)');
    rg.addColorStop(1, 'rgba(255,240,210,0)');
    g.fillStyle = rg; g.fillRect(0, roll, W, 46);
    const vg = g.createRadialGradient(W/2, H/2, H*0.3, W/2, H/2, H*0.78);
    vg.addColorStop(0, 'rgba(0,0,0,0)'); vg.addColorStop(1, 'rgba(0,0,0,0.66)');
    g.fillStyle = vg; g.fillRect(0, 0, W, H);
    if(Math.random() < 0.06){                                          // the tube coughs
      g.fillStyle = 'rgba(255,246,225,0.05)'; g.fillRect(0, Math.random()*H, W, 2);
    }
    u.glow.intensity = 1.35 + Math.sin(tt*7.1)*0.12 + Math.sin(tt*1.7)*0.18;
    tex.needsUpdate = true;
  }
}
/* ---------- the answer plays as film: a library of clips the seats speak through ----------
   Drop MP4s at /assets/clips/<seat>/<mood>.mp4 and list them in manifest.json. When a seat
   answers, its mood tag picks a clip and the room's set plays it for the length of the line. */
const CLIP_MOODS = ['think','agree','refuse','deliver','laugh','idle'];
let CLIP_MANIFEST = null;
const CLIP_CACHE = {};
fetch('/assets/clips/manifest.json').then(r=>r.ok?r.json():null)
  .then(j=>{ CLIP_MANIFEST = j; }).catch(()=>{});
function clipFor(seat, mood){
  if(!CLIP_MANIFEST) return null;
  const have = CLIP_MANIFEST[seat];
  if(!have || !have.length) return null;
  const want = CLIP_MOODS.includes(mood) ? mood : 'deliver';
  return have.includes(want) ? want : (have.includes('deliver') ? 'deliver' : have[0]);
}
function playClip(seat, mood, seconds){
  const room = SEAT_ROOM[seat] || seat;
  const tv = TVS.find(u=>u.room === room);
  if(!tv || !tv.play) return false;
  const pick = clipFor(seat, mood);
  if(!pick) return false;
  const key = seat + '/' + pick;
  const start = tex => {
    tv.play(tex);
    if(tex.image && tex.image.play){ try{ tex.image.currentTime = 0; tex.image.play(); }catch(e){} }
    clearTimeout(tv._back);
    tv._back = setTimeout(()=>{ tv.restore(); if(tex.image && tex.image.pause) tex.image.pause(); },
      Math.max(2200, (seconds||4)*1000 + 600));
  };
  if(CLIP_CACHE[key]){ start(CLIP_CACHE[key]); return true; }
  const v = document.createElement('video');
  v.muted = true; v.loop = true; v.playsInline = true; v.preload = 'auto';
  v.addEventListener('canplay', ()=>{
    const t = new THREE.VideoTexture(v); t.colorSpace = THREE.SRGBColorSpace;
    CLIP_CACHE[key] = t; start(t);
  }, { once:true });
  v.addEventListener('error', ()=>{ CLIP_CACHE[key] = null; }, { once:true });
  v.src = '/assets/clips/' + seat + '/' + pick + '.mp4';
  return true;
}
/* ---------- framed quotes — each office wears its own words ---------- */
function quoteTex(text, author){
  const c = document.createElement('canvas'); c.width = 1024; c.height = 640;
  const g = c.getContext('2d');
  g.fillStyle = '#15151a'; g.fillRect(0,0,1024,640);                       // charcoal mat
  g.strokeStyle = 'rgba(190,182,160,0.5)'; g.lineWidth = 3;
  g.strokeRect(46,46,932,548);
  g.fillStyle = '#cfc8b4'; g.textAlign = 'center';
  g.font = 'italic 64px Georgia, serif';
  const words = text.split(' '), lines = []; let line = '';
  for(const w of words){
    const tst = line ? line+' '+w : w;
    if(g.measureText(tst).width > 830 && line){ lines.push(line); line = w; }
    else line = tst;
  }
  lines.push(line);
  const lh = 84, y0 = 320 - (lines.length-1)*lh/2 - 20;
  lines.forEach((l,i)=>g.fillText('“'.repeat(i===0?1:0)+l+(i===lines.length-1?'”':''), 512, y0+i*lh));
  g.font = '30px Georgia, serif'; g.fillStyle = 'rgba(176,168,146,0.85)';
  g.fillText('— '+author, 512, y0 + lines.length*lh + 26);
  const tex = new THREE.CanvasTexture(c); tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
function quoteFrame(x, z, ry, text, author, w=1.7){
  const h = w*0.625;
  const g = new THREE.Group(); g.position.set(x, 1.72, z); g.rotation.y = ry; world.add(g);
  const fr = B(w+0.08, h+0.08, 0.045, M(0x3a2f24,.8,.15), 0,0,0, 0, g); fr.castShadow = true;
  const face = new THREE.Mesh(new THREE.PlaneGeometry(w, h),
    new THREE.MeshStandardMaterial({ map:quoteTex(text, author), roughness:.62 }));
  face.position.z = 0.025; g.add(face);
  return g;
}
quoteFrame(-14.72, -4.6,  Math.PI/2, 'Yes we can.', 'OBAMA');
quoteFrame(-10.72, -4.8,  Math.PI/2, 'Brevity is the soul of wit.', 'SHAKESPEARE');
quoteFrame( -2.72, -4.6,  Math.PI/2, 'Never trust a computer you can’t throw out a window.', 'STEVE WOZNIAK', 2.0);
quoteFrame(  5.28, -4.8,  Math.PI/2, 'Simplicity is not the absence of clutter.', 'JONY IVE');
quoteFrame( 12.28, -4.6,  Math.PI/2, 'Stay hungry. Stay foolish.', 'STEVE JOBS');
quoteFrame( -6.72,  5.2,  Math.PI/2, 'Price is what you pay. Value is what you get.', 'WARREN BUFFETT', 1.9);
quoteFrame(  1.28,  5.0,  Math.PI/2, 'It lands here first.', 'JANNA');
quoteFrame(-14.72,  5.0,  Math.PI/2, 'Put it in writing.', 'B. WRENCH');
quoteFrame(  9.28,  5.4,  Math.PI/2, 'Make it loud. Keep it fair.', 'LOUDFAIR');
quoteFrame(-15.28, -3.0, -Math.PI/2, 'YES I APPROVE TO SEND', 'BEN', 1.9);

/* ---------- in-character dressing ---------- */
{ /* Obama — the resident gets his oval: navy oval rug, two facing sofas, oval table */
  const rug = new THREE.Mesh(new THREE.CircleGeometry(1,48), M(0x1e2436,.95));
  rug.rotation.x = -Math.PI/2; rug.scale.set(1.62,1.18,1); rug.position.set(-13,0.012,-8.6);
  rug.receiveShadow = true; world.add(rug);
  const trim = new THREE.Mesh(new THREE.RingGeometry(0.94,1,48), M(0x8a7a5c,.85));
  trim.rotation.x = -Math.PI/2; trim.scale.set(1.62,1.18,1); trim.position.set(-13,0.014,-8.6);
  world.add(trim);
  couch(-14.02,-8.6, Math.PI/2); couch(-11.98,-8.6, -Math.PI/2);
  const ot = new THREE.Mesh(new THREE.CylinderGeometry(0.5,0.44,0.4,28), WOODD);
  ot.scale.set(1.25,1,0.62); ot.position.set(-13,0.2,-8.6); ot.castShadow = true; world.add(ot);
  B(0.3,0.02,0.2, PAPER, -13.1,0.415,-8.55, 0.25);                          // briefing papers on the oval table
}
{ /* Jobs — the museum shelf: Macintosh 128K, a row of iPods, folded turtlenecks */
  B(2.3,0.55,0.42, WOODD, 17.9,0.275,-6.6, 0, world);                       // credenza along the east wall
  const mac = new THREE.Group(); mac.position.set(17.9,0.55,-7.25); mac.rotation.y = -Math.PI/2 - 0.12; world.add(mac);
  const BEIGE = M(0xcdc4ae,.7,.05);
  B(0.25,0.33,0.26, BEIGE, 0,0.165,0, 0, mac);
  B(0.19,0.15,0.012, M(0x2a3038,.35,.2), 0,0.21,0.132, 0, mac);             // the nine-inch screen
  B(0.09,0.008,0.012, M(0x8a8578,.6), 0.05,0.10,0.132, 0, mac);             // floppy slot
  B(0.24,0.035,0.2, BEIGE, 0,-0.018,0.05, 0, mac);                          // keyboard
  const pods = new THREE.Group(); pods.position.set(17.88,0.555,-6.42); pods.rotation.y = -Math.PI/2; world.add(pods);
  [[-0.16],[0],[0.16]].forEach((p,i)=>{
    const ip = new THREE.Group(); ip.position.set(p[0],0,0); ip.rotation.x = -0.22; pods.add(ip);
    B(0.068,0.115,0.011, M(0xe8e6e0,.35,.1), 0,0.057,0, 0, ip);
    B(0.05,0.038,0.013, M(0x22262c,.3,.2), 0,0.082,0.0008, 0, ip);          // screen
    const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.021,0.021,0.003,20), M(0xc9c7c2,.4));
    wheel.rotation.x = Math.PI/2 - 0.22; wheel.position.set(0,0.035,0.007); ip.add(wheel);
  });
  [[0,0],[0.02,0.075],[-0.015,0.15]].forEach(p=>                             // black turtlenecks, folded
    B(0.34,0.075,0.26, M(0x141416,.94), 17.9+p[0], 0.585+p[1], -5.85, p[0]*2, world));
}
{ /* Wozniak — an Apple II wedge with its little CRT on the bench */
  const a2g = new THREE.Group(); a2g.position.set(3.2,0.75,-11.35); a2g.rotation.y = 0.18; world.add(a2g);
  const BEIGE = M(0xd0c6ad,.7,.05);
  B(0.44,0.075,0.34, BEIGE, 0,0.037,0, 0, a2g);
  const deck = B(0.44,0.02,0.19, M(0x8a7f6c,.7), 0,0.085,0.055, 0, a2g); deck.rotation.x = -0.14;
  B(0.44,0.1,0.13, BEIGE, 0,0.12,-0.1, 0, a2g);                             // back riser
  B(0.34,0.26,0.3, BEIGE, 0,0.3,-0.02, 0, a2g);                             // monitor
  B(0.26,0.19,0.012, M(0x1e2a22,.35,.2), 0,0.31,0.135, 0, a2g);             // green phosphor screen
  B(0.5,0.75,0.44, WOODD, 3.2, 0.375, -11.35, 0.18, world);                  // its bench
}
{ /* Shakespeare — poor Yorick on a side table */
  B(0.4,0.66,0.4, WOODD, -5.5,0.33,-6.1, 0.2, world);
  const skull = new THREE.Group(); skull.position.set(-5.5,0.75,-6.1); skull.rotation.y = -1.15; world.add(skull);
  const bone = M(0xd8cfba,.62,.02);
  const cr = new THREE.Mesh(new THREE.SphereGeometry(0.085,18,14), bone); cr.scale.set(1,0.92,1.05); cr.castShadow = true; skull.add(cr);
  const jaw = new THREE.Mesh(new THREE.SphereGeometry(0.055,14,10), bone); jaw.scale.set(0.86,0.6,0.9); jaw.position.set(0,-0.066,0.03); skull.add(jaw);
  [[-0.033],[0.033]].forEach(p=>{ const eye = new THREE.Mesh(new THREE.SphereGeometry(0.026,10,8), M(0x17151a,.95));
    eye.position.set(p[0],0.008,0.068); eye.scale.z = 0.5; skull.add(eye); });
  const nose = new THREE.Mesh(new THREE.SphereGeometry(0.014,8,6), M(0x17151a,.95));
  nose.position.set(0,-0.028,0.078); nose.scale.set(0.8,1.3,0.5); skull.add(nose);
  const teethLine = new THREE.Mesh(new THREE.BoxGeometry(0.05,0.008,0.012), M(0x35302a,.8));
  teethLine.position.set(0,-0.058,0.074); skull.add(teethLine);
}
{ /* Buffett — the Cherry Coke, never far from the ledgers */
  const can = new THREE.Mesh(new THREE.CylinderGeometry(0.033,0.033,0.115,16), M(0x8c2f2a,.35,.4));
  can.position.set(-2.72,0.845,6.38); can.castShadow = true; world.add(can);
  const lid = new THREE.Mesh(new THREE.CylinderGeometry(0.033,0.033,0.006,16), M(0xb8b4ac,.35,.6));
  lid.position.set(-2.72,0.905,6.38); world.add(lid);
}
{ /* Buffett — hundreds of millions, banded and stacked against the east wall */
  const CASH = M(0x59634a,.9,.02), BAND = M(0xd9d4c4,.9);
  B(3.9,0.1,0.55, M(0x5a4a36,.9), 0.42,0.05,6.4);                          // the pallet
  for(let col=0; col<11; col++) for(let row=0; row<14; row++) for(let dep=0; dep<2; dep++){
    const jx = ((col*7+row*3+dep)%5)*0.004, jz = ((col*3+row)%7)*0.003;
    const bx = B(0.155,0.078,0.335, CASH, 0.62-dep*0.17+jx, 0.14+row*0.08, 4.72+col*0.34+jz, jx*8);
    bx.castShadow = row>10;
    if(dep===1) B(0.008,0.06,0.1, BAND, 0.44+jx, 0.14+row*0.08, 4.72+col*0.34+jz);   // the band, facing the room
  }
  /* The Intelligent Investor — the other wall */
  const c = document.createElement('canvas'); c.width=512; c.height=680;
  const g = c.getContext('2d');
  g.fillStyle='#1c2233'; g.fillRect(0,0,512,680);
  g.strokeStyle='#b59a52'; g.lineWidth=6; g.strokeRect(28,28,456,624);
  g.fillStyle='#d9d2bc'; g.textAlign='center';
  g.font='700 58px Georgia, serif';
  g.fillText('THE',256,180); g.fillText('INTELLIGENT',256,255); g.fillText('INVESTOR',256,330);
  g.fillStyle='#b59a52'; g.fillRect(96,392,320,3);
  g.font='34px Georgia, serif'; g.fillStyle='#b0a88e';
  g.fillText('BENJAMIN GRAHAM',256,470);
  const bt = new THREE.CanvasTexture(c); bt.colorSpace = THREE.SRGBColorSpace;
  const bg2 = new THREE.Group(); bg2.position.set(-6.72,1.75,7.7); bg2.rotation.y = Math.PI/2; world.add(bg2);
  const bfr = B(1.14,1.52,0.05, M(0x3a2f24,.8,.15), 0,0,0, 0, bg2); bfr.castShadow = true;
  const bfc = new THREE.Mesh(new THREE.PlaneGeometry(1.04,1.4),
    new THREE.MeshStandardMaterial({ map:bt, roughness:.6 }));
  bfc.position.z = 0.028; bg2.add(bfc);
}
{ /* Shakespeare — floor-to-ceiling stacks and a set that never stops playing him */
  const SPINE = [0x6e4a3a,0x4f5a4e,0x8a7a5c,0x5a5a46,0x6b5566,0x7a4a34,0x3f4a52,0x8a6f42];
  const bookGeo = new THREE.BoxGeometry(0.036, 0.24, 0.17);
  /* one instanced mesh carries every book on the wall — hundreds of spines, one draw call */
  const runs = [
    { x:-10.55, z0:-12.0, z1:-3.2, ry: 0,        side: 1 },   // west wall of the library
    { x:-3.45,  z0:-12.0, z1:-3.2, ry: 0,        side:-1 },   // east wall
  ];
  let total = 0;
  const shelfY = [0.42, 0.79, 1.16, 1.53, 1.90, 2.27, 2.64, 3.01];
  runs.forEach(r=>{ total += shelfY.length * Math.floor((r.z1-r.z0)/0.042); });
  const books = new THREE.InstancedMesh(bookGeo, M(0xffffff,.92), total);
  books.castShadow = true; books.receiveShadow = true;
  const m4 = new THREE.Matrix4(), q0 = new THREE.Quaternion(), sc = new THREE.Vector3();
  const col = new THREE.Color();
  let i = 0;
  runs.forEach(r=>{
    shelfY.forEach((y, li)=>{
      /* the timber the books sit on */
      B(0.30, 0.028, r.z1-r.z0, WOODD, r.x + r.side*0.16, y-0.125, (r.z0+r.z1)/2, 0, world);
      for(let z = r.z0 + 0.06; z < r.z1 - 0.06; z += 0.042){
        if(i >= total) break;
        const h = 0.84 + ((i*7919)%13)/13 * 0.34;             // spines are never uniform
        const lean = ((i*104729)%17) === 0 ? 0.22 : 0;        // one in seventeen has fallen over
        sc.set(1, h, 0.86 + ((i*31)%7)/7*0.3);
        q0.setFromEuler(new THREE.Euler(0, 0, lean));
        m4.compose(new THREE.Vector3(r.x + r.side*0.16, y + h*0.02, z), q0, sc);
        books.setMatrixAt(i, m4);
        col.setHex(SPINE[(i*13)%SPINE.length]).multiplyScalar(0.78 + ((i*17)%5)/5*0.42);
        books.setColorAt(i, col);
        i++;
      }
    });
  });
  books.count = i;
  books.instanceMatrix.needsUpdate = true;
  if(books.instanceColor) books.instanceColor.needsUpdate = true;
  world.add(books);
  /* the uprights that make it read as joinery rather than floating rows */
  [-12.0, -9.6, -7.2, -4.8, -3.2].forEach(z=>{
    runs.forEach(r=>B(0.30, 3.1, 0.05, WOODD, r.x + r.side*0.16, 1.55, z, 0, world));
  });
  /* the set: a valve-era CRT that never stops playing him */
  crtUnit(-6.0, -11.6, 0);
}
{ /* Shakespeare — drown the room in books */
  [[-8.6,-4.6,0.4],[-6.0,-4.4,1.2],[-4.6,-5.2,2.1],[-4.2,-8.7,0.9],[-5.4,-10.9,1.7],
   [-8.8,-10.9,0.2],[-9.9,-4.3,2.6],[-6.6,-10.5,1.1]].forEach(p=>
    prop('book_encyclopedia_set_01', p[0], p[1], p[2], { w:0.5 }));
  for(let i=0;i<9;i++) B(0.24,0.045,0.34, M([0x6e4a3a,0x4f5a4e,0x8a7a5c,0x5a5a46,0x6b5566][i%5],.9),
    -4.35, 0.023+i*0.047, -7.2, i*0.31);                                   // the teetering tower
  for(let i=0;i<6;i++) B(0.26,0.05,0.36, M([0x4f5a4e,0x6e4a3a,0x8a7a5c][i%3],.9),
    -9.55, 0.025+i*0.052, -8.9, -i*0.26);
  for(let i=0;i<5;i++) B(0.22,0.045,0.3, M([0x8a7a5c,0x5a5a46,0x6b5566][i%3],.9),
    -6.1, 0.023+i*0.047, -9.4, i*0.4);
}

/* ---------- corridor life: bathrooms + security post ---------- */
{ /* WC pod — south side, on the blank wall between the stood-down doors (never in front of the cell) */
  const px = 21, pz = 1.56;
  B(2.2,2.5,0.85, M(0x35333a,.85), px,1.25,pz, 0, world);
  [[-0.52],[0.52]].forEach(p=>{
    B(0.62,1.95,0.04, WOODD, px+p[0], 0.975, pz-0.44, 0, world);            // stall doors face the corridor
    const kn = new THREE.Mesh(new THREE.SphereGeometry(0.022,10,8), M(0x9a917e,.4,.5));
    kn.position.set(px+p[0]-0.22, 1.02, pz-0.47); world.add(kn);
  });
  const wcc = document.createElement('canvas'); wcc.width=256; wcc.height=128;
  const wg = wcc.getContext('2d'); wg.fillStyle='#0f0f12'; wg.fillRect(0,0,256,128);
  wg.fillStyle='#cfc8b4'; wg.font='700 74px Futura, sans-serif'; wg.textAlign='center'; wg.fillText('WC',128,90);
  const wcTex = new THREE.CanvasTexture(wcc); wcTex.colorSpace = THREE.SRGBColorSpace;
  const sign = new THREE.Mesh(new THREE.PlaneGeometry(0.34,0.17), new THREE.MeshStandardMaterial({ map:wcTex, roughness:.6 }));
  sign.position.set(px, 2.18, pz-0.442); sign.rotation.y = Math.PI; world.add(sign);
}
{ /* security, out the front of the pod end */
  B(1.5,1.02,0.5, M(0x2b2b30,.7,.2), 23.9,0.51,0.95, 0, world);             // standing desk
  B(1.5,0.05,0.6, M(0x38383e,.6,.3), 23.9,1.04,0.95, 0, world);
  { const mon = B(0.34,0.22,0.02, M(0x24262c,.35,.3), 23.75,1.22,0.85, -0.3, world);
    const ms = new THREE.Mesh(new THREE.PlaneGeometry(0.3,0.18),
      new THREE.MeshStandardMaterial({ color:0x0c0d10, emissive:0x8a93a8, emissiveIntensity:.5 }));
    ms.position.set(23.74,1.22,0.87); ms.rotation.y = -0.3; world.add(ms);
    SCREENS.push({ m:ms.material, base:.5 }); }
  [[22.9,-0.6],[24.9,-0.6]].forEach(p=>{                                     // rope line
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.025,0.035,0.95,10), M(0x3c3830,.5,.6));
    post.position.set(p[0],0.475,p[1]); post.castShadow = true; world.add(post);
  });
  { const rope = new THREE.Mesh(new THREE.CylinderGeometry(0.014,0.014,2,8), M(0x66332c,.8));
    rope.rotation.z = Math.PI/2; rope.position.set(23.9,0.86,-0.6); world.add(rope); }
}

/* ---------- the roof — 8% glass until you tap it off and dive in ---------- */
let roofOn = true, roofFadeT = -1;
const ROOF = new THREE.Group(); world.add(ROOF);
const roofPlate = new THREE.Mesh(new THREE.BoxGeometry(53.6,0.07,25.6),
  new THREE.MeshStandardMaterial({ color:0xd8dce4, transparent:true, opacity:0.08, roughness:.25, metalness:.1, depthWrite:false }));
roofPlate.position.set(3.5,3.42,0); ROOF.add(roofPlate);
for(let i=0;i<7;i++){
  const beam = new THREE.Mesh(new THREE.BoxGeometry(0.12,0.16,25.6),
    new THREE.MeshStandardMaterial({ color:0x8a8578, transparent:true, opacity:0.16, depthWrite:false }));
  beam.position.set(-21+i*7.4, 3.34, 0); ROOF.add(beam);
}
const roofSpine = new THREE.Mesh(new THREE.BoxGeometry(53.6,0.16,0.14),
  new THREE.MeshStandardMaterial({ color:0x8a8578, transparent:true, opacity:0.16, depthWrite:false }));
roofSpine.position.set(3.5,3.34,0); ROOF.add(roofSpine);
function roofDive(){
  if(!roofOn) return;
  roofOn = false; roofFadeT = 0;
  say('roof off, going in', new THREE.Vector3(3.5,3.6,0), 2400);
  controls.enabled = false;
  tween = { p0:camera.position.clone(), t0:controls.target.clone(),
            p1:new THREE.Vector3(-11,1.6,0), t1:new THREE.Vector3(8,1.1,0), t:0, dur:1.8 };
}
function roofRestore(){
  roofOn = true; roofFadeT = -1; ROOF.visible = true;
  ROOF.traverse(o=>{ if(o.material) o.material.opacity = o===roofPlate ? 0.08 : 0.16; });
}

/* static (non-actor) figure */
function figure(o){
  const r = makeRig(o.kind || (o.ben?'ben':'bot'), o.scale||1, o.head, o.opts||{});
  r.setPose(o.seated===false?'stand':'sit');
  r.root.position.set(o.x,0,o.z); r.root.rotation.y = o.ry||0;
  r.style = o.style;
  if(o.voice) addVoiceTarget(r, o.voice);
  return r;
}

/* ---------- actors ---------- */
const ACTORS = {};
function actor(id, kind, homeX, homeZ, homeYaw, pose, head, opts){
  const r = makeRig(kind, kind==='ben'?1.06:(kind==='atg'?1.12:1), head, opts||{});
  r.home = { x:homeX, z:homeZ, yaw:homeYaw, pose };
  r.root.position.set(homeX,0,homeZ); r.root.rotation.y = homeYaw;
  r.setPose(pose); r.walkT = 0; r.anim = null; r.lean = 0; r.id = id;
  if(VOICES[id]) addVoiceTarget(r, id);   // the cleaners are staff, not seats — nothing to click
  ACTORS[id] = r; return r;
}
actor('ben',   'ben', -19.9, 0,    Math.PI/2, 'sit');               // faces his door
actor('atg',   'atg', -13.9, 0.85, -Math.PI/2, 'stand');            // Alexander, at his post by the door
actor('wrench','suit', -12.6, 6.7, Math.PI, 'sit', 'slick');        // in a suit, at the west counsel table
actor('shakespeare','bot', -7, -6.7, Math.PI, 'sit', 'bard', {cloth:0x4a3626});
actor('janna', 'bot', -12.55, 1.35, -Math.PI/2, 'sit', 'bun', {cloth:0x5a4a52}).style='type';   // the EA, right outside Ben's door
actor('jobs',  'turtle', 14.3, -6.9, Math.PI+0.5, 'stand', 'jobs', {shoes:0xb8b2a6}); // turtleneck, jeans, sneakers
actor('ive',   'bot', 8.5, -5.9, Math.PI, 'stand', 'ive', {cloth:0x6b6e72, legs:0x2e3033, tee:true});
actor('efficacy','bot', -13, -10.9, 0, 'sit', 'headset').style='type';     // at the head of his oval office
actor('pete', 'bot', 19.55, 6.75, -2.5, 'stand', 'pete').style='paint';    // at the easel, mid-stroke

/* ---------- room furnishing ---------- */
/* Ben — locked office: an actual CEO suite */
{
  B(4.2,0.025,3.2, M(0x262019,.98), -19, 0.045, 0.2, 0, world, false);   // rug
  desk(1.3, 2.2, -19, 0);                                                // executive desk, faces the door
  B(1.24,0.4,2.1, M(0x2f2921,.85), -19.28, 0.5, 0, 0, world);            // modesty panel
  taskChair(-19.9, 0, Math.PI/2, true);
  papers(-19, -0.45, 3); stack(-18.9, 0.6, 2); mug(-18.85,-0.15);
  { const lt=B(0.32,0.015,0.24, M(0x2e2e33,.4,.3), -19.05,0.79,0.42, 0.25);       // laptop
    const ls=B(0.32,0.22,0.012, M(0x2e2e33,.4,.3), -19.16,0.895,0.46, 0.25); ls.rotation.x=-1.2; }
  const lampArm = new THREE.Group(); lampArm.position.set(-19.1,0.78,-0.85); world.add(lampArm);
  Cyl(0.03,0.34, BOT_J, 0,0.17,0, lampArm);
  const bulb = new THREE.Mesh(new THREE.SphereGeometry(0.07,12,10), new THREE.MeshStandardMaterial({color:0xffd9a0, emissive:0xffc37a, emissiveIntensity:3.0}));
  bulb.position.set(0,0.36,0.06); lampArm.add(bulb);
  const sh = shelf(-22.55, 3.6, Math.PI/2, 2.4); books(sh, 2.4);
  couch(-21.3, 2.7, Math.PI/2); B(0.9,0.34,0.6, WOODL, -20.1, 0.17, 2.7); // visitor corner
  Cyl(0.05,0.12, M(0x8a6f42,.3,.7), -20.1,0.42,2.55, world, 10);          // decanter
  Cyl(0.025,0.06, M(0xd6d0c2,.4,.3), -19.9,0.39,2.8, world, 8); Cyl(0.025,0.06, M(0xd6d0c2,.4,.3), -20.3,0.39,2.85, world, 8);
  plant(-22.3,-4.6,1.15); bin(-18.5,-1.15);
  { const fp=B(0.6,0.75,0.04, M(0x2b241c,.8), -22.5,0.55,-1.6, 0.12); fp.rotation.z=0.08;   // leaning framed print
    B(0.5,0.65,0.012, M(0x8a7a5c,.9), -22.47,0.55,-1.58, 0.12); }
  { const fl = new THREE.Group(); fl.position.set(-21.2,0,-4.2); world.add(fl);            // floor lamp
    Cyl(0.02,1.5, M(0x2b2b30,.6), 0,0.75,0, fl, 8); Cyl(0.16,0.2, M(0x3a332a,.8), 0,1.58,0, fl, 12);
    const li=new THREE.Mesh(new THREE.CylinderGeometry(0.13,0.13,0.02,10), new THREE.MeshStandardMaterial({color:0xffd9a0, emissive:0xffc37a, emissiveIntensity:2.4})); li.position.set(0,1.5,0); fl.add(li); }
  chair(-16.5, 1.3, -Math.PI/2);                                          // guest chair by the door
  B(0.7,0.5,0.44, WOODD, -16.1, 0.25, -1.3);                              // drop table for messages
  B(0.34,0.05,0.24, PAPER, -16.1, 0.53, -1.3, 0.2);
  [[-3.1],[1.9]].forEach(p=>{                                             // west windows, late sun
    const wm = new THREE.Mesh(new THREE.BoxGeometry(0.06,1.3,2.6), new THREE.MeshStandardMaterial({color:0xffe1b0, emissive:0xffcf94, emissiveIntensity:0.30}));   // the real city is out there now — stop faking daylight
    wm.position.set(-22.78, 1.7, p[0]); world.add(wm);
  });
}
/* ATG — the door plus a small desk just outside it */
{
  desk(0.8, 0.5, -13.6, -1.42); B(0.3,0.06,0.2, PAPER, -13.6, 0.81, -1.42, 0.3);
  B(0.32,0.16,0.22, WOODL, -13.35, 0.86, -1.42);                          // out-tray
  [[-0.12],[0],[0.12]].forEach(p=>Cyl(0.022,0.24, M(0xcfc8b6,.9), -13.82+p[0],0.87,-1.35, world, 8));  // scroll tubes
  Cyl(0.09,0.3, M(0x6e5a3c,.85), -13.15,0.15,-1.75, world, 10);           // amphora
  Cyl(0.05,0.12, M(0x6e5a3c,.85), -13.15,0.36,-1.75, world, 8);
  Cyl(0.025,1.95, M(0x4a3c2c,.8), -14.5,0.975,-1.72);                     // his standard, planted by the door
  var pennant = new THREE.Group(); pennant.position.set(-14.5,1.76,-1.72); world.add(pennant);
  B(0.36,0.22,0.015, M(0x66332c,.9), 0.2,0,0, 0, pennant);
  { const ph = new THREE.Group(); ph.position.set(-14.82,0,1.9); ph.rotation.y=Math.PI/2; world.add(ph);  // message pigeonholes on the wall
    B(0.72,0.72,0.05, WOODD, 0,1.05,-0.09, 0, ph);
    [0.72,0.96,1.2,1.44].forEach(y=>B(0.72,0.03,0.16, WOODD, 0,y,0,0,ph));
    [-0.24,0,0.24].forEach(px=>B(0.03,0.72,0.16, WOODD, px,1.08,0,0,ph));
    B(0.18,0.02,0.13, PAPER, -0.1,0.99,0.02, 0.2, ph); B(0.18,0.02,0.13, PAPER, 0.14,1.23,0.02, -0.15, ph); }
}
/* Janna — the EA station, right outside Ben's door */
{
  desk(1.05,0.62,-13.2,1.42,Math.PI/2);
  B(0.28,0.013,0.2, M(0x2e2e33,.4,.3), -13.15,0.815,1.2, 0.1);             // her laptop
  { const ls=B(0.28,0.19,0.01, M(0x2e2e33,.4,.3), -13.24,0.9,1.16, 0.1); ls.rotation.x=-1.15; }
  papers(-13.18,1.68,2); mug(-13.05,1.05);
  B(0.2,0.05,0.14, M(0x1c1c1f,.5,.3), -13.3,0.84,1.75, -0.25);             // the phone that rings first
  B(0.24,0.03,0.06, WOODL, -12.98,0.83,1.44);                              // name block
  plant(-14.55,0.35,0.8);
}
/* the door guard — two soldiers who never move */
figure({ kind:'guard', seated:false, x:-14.5, z:-1.15, ry:-Math.PI/2 });   // Soldier rests facing -z
figure({ kind:'guard', seated:false, x:-14.5, z: 1.32, ry:-Math.PI/2 });
/* Assange — standing at the cell bars, hands out through them */
{
  [[21.68],[22.32]].forEach(p=>B(0.07,2.05,0.09, M(0x565a63,.4,.6), p[0],1.02,-2, 0, world));   // the cell door frame
  B(0.71,0.07,0.09, M(0x565a63,.4,.6), 22,1.55,-2);
  B(0.71,0.07,0.09, M(0x565a63,.4,.6), 22,0.45,-2);
  B(0.09,0.16,0.06, M(0x3c3f46,.45,.6), 22.3,1.0,-1.97);                    // the lock that stays locked
  figure({ kind:'staff', seated:false, x:22, z:-2.52, ry:0, style:'handsout' });
}
/* the mascot — three.js's own robot, in the agency lab (no Disney: none are open source) */
{
  B(1.0,0.14,1.0, M(0x2d2d33,.6), 15.9,0.07,10.6, 0.3, world);
  figure({ kind:'mascot', seated:false, x:15.9, z:10.6, ry:-2.35, scale:0.42 });
}
/* Pete Gay — the studio: art on every wall, an easel mid-room, paint everywhere */
function artTex(seed){
  const c = document.createElement('canvas'); c.width = 256; c.height = 300;
  const g = c.getContext('2d');
  let s = seed; const rnd = ()=>{ s = (s*16807)%2147483647; return (s-1)/2147483646; };
  const PALS = [
    ['#141416',['#c2543a','#d9c26a','#cfc8b4','#4a6a5e']],
    ['#1a1c22',['#8291c9','#d9d4c4','#b5543a','#6a5a8e']],
    ['#20140f',['#d98a4a','#cfc8b4','#8a2f2a','#3a5a4e']],
    ['#15181a',['#d9b36a','#6a7a8e','#c2c8b4','#8a3a52']],
    ['#101014',['#cfc8b4','#5a6a8e','#c2543a','#7a8a5e']],
  ];
  const [bg, cols] = PALS[Math.floor(rnd()*PALS.length)];
  g.fillStyle = bg; g.fillRect(0,0,256,300);
  const n = 6 + Math.floor(rnd()*8);
  for(let i=0;i<n;i++){
    g.strokeStyle = g.fillStyle = cols[Math.floor(rnd()*cols.length)];
    g.globalAlpha = 0.55 + rnd()*0.45;
    const k = rnd();
    if(k < 0.34){                                            // fat gestural stroke
      g.lineWidth = 6 + rnd()*22; g.beginPath();
      g.moveTo(rnd()*256, rnd()*300);
      g.bezierCurveTo(rnd()*256,rnd()*300, rnd()*256,rnd()*300, rnd()*256,rnd()*300);
      g.stroke();
    } else if(k < 0.62){                                     // block
      g.fillRect(rnd()*200, rnd()*250, 20+rnd()*90, 16+rnd()*80);
    } else if(k < 0.85){                                     // circle
      g.beginPath(); g.arc(rnd()*256, rnd()*300, 8+rnd()*46, 0, 7);
      rnd() < 0.5 ? g.fill() : (g.lineWidth = 3+rnd()*8, g.stroke());
    } else {                                                 // drip
      const dx = rnd()*256, dy = rnd()*160;
      g.fillRect(dx, dy, 3+rnd()*4, 40+rnd()*120);
      g.beginPath(); g.arc(dx+3, dy, 6+rnd()*6, 0, 7); g.fill();
    }
  }
  g.globalAlpha = 1;
  const tex = new THREE.CanvasTexture(c); tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
function artFrame(x, z, ry, seed, w=0.85, lean=0){
  const h = w*1.17;
  const g = new THREE.Group(); g.position.set(x, lean ? h/2+0.02 : 1.7, z); g.rotation.y = ry;
  if(lean) g.rotation.x = -0.13;
  world.add(g);
  const F = [0x3a2f24, 0x17171a, 0xcfc8b4, 0x4a3c2c][seed%4];
  const fr = B(w+0.07, h+0.07, 0.04, M(F,.75,.1), 0,0,0, 0, g); fr.castShadow = true;
  const face = new THREE.Mesh(new THREE.PlaneGeometry(w, h),
    new THREE.MeshStandardMaterial({ map:artTex(seed*97+13), roughness:.7 }));
  face.position.z = 0.022; g.add(face);
  return g;
}
{
  /* every wall gets art */
  artFrame(20.72, 4.4, -Math.PI/2, 3, 0.95);
  artFrame(20.72, 6.6, -Math.PI/2, 8, 0.6);
  artFrame(20.72, 8.9, -Math.PI/2, 11, 1.1);
  artFrame(17.28, 5.6,  Math.PI/2, 5, 0.8);
  artFrame(17.28, 8.6,  Math.PI/2, 21, 0.55);
  artFrame(18.0, 12.22, Math.PI, 14, 0.9);
  artFrame(20.1, 12.22, Math.PI, 27, 0.7);
  artFrame(17.42, 11.2, Math.PI/2, 33, 0.9, 1);              // leaning, not yet hung
  artFrame(20.6, 2.9, -Math.PI/2-0.12, 40, 0.75, 1);
  quoteFrame(17.28, 3.4, Math.PI/2, 'Make it weirder.', 'PETE GAY', 1.3);
  /* the easel and the mess */
  { const eg = new THREE.Group(); eg.position.set(19.15,0,7.0); eg.rotation.y = -2.5; world.add(eg);
    Cyl(0.025,1.55, WOODD, -0.28,0.775,0.12, eg, 8); Cyl(0.025,1.55, WOODD, 0.28,0.775,0.12, eg, 8);
    const bk = Cyl(0.025,1.5, WOODD, 0,0.75,-0.18, eg, 8); bk.rotation.x = 0.3;
    B(0.62,0.05,0.05, WOODD, 0,0.62,0.14, 0, eg);
    const wip = new THREE.Mesh(new THREE.PlaneGeometry(0.72,0.85),
      new THREE.MeshStandardMaterial({ map:artTex(777), roughness:.8 }));
    wip.position.set(0,1.06,0.11); wip.rotation.x = -0.08; eg.add(wip); }
  [[18.4,6.2,0x8a2f2a],[18.6,6.45,0xd9c26a],[18.35,6.6,0x4a6a5e]].forEach(p=>{   // paint tins
    Cyl(0.075,0.16, M(0x8a8578,.5,.4), p[0],0.08,p[1], world, 12);
    Cyl(0.06,0.015, M(p[2],.6), p[0],0.17,p[1], world, 12); });
  [[19.6,6.5],[19.9,6.3]].forEach((p,i)=>Cyl(0.032,0.15, M(i?0x8291c9:0xc2543a,.4,.5), p[0],0.075,p[1], world, 10));  // rattle cans
  [[18.9,7.6,0x8a2f2a,0.22],[19.5,7.9,0xd9c26a,0.16],[18.3,7.2,0x4a6a5e,0.28],[19.8,6.9,0xcfc8b4,0.13]].forEach(p=>{
    const sp = new THREE.Mesh(new THREE.CircleGeometry(p[3],14), new THREE.MeshStandardMaterial({ color:p[2], roughness:.95, transparent:true, opacity:.45, polygonOffset:true, polygonOffsetFactor:-1 }));
    sp.rotation.x = -Math.PI/2; sp.rotation.z = p[0]*7; sp.position.set(p[0],0.012,p[1]); world.add(sp); });   // floor splatter
  desk(1.4,0.7,19.9,10.9); papers(19.9,10.85,4); mug(19.5,10.7,0x2c3540);
  couch(17.9,9.9, Math.PI/2+0.5, 0.95);                                    // where ideas nap
  plant(20.5,11.9,1.0);
  pendant(19,8.4,2.6); pendant(18.2,5,2.6);
}
/* Efficacy — the resident gets the full oval office: the desk at the head, colours, the sitting set */
{
  desk(1.7,0.85,-13,-10.25);                                               // the resolute-weight desk
  B(1.64,0.5,0.1, M(0x3a2c1e,.8,.1), -13,0.52,-9.86);                      // carved front panel
  B(1.7,0.045,0.9, M(0x2b2013,.55,.15), -13,0.845,-10.25);                 // darker top slab
  taskChair(-13,-10.95, 0, true);
  papers(-13.25,-10.2,2); B(0.3,0.02,0.2, PAPER, -12.7,0.87,-10.3, -0.2);
  { const ph=B(0.2,0.05,0.14, M(0x1c1c1f,.5,.3), -12.55,0.895,-10.05, 0.3); // the desk phone
    B(0.16,0.035,0.045, M(0x1c1c1f,.4,.4), -12.55,0.94,-10.1, 0.3); }
  [[-14.15],[-11.85]].forEach(p=>{                                          // twin standards behind the desk
    Cyl(0.022,2.05, M(0x4a3c2c,.7), p[0],1.025,-11.9, world, 10);
    const fin = new THREE.Mesh(new THREE.SphereGeometry(0.045,12,10), M(0xb59a52,.4,.7));
    fin.position.set(p[0],2.1,-11.9); world.add(fin);
    const cloth=B(0.4,1.05,0.03, M(0x1e2436,.9), p[0],1.42,-11.78, 0.06); cloth.rotation.x=0.05;
    B(0.4,0.06,0.035, M(0xb59a52,.55,.5), p[0],0.87,-11.75, 0.06);          // gold fringe
  });
  [[-1.35,0.5],[0,0],[1.35,-0.5]].forEach(p=>{                              // the curved cream wall, suggested
    const panel=B(1.32,2.4,0.06, M(0xd8d2c2,.85), -13+p[0],1.2,-12.1+Math.abs(p[0])*0.12, p[1]*0.28);
    panel.receiveShadow = true; });
  plant(-11.6,-11.7,0.95); plant(-14.45,-11.6,0.9);
  bin(-12.15,-9.6);
}
/* Shakespeare — library / copy room: candle, quill, crumpled drafts */
{
  const s1 = shelf(-9.1,-12.12,0,3.0); books(s1,3.0);
  const s2 = shelf(-5.2,-12.12,0,3.0); books(s2,3.0);
  /* the side walls are now floor-to-ceiling stacks — a second shelf 3cm away only z-fights */
  desk(2.9,1.5,-7,-7.6);
  chair(-7,-6.7,Math.PI);
  papers(-7,-7.6,8); stack(-8.1,-7.4,4); stack(-5.9,-7.9,3);
  B(0.36,0.03,0.5, PAPER, -6.5,0.795,-7.3, 0.35);                          // open draft
  candle(-6.2,0.78,-8.1); candle(-8.0,0.78,-8.2,0.024);
  Cyl(0.035,0.07, M(0x2b2b30,.5,.3), -7.5,0.815,-7.15, world, 8);          // inkpot
  { const q=B(0.012,0.3,0.03, M(0xd6d0c2,.9), -7.46,0.95,-7.12); q.rotation.z=0.5; q.rotation.x=0.2; }
  bin(-5.6,-6.3); crumples(-5.6,-6.3,5);
  couch(-9.7,-6.0, Math.PI/2+0.3, 1.1);                                    // reading chair
  B(0.5,0.4,0.5, WOODL, -9.0,0.2,-5.3); B(0.28,0.06,0.2, M(0x6b5566,.9), -9.0,0.46,-5.3, 0.4);  // side table + open book
}
/* Wozniak — a real workshop: pegboard, scope, open machines, parts bins */
{
  desk(3.4,0.85,-1.1,-11.4); desk(3.4,0.85,2.6,-11.4); desk(0.85,3.0,4.45,-8.6);
  monitor(-1.9,-11.35); monitor(2.0,-11.35); monitor(4.42,-9.3,-Math.PI/2);
  keyboard(-1.9,-11.05); keyboard(2.0,-11.05); mug(1.4,-11.15,0x4f5a4e);
  [[-1.4,-11.2],[3.0,-11.15],[4.3,-8.0]].forEach(p=>B(0.22,0.5,0.44, M(0x808a80,.6,.2), p[0],0.25,p[1]));
  chair(0.3,-10.55,Math.PI); figure({x:0.3,z:-10.55,ry:Math.PI,style:'type',head:'woz',voice:'wozniak',scale:1.04,opts:{cloth:0x4a5a6e,wide:true}});
  taskChair(3.4,-10.55,Math.PI);
  { const pb=B(2.6,1.0,0.05, M(0x3f3a33,.9), 1.0,1.5,-12.08);                        // pegboard of tools
    [[-1.0,0.2,0x8a7a5c],[-0.6,0.3,0x63635e],[-0.2,0.15,0x6e4a3a],[0.2,0.28,0x63635e],[0.6,0.2,0x4f5a4e],[1.0,0.32,0x8a7a5c],[-0.8,-0.2,0x63635e],[0.4,-0.18,0x6e4a3a],[0.9,-0.22,0x63635e]]
      .forEach(p=>B(0.06,p[1],0.04, M(p[2],.6,.3), 1.0+p[0],1.5+(p[1]<0?p[1]:0)+0.05,-12.03)); }
  { const os = new THREE.Group(); os.position.set(3.4,0.775,-11.45); world.add(os);  // oscilloscope
    B(0.4,0.3,0.3, M(0x6b6b60,.6,.2), 0,0.15,0, 0, os);
    const sc=B(0.2,0.16,0.012, new THREE.MeshStandardMaterial({color:0x1a2016, emissive:0x8a9a5e, emissiveIntensity:.5, roughness:.4}), -0.06,0.17,0.157, 0, os, false);
    SCREENS.push({m:sc.material, base:.5});
    Cyl(0.02,0.02, M(0x2b2b30,.5), 0.12,0.12,0.16, os, 8); Cyl(0.02,0.02, M(0x2b2b30,.5), 0.12,0.2,0.16, os, 8); }
  { B(0.5,0.44,0.4, M(0x8a8578,.6,.3), 0.6,0.99,-11.5);                              // open beige tower, PCB showing
    B(0.36,0.3,0.02, M(0x3f5a3a,.7), 0.6,0.98,-11.28);
    [[-0.08,0],[0.04,0.06],[0.1,-0.05]].forEach(p=>{ const d=B(0.03,0.03,0.012, new THREE.MeshStandardMaterial({color:0x222,emissive:0xd9a05e,emissiveIntensity:1.5}), 0.6+p[0],0.98+p[1],-11.265,0,world,false); BLINKS.push({m:d.material, ph:Math.random()*7}); }); }
  Cyl(0.14,0.1, M(0x4a4034,.8), -2.3,0.83,-11.6, world, 12); Cyl(0.1,0.14, M(0x4a4034,.8), -2.3,0.07,-10.9, world, 12);  // cable spools
  const s = shelf(-2.6,-9.2,Math.PI/2,2.2);
  [[-0.6,0.36],[0.1,0.36],[-0.3,0.94],[0.5,0.94]].forEach(p=>B(0.3,0.18,0.24, M(0x6b6b60,.7), p[0],p[1]+0.09,0,0,s));
  [[0],[1],[2],[3]].forEach(p=>B(0.2,0.14,0.28, M(0x50505a,.8), 4.35,0.85,-7.3+p[0]*0.34));   // parts bins on the side bench
  { B(0.3,0.34,0.28, M(0xb9b2a2,.8), 3.9,0.94,-11.32);                               // the old beige Mac
    B(0.2,0.16,0.012, M(0x2e2e33,.5), 3.9,0.99,-11.175); }
  papers(1.5,-10.9,3); pendant(-0.5,-10.3,2.3); pendant(2.9,-10.3,2.3);
}
/* Ive — the gallery: one object, one light */
{
  Cyl(0.4,1.05, M(0x2d2d33,.6), 8.5,0.525,-7.6, world, 26);
  Cyl(0.46,0.05, M(0x3a3a41,.5), 8.5,1.075,-7.6, world, 26);
  const phone = new THREE.Group(); phone.position.set(8.5,1.1,-7.6); phone.rotation.y=0.5; world.add(phone);
  B(0.02,0.09,0.06, BOT_J, 0,0.03,0.02, -0.16, phone);
  const pb = B(0.185,0.36,0.026, M(0x3c3c40,.35,.75), 0,0.21,0, 0, phone); pb.rotation.x=-0.16;
  const scr = B(0.15,0.24,0.004, new THREE.MeshStandardMaterial({color:0x11130f, emissive:0xd9d2c2, emissiveIntensity:.4, roughness:.3}), 0,0.223,0.016,0,phone,false); scr.rotation.x=-0.16;
  { const cone = new THREE.Mesh(new THREE.CylinderGeometry(0.12,0.75,1.6,20,1,true),            // the light cone
      new THREE.MeshBasicMaterial({color:0xffe2b0, transparent:true, opacity:0.05, blending:THREE.AdditiveBlending, depthWrite:false, side:THREE.DoubleSide}));
    cone.position.set(8.5,1.9,-7.6); world.add(cone);
    Cyl(0.09,0.1, M(0x2b2b30,.6,.3), 8.5,2.72,-7.6, world, 10); }
  B(0.9,1.15,0.05, M(0x3a3a41,.7), 8.5,1.55,-12.06);                                             // one framed blank canvas
  B(0.78,1.02,0.012, M(0xc9c2b2,.95), 8.5,1.55,-12.02);
  B(1.5,0.32,0.5, M(0x2f2f35,.7), 10.7,0.16,-10.2, 0.35);                                        // a single bench
}
/* Jobs — sparse product room, one secret under a cloth */
{
  desk(1.9,0.95,15.5,-8.2); chair(15.5,-7.35,Math.PI);
  B(0.3,0.2,0.2, M(0x83837a,.7), 15.1,0.87,-8.25); Cyl(0.09,0.24, M(0x9a958d,.6), 15.7,0.89,-8.1);
  { const cl=B(0.44,0.34,0.36, new THREE.MeshStandardMaterial({color:0xb9b2a2, roughness:1}), 16.1,0.96,-8.3, 0.3); cl.scale.y=1.1; }  // the prototype, draped
  const wb = B(1.7,1.05,0.05, M(0xd2ccbd,.9), 15.5,0.72,-12.18); wb.rotation.x = 0.1;
  B(1.78,0.06,0.1, WOODD, 15.5,0.16,-12.14);
  B(0.5,0.03,0.08, WOODD, 15.0,0.28,-12.05); B(0.08,0.025,0.03, M(0x66332c,.7), 14.9,0.31,-12.05); B(0.08,0.025,0.03, M(0x2b2b30,.7), 15.1,0.31,-12.05);  // marker tray
  { Cyl(0.03,1.1, M(0x2b2b30,.7), 18.35,0.55,-11.6, world, 8); B(0.06,0.06,0.06, M(0x2b2b30,.7), 18.35,1.12,-11.6);   // coat stand —
    B(0.34,0.5,0.06, M(0x1e1e21,1), 18.35,0.86,-11.54, 0.1); }                                                        // — the black turtleneck
  Cyl(0.42,0.14, M(0x2a2a2e,.95), 13.4,0.07,-10.2, world, 14);                                    // zen cushion
  plant(18.2,-2.9,0.9);
}
/* Assange — jail: cot, tally marks, one bulb, a rack that never sleeps */
{
  B(1.9,0.3,0.8, M(0x3a3a40,.9), 23.7,0.15,-10.6); B(1.86,0.09,0.74, M(0x4a463f,.95), 23.7,0.34,-10.6);
  B(1.2,0.06,0.6, M(0x6a655c,.95), 23.5,0.4,-10.6, 0.05);                  // blanket
  B(0.4,0.09,0.3, M(0xb9b2a2,.95), 24.3,0.42,-10.55, -0.1);                // pillow
  desk(1.1,0.7,21.4,-9.4); chair(21.4,-8.6,Math.PI);                       // empty chair
  papers(21.4,-9.4,2); bin(22.2,-8.9);
  { const cb=B(0.4,0.012,0.4, M(0x8a8578,.9), 21.4,0.79,-9.5);             // chessboard, played alone
    Cyl(0.02,0.06, M(0xd6d0c2,.8), 21.3,0.83,-9.55, world, 6); Cyl(0.02,0.06, M(0x2b2b30,.8), 21.55,0.83,-9.4, world, 6); }
  for(let i=0;i<7;i++) B(0.012,0.14,0.012, M(0x9d9682,.9), 24.83,0.75,-10.9+i*0.09);  // tally marks on the wall
  { Cyl(0.008,0.9, M(0x2b2b30,.7), 22,2.15,-9, world, 6);                  // the one hanging bulb
    const bl = new THREE.Mesh(new THREE.SphereGeometry(0.05,10,8), new THREE.MeshStandardMaterial({color:0xffe2a8, emissive:0xd9b06a, emissiveIntensity:1.1}));
    bl.position.set(22,1.68,-9); world.add(bl); CANDLES.push({m:bl.material, base:1.1, ph:2.2}); }
  { B(0.6,1.3,0.5, M(0x232327,.6,.2), 24.4,0.65,-3.1);                     // the rack, still blinking
    for(let i=0;i<6;i++){ const d=B(0.04,0.04,0.012, new THREE.MeshStandardMaterial({color:0x222222, emissive:0xd9a05e, emissiveIntensity:1.4}), 24.25+(i%2)*0.16, 0.4+Math.floor(i/2)*0.28, -2.84, 0, world, false); BLINKS.push({m:d.material, ph:i*1.7}); } }
}
/* Grants pod — the shared table, FY25 parked in its box */
{
  Cyl(1.55,0.07, WOODL, 27.5,0.75,0, world, 30); Cyl(0.12,0.72, WOODD, 27.5,0.36,0, world, 12); Cyl(0.5,0.06, WOODD, 27.5,0.03,0, world, 20);
  for(let i=0;i<6;i++){ const a=i/6*Math.PI*2+0.26; chair(27.5+Math.cos(a)*2.15, Math.sin(a)*2.15, -a+Math.PI/2); }
  [[27.0,0.4,0x6e4a3a],[27.9,-0.4,0x4f5a4e],[27.4,-0.7,0x8a7a5c],[28.0,0.55,0x5a5a46]].forEach(p=>B(0.32,0.045,0.24, M(p[2],.9), p[0],0.81,p[1],0.4));
  stack(26.7,0.75,3);
  B(0.34,0.06,0.26, M(0x66332c,.9), 27.5,0.815,0.05, 0.2);                 // the FY25 folder, ribbon-tied, centre of the table
  { const ch=B(1.5,0.9,0.05, M(0x2b241c,.8), 26.1,1.15,-3.6, 0.5);         // wall chart on an easel
    B(1.34,0.74,0.012, M(0x8f887a,.9), 26.12,1.15,-3.57, 0.5);
    [[-0.4,0.22,0.5],[-0.1,0.12,0.7],[0.2,0.02,0.4],[0.4,-0.12,0.6]].forEach(p=>B(p[2],0.05,0.014, M(0x6e6152,.9), 26.14+p[0],1.15+p[1],-3.54, 0.5));
    Cyl(0.025,1.3, WOODD, 25.7,0.65,-3.9, world, 8); Cyl(0.025,1.3, WOODD, 26.6,0.65,-3.2, world, 8); }
  B(0.52,0.34,0.4, M(0x6e6152,.95), 26.6,0.17,1.6, 0.2); B(0.52,0.34,0.4, M(0x6e6152,.95), 28.4,0.17,-1.5, -0.3);   // doc boxes
  plant(28.9,3.4,1.1); plant(28.9,-3.6,0.9);
}
/* Wrench — a real courtroom: bench, bar, jury, witness box, scales */
{
  B(2.7,0.42,1.25, WOODD, -11,0.21,3.35);                                  // dais
  B(2.7,0.95,0.16, M(0x332b21,.85), -11,0.89,3.95);                        // bench front
  B(2.9,0.09,0.6, WOODL, -11,1.4,3.8);                                     // bench top
  chair(-11,3.1,Math.PI,M(0x241f19,.9));
  B(0.16,0.05,0.05, WOODL, -10.5,1.47,3.75); Cyl(0.028,0.12, WOODL, -10.36,1.5,3.7, world, 8);   // gavel + block
  { const sc = new THREE.Group(); sc.position.set(-11.7,1.44,3.75); world.add(sc);               // scales of justice
    Cyl(0.012,0.3, M(0x8a6f42,.4,.6), 0,0.15,0, sc, 6); B(0.3,0.012,0.012, M(0x8a6f42,.4,.6), 0,0.3,0, 0, sc);
    Cyl(0.05,0.012, M(0x8a6f42,.4,.6), -0.14,0.22,0, sc, 8); Cyl(0.05,0.012, M(0x8a6f42,.4,.6), 0.14,0.22,0, sc, 8); }
  { B(0.9,0.85,0.09, M(0x332b21,.85), -8.6,0.42,3.4); B(0.09,0.85,0.8, M(0x332b21,.85), -8.2,0.42,3.75);   // witness box
    chair(-8.7,3.9,Math.PI-0.3,M(0x241f19,.9)); }
  for(let px=-14.2; px<=-7.9; px+=1.26) Cyl(0.04,0.72, WOODD, px,0.36,7.6);
  B(6.6,0.07,0.12, WOODL, -11.05,0.75,7.6);                                // the bar
  desk(1.7,0.85,-12.6,5.9); desk(1.7,0.85,-9.4,5.9);
  chair(-12.6,6.7,Math.PI);
  chair(-9.4,6.7,Math.PI);
  papers(-12.6,5.9,4); papers(-9.4,5.9,3);
  { B(1.9,0.34,0.7, WOODD, -14.2,0.17,4.7, Math.PI/2);                     // jury box
    for(let i=0;i<3;i++){ chair(-14.35,3.9+i*0.8,Math.PI/2,M(0x241f19,.9)); chair(-13.7,4.1+i*0.8,Math.PI/2,M(0x241f19,.9)); } }
  const lsh = shelf(-7.45,5.2,-Math.PI/2,2.0); books(lsh,2.0);             // law reports
  [0,1,2].forEach(i=>B(0.52,0.34,0.4, M(0x6e6152,.95), -14.25,0.17+i*0.35,11.6, i*0.12));
  [0,1].forEach(i=>B(0.52,0.34,0.4, M(0x6e6152,.95), -13.6,0.17+i*0.35,11.9, -0.2));
  pendant(-11,7,2.4);
}
/* Buffett — books & markets */
{
  desk(1.9,0.95,-3,5.3); chair(-3,6.15,Math.PI); figure({x:-3,z:6.15,ry:Math.PI,kind:'suit',head:'buffett',voice:'buffett',opts:{wide:true,tie:0x8a3a30}});
  const xc = document.createElement('canvas'); xc.width=256; xc.height=160;
  const g2 = xc.getContext('2d'); g2.fillStyle='#101211'; g2.fillRect(0,0,256,160);
  g2.fillStyle='#d9a05e'; g2.font='600 34px Menlo, monospace'; g2.fillText('XERO',18,44);
  g2.fillStyle='#6d675c'; g2.font='16px Menlo, monospace';
  ['reconciled  ✓','payable   2 held','lodgement  none'].forEach((t,i)=>g2.fillText(t,18,78+i*24));
  const xt = new THREE.CanvasTexture(xc); xt.colorSpace = THREE.SRGBColorSpace;
  monitor(-3,5.05,0,xt);                                                    // the Xero terminal faces the room
  keyboard(-3,5.35); mug(-2.35,5.5,0x7a3a34);
  const s = shelf(-6.55,8.4,Math.PI/2,2.8); books(s,2.8);
  chair(-1.5,6.4,Math.PI-0.5,M(0x2e2b27,.9));                               // Peck's empty chair
  [0,1,2].forEach(i=>B(0.34,0.09,0.26, M(SPINES[i+2],.9), -4.6,0.05+i*0.095,9.6, i*0.3));
  papers(-2.6,5.2,3);
  B(0.3,0.012,0.42, M(0xb9b2a2,.95), -3.7,0.79,5.0, 0.5); B(0.3,0.006,0.2, M(0x8f887a,.95), -3.68,0.8,4.9, 0.5);  // the morning paper
  { const tk = document.createElement('canvas'); tk.width=512; tk.height=64;          // the ticker board
    const tg = tk.getContext('2d'); tg.fillStyle='#0e100e'; tg.fillRect(0,0,512,64);
    tg.fillStyle='#d9a05e'; tg.font='600 30px Menlo, monospace';
    tg.fillText('BRK.A ▲   SPX —   AUD ▲   GYM +2   CASH ✓   ', 0, 42);
    const tt = new THREE.CanvasTexture(tk); tt.colorSpace=THREE.SRGBColorSpace;
    tt.wrapS = THREE.RepeatWrapping; TICKERS.push(tt);
    const bd = new THREE.Group(); bd.position.set(-6.55,0,11.2); bd.rotation.y=Math.PI/2-0.2; world.add(bd);
    B(2.3,0.5,0.07, M(0x1c1c1f,.6), 0,1.55,0, 0, bd);
    const tp = B(2.1,0.32,0.012, new THREE.MeshStandardMaterial({map:tt, emissive:0xffffff, emissiveMap:tt, emissiveIntensity:.55, roughness:.5}), 0,1.55,0.045, 0, bd, false);
    Cyl(0.03,1.3, M(0x2b2b30,.7), -0.9,0.65,0, bd, 8); Cyl(0.03,1.3, M(0x2b2b30,.7), 0.9,0.65,0, bd, 8); }
  { B(0.24,0.1,0.3, M(0x63635e,.7), -2.2,0.83,5.55); const tp2=B(0.1,0.14,0.012, PAPER, -2.2,0.95,5.5, 0); tp2.rotation.x=-0.4; }  // adding machine
  pendant(-3,7,2.4);
}
/* Janna — day desk */
{
  desk(1.8,0.9,5,5.2); chair(5,6.05,Math.PI);
  const ms = new THREE.Group(); ms.position.set(1.62,0,8.6); ms.rotation.y=-Math.PI/2; world.add(ms);
  B(1.9,1.62,0.06, WOODD, 0,0.95,-0.16,0,ms);
  [0.2,0.56,0.92,1.28,1.64].forEach(y=>B(1.9,0.045,0.32, WOODD, 0,y,0,0,ms));
  [-0.72,-0.36,0,0.36,0.72].forEach(px=>B(0.045,1.5,0.32, WOODD, px,0.92,0,0,ms));
  [[-0.54,0.31],[0.18,0.67],[-0.18,1.03],[0.54,1.03]].forEach(p=>{ const e=B(0.26,0.03,0.2, PAPER, p[0],p[1],0.1,0,ms); e.rotation.x=0.5; });
  [0,1].forEach(i=>B(0.36,0.05,0.28, WOODL, 5.6,0.81+i*0.09,5.05));
  stack(4.5,5.35,3); papers(5.1,5.0,2); keyboard(5,5.0); mug(4.35,4.95);
  { const cart = new THREE.Group(); cart.position.set(6.9,0,8.3); cart.rotation.y=0.3; world.add(cart);   // mail cart
    B(0.8,0.05,0.5, M(0x45454c,.7,.2), 0,0.45,0, 0, cart); B(0.8,0.3,0.05, M(0x45454c,.7,.2), 0,0.62,-0.22, 0, cart);
    [[-0.32,-0.18],[0.32,-0.18],[-0.32,0.18],[0.32,0.18]].forEach(p=>Cyl(0.05,0.04, M(0x1e1e21,.6), p[0],0.05,p[1], cart, 8));
    [[-0.32,-0.18],[0.32,-0.18],[-0.32,0.18],[0.32,0.18]].forEach(p=>Cyl(0.02,0.36, M(0x2b2b30,.6), p[0],0.26,p[1], cart, 6));
    B(0.3,0.18,0.24, M(0x8a7a5c,.9), -0.15,0.58,0.05, 0.2, cart); B(0.26,0.14,0.2, M(0x6e5a3c,.9), 0.22,0.55,-0.05, -0.15, cart); }
  [[7.4,10.6],[7.9,10.2],[7.2,10.1]].forEach((p,i)=>B(0.44,0.3,0.36, M(0x8a7a5c,.9), p[0],0.15+ (i===2?0.32:0), p[1], i*0.4));  // parcels
  { const cork = new THREE.Group(); cork.position.set(8.45,0,6.4); cork.rotation.y=-Math.PI/2; world.add(cork);   // corkboard of notes
    B(1.3,0.85,0.05, M(0x5a4a38,.95), 0,1.35,0, 0, cork);
    [[-0.45,0.2,0xcfc8b6],[-0.15,0.28,0xd9a05e],[0.2,0.15,0xcfc8b6],[0.45,0.25,0x8a9a5e],[-0.3,-0.12,0xcfc8b6],[0.05,-0.2,0xb98a7c],[0.38,-0.1,0xcfc8b6]]
      .forEach(p=>B(0.16,0.14,0.012, M(p[2],.95), p[0],1.35+p[1],0.035, Math.sin(p[0]*9)*0.15, cork)); }
  plant(1.8,3.0,0.85); pendant(5,7,2.4);
}
/* Loudfair — glass agency lab: wireframes on the wall, stickies everywhere */
{
  desk(1.7,0.85,12,5.4); desk(1.7,0.85,14.6,7.9);
  monitor(12,5.15); monitor(14.6,7.65);
  keyboard(12,5.45); keyboard(14.6,7.95); mug(11.35,5.6,0x4f5a4e);
  taskChair(12,6.2,Math.PI); figure({x:12,z:6.2,ry:Math.PI,scale:.96,style:'type',head:'beanie',voice:'loudfair'});
  taskChair(14.6,8.7,Math.PI); figure({x:14.6,z:8.7,ry:Math.PI,scale:.96,style:'type',head:'phones'});
  papers(12.4,5.3,2); B(0.5,0.3,0.35, M(0x4f5a4e,.9), 15.6,0.15,10.9);
  { const wf = document.createElement('canvas'); wf.width=256; wf.height=160;          // wireframe on the big screen
    const wg = wf.getContext('2d'); wg.fillStyle='#141416'; wg.fillRect(0,0,256,160);
    wg.strokeStyle='#8f887a'; wg.lineWidth=3;
    wg.strokeRect(14,14,228,30); wg.strokeRect(14,56,108,80); wg.strokeRect(134,56,108,36); wg.strokeRect(134,100,108,36);
    wg.fillStyle='#8f887a'; wg.fillRect(20,22,60,12); wg.fillRect(20,66,90,8); wg.fillRect(20,82,70,8);
    const wt = new THREE.CanvasTexture(wf); wt.colorSpace=THREE.SRGBColorSpace;
    const bigm = new THREE.Group(); bigm.position.set(16.0,0,5.0); bigm.rotation.y=-Math.PI/2; world.add(bigm);
    B(1.5,0.9,0.06, M(0x1c1c1f,.6), 0,1.5,0, 0, bigm);
    B(1.38,0.8,0.012, new THREE.MeshStandardMaterial({map:wt, emissive:0xffffff, emissiveMap:wt, emissiveIntensity:.4, roughness:.5}), 0,1.5,0.04, 0, bigm, false);
    B(0.1,0.6,0.1, M(0x2b2b30,.7), 0,0.6,0, 0, bigm); B(0.5,0.06,0.3, M(0x2b2b30,.7), 0,0.03,0, 0, bigm); }
  { const st = new THREE.Group(); st.position.set(10.2,0,9.8); st.rotation.y=Math.PI/2; world.add(st);   // sticky-note board
    B(1.4,0.9,0.05, M(0x2e2e33,.9), 0,1.3,0, 0, st);
    const cols=[0xd9a05e,0x8a9a5e,0xb98a7c,0xcfc8b6];
    for(let r2=0;r2<3;r2++) for(let c2=0;c2<5;c2++){ if((r2*5+c2)%4===3) continue;
      B(0.14,0.14,0.012, M(cols[(r2*5+c2)%4],.95), -0.5+c2*0.25, 1.55-r2*0.28, 0.032, Math.sin(r2*7+c2)*0.12, st); } }
  plant(16.3,3.1,0.9); pendant(13,7,2.4);
}
/* stood-down rooms — mothballed under dust sheets */
{
  dustSheet(19,6.2,1.7,1.0);                                                // R&D desk, covered
  chair(19,7.3,Math.PI);
  B(0.5,0.32,0.4, M(0x8a7a5c,.9), 20.2,0.16,10.6, 0.3); B(0.44,0.28,0.36, M(0x8a7a5c,.9), 20.15,0.62,10.55, 0.5);  // packed boxes
  chair(23,7.6,Math.PI-0.4);                                                // FE — one empty chair
  dustSheet(23.4,10.2,1.2,0.9);
  B(0.5,0.32,0.4, M(0x8a7a5c,.9), 21.8,0.16,11.2, -0.2);
}
/* corridor — the office spine: cooler, printer, plants */
{
  { const wc = new THREE.Group(); wc.position.set(-0.6,0,1.66); world.add(wc);         // water cooler
    B(0.34,0.9,0.34, M(0xd6d0c2,.85), 0,0.45,0, 0, wc);
    const bt = new THREE.Mesh(new THREE.CylinderGeometry(0.13,0.13,0.3,12), new THREE.MeshStandardMaterial({color:0x9aa39c, roughness:.2, transparent:true, opacity:.5}));
    bt.position.set(0,1.05,0); bt.castShadow=true; wc.add(bt);
    B(0.1,0.08,0.06, M(0x63635e,.6), 0.12,0.62,0.16, 0, wc); }
  { const pr = new THREE.Group(); pr.position.set(6.6,0,-1.66); world.add(pr);         // printer station
    B(0.9,0.5,0.5, WOODD, 0,0.25,0, 0, pr);
    B(0.6,0.28,0.44, M(0x45454c,.7,.2), 0,0.64,0, 0, pr);
    B(0.4,0.02,0.3, PAPER, 0,0.79,0.02, 0.05, pr); B(0.3,0.1,0.42, PAPER, 0.5,0.55,0, 0.1, pr); }
  plant(24.5,1.7,1.0); plant(-14.4,-1.75,0.85); plant(24.5,-1.7,0.8);
  pendant(-6,0,2.55); pendant(13,0,2.55);
  pendant(-7,-8,2.45); pendant(15.5,-8.6,2.35); pendant(27.5,0,2.5);
}

/* ---------- dust in Ben's window light ---------- */
const DUST = [];
{
  const dc = document.createElement('canvas'); dc.width=dc.height=32;
  const dctx = dc.getContext('2d'); const dgr = dctx.createRadialGradient(16,16,1,16,16,15);
  dgr.addColorStop(0,'rgba(255,226,180,0.9)'); dgr.addColorStop(1,'rgba(255,226,180,0)');
  dctx.fillStyle=dgr; dctx.fillRect(0,0,32,32);
  const dt2 = new THREE.CanvasTexture(dc);
  for(let i=0;i<34;i++){
    const s = new THREE.Sprite(new THREE.SpriteMaterial({ map:dt2, transparent:true, opacity:0, blending:THREE.AdditiveBlending, depthWrite:false }));
    const sc = 0.05+Math.random()*0.09; s.scale.set(sc,sc,1);
    s.position.set(-22.7+Math.random()*2.0, 0.5+Math.random()*1.9, -4.6+Math.random()*8.0);
    world.add(s); DUST.push({ s, sp:0.05+Math.random()*0.06, ph:Math.random()*6.28 });
  }
}

/* ---------- the letter ---------- */
const env = B(0.3,0.045,0.22, PAPER, 0,0,0); env.visible = false;
B(0.26,0.01,0.001, M(0x8a7a5c,.9), 0,0.028,0, 0, env);                      // seal line
let envCarrier = null;
function envAt(x,y,z){ envCarrier=null; env.visible=true; env.position.set(x,y,z); env.rotation.set(0,0.3,0); }
function envCarry(id){ envCarrier=id; env.visible=true; }
function envHide(){ envCarrier=null; env.visible=false; }

/* ---------- labels ---------- */
function makeLabel(text, color='#d9d3c6', h=1.0){
  const px = 132, pad = 48;
  const c0 = document.createElement('canvas'); const m0 = c0.getContext('2d');
  m0.font = `600 ${px}px -apple-system, 'Helvetica Neue', Arial, sans-serif`;
  try{ m0.letterSpacing = '15px'; }catch(e){}
  const w = Math.ceil(m0.measureText(text).width) + pad*2;
  c0.width = w; c0.height = px + pad*1.6;
  const g2 = c0.getContext('2d');
  g2.font = `600 ${px}px -apple-system, 'Helvetica Neue', Arial, sans-serif`;
  try{ g2.letterSpacing = '15px'; }catch(e){}
  g2.textBaseline = 'middle'; g2.textAlign = 'center';
  g2.shadowColor = 'rgba(0,0,0,0.85)'; g2.shadowBlur = 38;
  g2.fillStyle = color; g2.fillText(text, c0.width/2, c0.height/2+8);
  const t = new THREE.CanvasTexture(c0); t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 16;
  const s = new THREE.Sprite(new THREE.SpriteMaterial({ map:t, transparent:true, depthTest:false }));
  s.scale.set(h * c0.width/c0.height, h, 1); s.renderOrder = 1000;
  return s;
}
const LBL = [];
/* ---------- printed signage: worn paint on walls and floorboards, no floating text ---------- */
function printTex(text, { px=170, color='#cbc4b2', worn=0.5, spacing=14 }={}){
  const c = document.createElement('canvas'); const m0 = c.getContext('2d');
  const font = `700 ${px}px 'Helvetica Neue', Arial, sans-serif`;
  m0.font = font; try{ m0.letterSpacing = spacing+'px'; }catch(e){}
  c.width = Math.ceil(m0.measureText(text).width) + 90; c.height = px*1.5;
  const g = c.getContext('2d');
  g.font = font; try{ g.letterSpacing = spacing+'px'; }catch(e){}
  g.textAlign='center'; g.textBaseline='middle';
  g.fillStyle = color; g.fillText(text, c.width/2, c.height/2+px*0.06);
  if(worn>0){                                                  // eat the paint like years of feet and sun
    g.globalCompositeOperation = 'destination-out';
    for(let i=0;i<c.width*worn*2.2;i++){
      const x = Math.random()*c.width, y = Math.random()*c.height, r = 0.6+Math.random()*2.4;
      g.globalAlpha = 0.25+Math.random()*0.5;
      g.beginPath(); g.arc(x, y, r, 0, 7); g.fill();
    }
    g.globalAlpha = 1; g.globalCompositeOperation = 'source-over';
  }
  const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 16;
  return { t, ar: c.width/c.height };
}
function printedPlane(text, o={}){
  const { t, ar } = printTex(text, o);
  const h = o.h || 0.5, w = h*ar;
  const m = new THREE.Mesh(new THREE.PlaneGeometry(w, h),
    new THREE.MeshStandardMaterial({ map:t, transparent:true, roughness:.95, metalness:0,
      color:o.tint||0xffffff, opacity:o.opacity!==undefined?o.opacity:0.94,
      polygonOffset:true, polygonOffsetFactor:-2, polygonOffsetUnits:-2, depthWrite:false }));
  m.renderOrder = 2; m.receiveShadow = true;
  return { m, w, h };
}
function wallName(text, x, y, z, ry, o={}){
  const { m } = printedPlane(text, o);
  m.position.set(x, y, z); m.rotation.y = ry; world.add(m); return m;
}
function floorName(text, x, z, o={}){
  const { t, ar } = printTex(text, Object.assign({color:'#ffffff', worn:0.32}, o));
  let w = o.w || 6, h = w/ar;
  if(o.h){ h = o.h; w = h*ar; }
  const nm = PBR_TEX['herringbone_parquet/nor_gl.jpg'];
  const nmap = nm ? nm.clone() : null;
  if(nmap){ nmap.needsUpdate = true; nmap.repeat.set(w/2.6, h/2.6); }
  const m = new THREE.Mesh(new THREE.PlaneGeometry(w, h),
    new THREE.MeshStandardMaterial({ map:t, transparent:true, opacity:o.opacity!==undefined?o.opacity:0.3,
      color:0xffffff, roughness:.85, normalMap:nmap,
      polygonOffset:true, polygonOffsetFactor:-2, polygonOffsetUnits:-2, depthWrite:false }));
  m.renderOrder = 2; m.receiveShadow = true;
  m.rotation.x = -Math.PI/2; m.rotation.z = o.rz||0;
  m.position.set(x, 0.078, z); world.add(m); m.userData.h = h; return m;
}
const SEAT_TITLE = { rnd:'CREATIVE DIRECTOR', shakespeare:'COPYWRITER', wozniak:'CTO', ive:'CHIEF DESIGN OFFICER', jobs:'CO-FOUNDER',
  buffett:'CFO', wrench:'GENERAL COUNSEL', janna:'OPERATIONS', efficacy:'THE FILTER', loudfair:'AGENCY',
  assange:'STOOD DOWN', grants:'FY25 PARKED', ben:'THE DECISION' };
ROOMS.forEach(r=>{
  const [x1,z1,x2,z2] = r.rect, cx=(x1+x2)/2;
  const north = z2 <= 0, tint = r.lc || 0xd6cfbd;
  const label = r.sub ? r.name+' · '+r.sub : r.name;
  if(r.id==='ben'){
    wallName('BEN', -22.75, 1.9, 0, Math.PI/2, {h:0.7, tint});
    const fb = floorName('LOCKED', -17.0, 0, {w:5.5, rz:-Math.PI/2});
    floorName('THE DECISION', -17.0+fb.userData.h*0.66+0.26, 0, {h:fb.userData.h*0.3, rz:-Math.PI/2, opacity:0.26});
  } else if(r.id==='grants'){
    const fn = floorName(label, x1+0.85, 0, {w:8.5, rz:-Math.PI/2});
    floorName(SEAT_TITLE[r.id], x1+0.85+fn.userData.h*0.66+0.28, 0, {h:fn.userData.h*0.3, rz:-Math.PI/2, opacity:0.26});
  } else if(north){
    wallName(label, cx, 1.66, -12.24, 0, {h:Math.min(0.78, (x2-x1)/(label.length*0.52)), tint, worn:0.5});
    const fn = floorName(label, cx, z2-1.9, {w:(x2-x1)*0.86});
    if(SEAT_TITLE[r.id]) floorName(SEAT_TITLE[r.id], cx, z2-1.9+fn.userData.h*0.66+0.28, {h:fn.userData.h*0.3, opacity:0.26});
  } else {
    wallName(label, cx, 0.55, 2.16, 0, {h:0.42, tint, worn:0.4});
    const fn = floorName(label, cx, z1+1.55, {w:(x2-x1)*0.86});
    if(SEAT_TITLE[r.id]) floorName(SEAT_TITLE[r.id], cx, z1+1.55+fn.userData.h*0.66+0.28, {h:fn.userData.h*0.3, opacity:0.26});
  }
});
/* the one coral: painted straight onto the door monolith */
wallName('ATG · THE ONLY DOOR', -14.72, 2.62, 0, Math.PI/2, {h:0.22, tint:0xff8a7e, worn:0.3, opacity:1});
wallName('ALEXANDER THE GREAT · RULER OF THE AGENTS', -14.72, 2.36, 0, Math.PI/2, {h:0.12, tint:0xc99384, worn:0.3});
wallName('CHIEF OF STAFF', -14.72, 2.18, 0, Math.PI/2, {h:0.1, tint:0xb98a7c, worn:0.3});

/* ---------- corridor flow: orders east, reports west ---------- */
const flow = [];
{
  const og = new THREE.SphereGeometry(0.06, 8, 6);
  const om = new THREE.MeshBasicMaterial({ color:0xd8a05a, transparent:true, opacity:.7 });
  const rm2 = new THREE.MeshBasicMaterial({ color:0x7d786c, transparent:true, opacity:.34 });
  for(let i=0;i<15;i++){ const m=new THREE.Mesh(og,om); m.position.y=0.1; world.add(m); flow.push({m,off:i*2.63,lane:1}); }
  for(let i=0;i<12;i++){ const m=new THREE.Mesh(og,rm2); m.position.y=0.1; m.scale.setScalar(.8); world.add(m); flow.push({m,off:i*3.29,lane:-1}); }
}
/* Assange's quiet dotted walk to the door */
{
  const pts=[[22,-2.3],[22,-0.68],[-13.6,-0.68],[-14.4,-0.1]];
  const dg=new THREE.CircleGeometry(0.065,8), dm=new THREE.MeshBasicMaterial({color:0x8a8072,transparent:true,opacity:.7});
  let total=0; for(let i=1;i<pts.length;i++) total+=Math.hypot(pts[i][0]-pts[i-1][0],pts[i][1]-pts[i-1][1]);
  for(let s=0;s<1;s+=0.014){
    let d=s*total, p=null;
    for(let i=1;i<pts.length;i++){ const L=Math.hypot(pts[i][0]-pts[i-1][0],pts[i][1]-pts[i-1][1]); if(d<=L){const t=d/L; p=[pts[i-1][0]+(pts[i][0]-pts[i-1][0])*t, pts[i-1][1]+(pts[i][1]-pts[i-1][1])*t]; break;} d-=L; }
    if(!p) continue;
    const m=new THREE.Mesh(dg,dm); m.rotation.x=-Math.PI/2; m.position.set(p[0],0.045,p[1]); world.add(m);
  }
}

/* ---------- busy lock (pings + scenes share it) ---------- */
let busy = false;
function setBusy(b){ busy=b; document.body.classList.toggle('busy', b); }

/* ---------- ping system ---------- */
const PINGS = {
  inbox:   { room:'janna',   pts:[[5,0.35],[5,2.1],[5,4.9]],
             answer:'Janna: three items today — one needs your YES, the rest are handled.' },
  books:   { room:'buffett', pts:[[-3,0.35],[-3,2.1],[-3,4.9]],
             answer:'Buffett: books reconciled to Friday. Nothing paid, nothing lodged — two invoices wait on your YES.' },
  jobhunt: { room:'efficacy',pts:[[-13,-0.35],[-13,-2.1],[-13,-4.7]],
             answer:'Obama: two roles worth your eyes. Shortlist drafted — nothing applied, nothing sent.' },
  grants:  { room:'grants',  pts:[[24.6,0],[26.2,0]],
             answer:'Grants pod: FY25 stays parked. The file is ready the day you say go.' },
  loudfair:{ room:'loudfair',pts:[[13,0.35],[13,2.1],[13,4.9]],
             answer:'Loudfair: copy v2 is on the glass — waiting on YES I APPROVE TO SEND.' },
};
const packet = new THREE.Group(); packet.visible = false; world.add(packet);
{
  const core = new THREE.Mesh(new THREE.SphereGeometry(0.13,14,10),
    new THREE.MeshStandardMaterial({ color:0xffe6bd, emissive:0xffc37a, emissiveIntensity:2.2, roughness:.4 }));
  packet.add(core);
  const gc = document.createElement('canvas'); gc.width=gc.height=64;
  const gg = gc.getContext('2d'); const gr = gg.createRadialGradient(32,32,2,32,32,30);
  gr.addColorStop(0,'rgba(255,214,150,0.9)'); gr.addColorStop(1,'rgba(255,214,150,0)');
  gg.fillStyle=gr; gg.fillRect(0,0,64,64);
  const gt = new THREE.CanvasTexture(gc);
  const glow = new THREE.Sprite(new THREE.SpriteMaterial({ map:gt, transparent:true, blending:THREE.AdditiveBlending, depthWrite:false }));
  glow.scale.set(1.5,1.5,1); packet.add(glow);
  packet.userData.light = new THREE.PointLight(0xffbe7d, 0, 8, 1.8); packet.add(packet.userData.light);
}
const trail = [];
{
  const gm = packet.children[1].material;
  for(let i=0;i<9;i++){ const s=new THREE.Sprite(gm.clone()); s.material.opacity=0; s.scale.set(.9,.9,1); world.add(s); trail.push(s); }
}
function polyline(pts){
  const lens=[0]; let L=0;
  for(let i=1;i<pts.length;i++){ L+=Math.hypot(pts[i][0]-pts[i-1][0], pts[i][1]-pts[i-1][1]); lens.push(L); }
  return { total:L, at(s){ s=Math.max(0,Math.min(L,s));
    for(let i=1;i<pts.length;i++){ if(s<=lens[i]){ const t=(s-lens[i-1])/(lens[i]-lens[i-1]);
      return [ pts[i-1][0]+(pts[i][0]-pts[i-1][0])*t, pts[i-1][1]+(pts[i][1]-pts[i-1][1])*t ]; } }
    return pts[pts.length-1]; } };
}
let ping = null;
function runPing(key){
  if(busy) return;
  try{ SFX.cue('message-sent'); }catch(e){}
  const P = PINGS[key];
  const out = [[-18.6,0],[-16.2,0],[-14.6,0],[-13.4,0.12]].concat(P.pts);
  ping = { path: polyline(out), s:0, phase:'out', wait:0, answer:P.answer, room:P.room };
  packet.visible = true; packet.userData.light.intensity = 26;
  setBusy(true);
}
document.querySelectorAll('[data-ping]').forEach(b=>b.addEventListener('click', ()=>runPing(b.dataset.ping)));

/* ---------- speech bubble (pings + scenes) ---------- */
const bubble = document.getElementById('bubble');
let bubbleAnchor = new THREE.Vector3(-18.6, 3.0, 0);
let bubbleUntil = 0;
function say(text, anchor, ms=5600, who){
  if(anchor) bubbleAnchor = anchor.clone ? anchor.clone() : anchor;
  bubble.textContent = '';
  if(who && VOICES[who]){                                     // name the speaker so a busy floor stays legible
    const h = document.createElement('span');
    h.style.cssText = 'display:block;font:600 8.5px/1 Futura,Avenir,system-ui,sans-serif;letter-spacing:.2em;'
      + 'text-transform:uppercase;color:rgba(214,203,178,.62);margin-bottom:5px';
    h.textContent = VOICES[who].name;
    bubble.appendChild(h);
  }
  bubble.appendChild(document.createTextNode(text));
  bubble.style.display='block';
  bubble.style.opacity = '0';
  bubble.style.transition = 'opacity .28s ease';
  setTimeout(()=>{ bubble.style.opacity = '1'; }, 16);
  bubbleUntil = performance.now()+ms;
}
function actorAnchor(id){
  const a = rigOf(id);
  if(!a) return SEAT_POS[id] ? new THREE.Vector3(SEAT_POS[id][0], 1.9, SEAT_POS[id][1]) : new THREE.Vector3();
  return new THREE.Vector3(a.root.position.x, (a.pose==='stand'?2.05:1.6)*a.root.scale.x, a.root.position.z);
}

/* room pulse */
const pulses = [];
function pulse(id){ const m = floorMesh[id]; if(!m) return; pulses.push({ m, t:0 }); }

/* ---------- scene engine ---------- */
const SPEED = 1.45;                                      // a real walking pace — the floor takes its time
function stepWalk(id, pts, endYaw){
  return { start(){ const a=ACTORS[id]; if(!a){ this.skip=true; return; } a.setPose('stand'); a.isWalking=true;
      this.a=a; this.p=polyline([[a.root.position.x, a.root.position.z]].concat(pts)); this.s=0; },
    update(dt){ if(this.skip) return true; const a=this.a;
      /* people accelerate away and slow into an arrival — they don't teleport up to speed */
      const inK  = Math.min(1, this.s/0.55), outK = Math.min(1, (this.p.total-this.s)/0.85);
      const k    = Math.max(0.18, Math.min(inK, outK*outK*(3-2*outK)));
      a.walkSpeed = SPEED*k;
      this.s += a.walkSpeed*dt;
      const done = this.s >= this.p.total;
      const q = this.p.at(this.s), q2 = this.p.at(Math.min(this.s+0.3, this.p.total));
      const dx=q2[0]-q[0], dz=q2[1]-q[1];
      if(Math.hypot(dx,dz)>0.001){ const yaw=Math.atan2(dx,dz);
        let d=yaw-a.root.rotation.y; while(d>Math.PI)d-=2*Math.PI; while(d<-Math.PI)d+=2*Math.PI;
        a.root.rotation.y += d*Math.min(1, dt*10); }
      a.root.position.set(q[0], 0, q[1]);
      a.walkT += dt;
      const w = Math.sin(a.walkT*10), w2 = Math.sin(a.walkT*10+0.6);
      a.legL.rotation.x = w*0.68; a.legR.rotation.x = -w*0.68;
      a.armL.rotation.x = -w*0.45; a.armR.rotation.x = envCarrier===id ? -1.05 : w*0.45;
      a.root.position.y = Math.abs(w2)*0.035;
      a.root.rotation.z = w*0.02;                                        // hip sway
      if(done){ a.legL.rotation.x=a.legR.rotation.x=a.armL.rotation.x=0;
        a.armR.rotation.x = envCarrier===id ? -1.05 : 0;
        a.root.position.y=0; a.root.rotation.z=0; a.walkT=0; a.isWalking=false; a.walkSpeed=0;
        if(endYaw!==undefined) a.root.rotation.y = endYaw; }
      return done; } };
}
function stepSit(id){ return { t:0, start(){ this.a=ACTORS[id]; },
  update(dt){ if(!this.a) return true; this.t+=dt; const a=this.a;
    if(this.t<0.2){ a.root.position.y = -0.05*Math.sin(this.t/0.2*Math.PI); return false; }
    a.root.position.set(a.home.x,0,a.home.z); a.root.rotation.y=a.home.yaw; a.setPose('sit'); return true; } }; }
function stepPose(id,p){ return { start(){ const r = rigOf(id); if(r) r.setPose(p); }, update(){ return true; } }; }
function stepSay(text, id, hold=2.4){ return { t:0,
  start(){ say(text, id?actorAnchor(id):undefined, (hold+0.6)*1000, id);
    if(id){ orbPulse(id); speak(id, text); } }, update(dt){ this.t+=dt; return this.t>=hold; } }; }
function stepWait(d){ return { t:0, start(){}, update(dt){ this.t+=dt; return this.t>=d; } }; }
function stepEmote(id, kind, dur=1.6){ return { t:0,
  start(){ this.r = rigOf(id); if(this.r) this.r.anim = {kind, t:0, dur}; },
  update(dt){ this.t += dt; return !this.r || !this.r.anim || this.t > dur + 1; } }; }
function stepLean(id, on){ return { start(){ const r = rigOf(id); if(r) r.leanTo = on?0.42:0; }, update(){ return true; } }; }
function stepDoor(op){ return { t:0, start(){ this.op=op; if(op==='deny'){ ACTORS.atg.anim=null; } },
  update(dt){ this.t+=dt;
    if(this.op==='deny' && !this._d){ this._d=1; try{ SFX.cue('door-denied'); }catch(e){} }
    if(this.op==='open'){ if(!this._c){ this._c=1; try{ SFX.cue('door-open'); }catch(e){} } doorOpenT=Math.min(1,doorOpenT+dt*2.4); return doorOpenT>=1; }
    if(this.op==='close'){ if(!this._c){ this._c=1; try{ SFX.cue('door-close'); }catch(e){} } doorOpenT=Math.max(0,doorOpenT-dt*2.4); return doorOpenT<=0; }
    doorMat.emissiveIntensity = 0.55 + Math.max(0,Math.sin(this.t*14))*2.2;   // deny — the door burns coral, stays shut
    return this.t>=1.1; } };
}
function stepCall(fn){ return { start(){ fn(); }, update(){ return true; } }; }
function stepPar(steps){ return { start(){ this.live=steps.slice(); this.live.forEach(s=>s.start()); },
  update(dt){ this.live = this.live.filter(s=>!s.update(dt)); return this.live.length===0; } }; }

let sceneQ = null, sceneCur = null;
let ambQ = null, ambCur = null, ambNext = 12;
function cancelAmbient(){
  if(!ambQ && !ambCur) return;
  ambQ = null; ambCur = null;
  ['efficacy','janna','shakespeare','cleaner1','cleaner2','pete','atg'].forEach(id=>{ const a=ACTORS[id];
    a.isWalking=false; a.anim=null; a.root.position.set(a.home.x,0,a.home.z);
    a.root.rotation.y=a.home.yaw; a.root.rotation.z=0; a.setPose(a.home.pose); });
}
function runScene(key){
  if(busy) return;
  try{ SFX.cue('scene-start'); }catch(e){}
  cancelAmbient();
  setBusy(true);
  sceneQ = SCENES[key]();
  sceneCur = null;
}
/* small background errands — the floor keeps living on its own */
/* ---------- ambient conversations — seats talk to each other on Ben's subscription ---------- */
const CONVO_PAIRS = [['jobs','ive'], ['pete','loudfair'], ['buffett','janna'], ['wozniak','pete'], ['shakespeare','wrench']];
const CONVO_TOPICS = ['what to cut from the product this week', 'the launch copy', 'the next campaign idea',
  'whether the numbers support the plan', 'the one thing that deserves Ben\'s YES today', 'making the booking flow simpler'];
function stepConverse(){
  return { t:0, done:false,
    start(){
      if(!aiReady()){ this.done = true; return; }
      const pair = CONVO_PAIRS[Math.floor(Math.random()*CONVO_PAIRS.length)];
      const topic = CONVO_TOPICS[Math.floor(Math.random()*CONVO_TOPICS.length)];
      this.run(pair, topic);
    },
    async run(pair, topic){
      const [a, b] = pair;
      try{
        const lines = [];
        for(let i=0; i<3; i++){
          if(busy) break;                                    // a real scene owns the floor
          const who = i%2===0 ? a : b, other = i%2===0 ? b : a;
          const ctx = lines.length ? 'The conversation so far:\n'+lines.join('\n')+'\n' : '';
          const l = await aiLine(who,
            'You are talking across the office with '+(VOICES[other]?VOICES[other].name:other)+' about '+topic+'. '+
            ctx+'Say only your next line of this office conversation.');
          if(busy || !l) break;
          lines.push((VOICES[who]?VOICES[who].name:who)+': '+l);
          say(l, anchorSeat(who), 4600, who); orbPulse(who); speak(who, l);
          await new Promise(r=>setTimeout(r, 4300));
        }
      }catch(e){}
      this.done = true;
    },
    update(dt){ this.t += dt; return this.done || this.t > 50; } };
}
/* ---------- the building staff — cleaners on rounds, security at the front ---------- */
figure({ kind:'staff', seated:false, x:23.55, z:1.05, ry:-Math.PI/2 });
function makeCleaner(id, x, z, ry){
  const c = actor(id, 'cleaner', x, z, ry, 'stand', null, {});
  const cart = new THREE.Group(); cart.position.set(0,0,0.52); c.root.add(cart);   // pushed ahead, rolls with them
  B(0.46,0.5,0.34, M(0x4a4a50,.7,.25), 0,0.32,0, 0, cart);
  B(0.5,0.03,0.38, M(0x3a3a40,.6,.3), 0,0.58,0, 0, cart);
  const bucket = new THREE.Mesh(new THREE.CylinderGeometry(0.09,0.075,0.16,12), M(0xb09a2e,.55));
  bucket.position.set(0.12,0.66,0.02); bucket.castShadow = true; cart.add(bucket);
  const mop = new THREE.Mesh(new THREE.CylinderGeometry(0.013,0.013,1.15,8), M(0x6e5a3c,.8));
  mop.position.set(-0.16,0.95,-0.06); mop.rotation.z = 0.22; cart.add(mop);
  const mophead = new THREE.Mesh(new THREE.SphereGeometry(0.05,10,8), M(0xb8b2a4,.95));
  mophead.scale.set(1,0.55,1); mophead.position.set(-0.28,0.44,-0.06); cart.add(mophead);
  [[-0.18,0.14],[0.18,0.14],[-0.18,-0.14],[0.18,-0.14]].forEach(p=>{
    const w = new THREE.Mesh(new THREE.SphereGeometry(0.038,10,8), M(0x1d1d20,.6));
    w.position.set(p[0],0.05,p[1]); cart.add(w);
  });
  return c;
}
makeCleaner('cleaner1', 20.4, 0.9, -Math.PI/2);
makeCleaner('cleaner2', -9.5, 1.2, Math.PI/2);
const AMBIENTS = [
  ()=>[ stepPose('efficacy','stand'),
        stepWalk('efficacy', [[-13,-2.1],[-13.3,-0.6],[-13.5,-1.0]], Math.PI),
        stepWait(0.8),
        stepWalk('efficacy', [[-13.2,-0.6],[-13,-2.1],[-13,-4.35]]),
        stepSit('efficacy') ],
  ()=>[ stepPose('shakespeare','stand'),
        stepWalk('shakespeare', [[-8.8,-6.5],[-8.8,-9.6],[-9.6,-11.2]], Math.PI),
        stepWait(1.1),
        stepWalk('shakespeare', [[-8.8,-9.6],[-8.8,-6.5],[-7,-6.7]]),
        stepSit('shakespeare') ],
  ()=>[ stepPose('janna','stand'),                                          // the EA's mail run to the sorting room
        stepWalk('janna', [[-12.4,0.6],[-6,0.85],[2,0.85],[4.4,3.6],[4.6,5.3]], Math.PI),
        stepWait(1.3),
        stepWalk('janna', [[4.4,3.6],[2,0.85],[-6,0.85],[-12.4,0.6],[-12.55,1.35]]),
        stepSit('janna') ],
  ()=>[ stepWalk('cleaner1', [[14,0.9],[6,0.9],[-2,0.9]], Math.PI),          // the long corridor sweep
        stepWait(1.4),
        stepWalk('cleaner1', [[6,0.9],[14,0.9],[20.4,0.9]], -Math.PI/2) ],
  ()=>[ stepWalk('cleaner2', [[-4,1.1],[4,1.1],[12,1.1]], Math.PI/2),
        stepWait(1.2),
        stepWalk('cleaner2', [[4,1.1],[-4,1.1],[-9.5,1.2]], Math.PI/2) ],
  ()=>[ stepWalk('cleaner1', [[20.7,0.5],[21,0.85]], 0),                     // the WC round
        stepWait(2.2),
        stepWalk('cleaner1', [[20.6,0.5],[20.4,0.9]], -Math.PI/2) ],
  /* the floor is never still: stretches, window-gazing, desk visits, coffee runs */
  ()=>[ stepEmote('shakespeare','nod',1.6), stepWait(1.2), stepEmote('shakespeare','shake',1.4) ],
  ()=>[ stepPose('efficacy','stand'),                                        // stands to think
        stepWalk('efficacy', [[-13.4,-9.6],[-14.1,-9.0]], -Math.PI/2),
        stepWait(3.2),
        stepWalk('efficacy', [[-13.4,-9.8],[-13,-10.9]], 0),
        stepSit('efficacy') ],
  ()=>[ stepPose('janna','stand'),                                           // walks a note to the counsel room
        stepWalk('janna', [[-12.6,0.85],[-11,0.85],[-11,3.4],[-11.9,5.4]], -Math.PI/2-0.4),
        stepWait(1.8),
        stepEmote('janna','nod',1.2),
        stepWalk('janna', [[-11,3.4],[-11,0.85],[-12.6,0.85],[-12.55,1.35]], -Math.PI/2),
        stepSit('janna') ],
  ()=>[ stepPose('pete','stand'),                                            // steps back to judge the canvas
        stepWalk('pete', [[20.3,5.6]], -Math.PI/2),
        stepWait(2.6),
        stepEmote('pete','shake',1.3),
        stepWalk('pete', [[19.55,6.75]], -2.5) ],
  ()=>[ stepEmote('atg','nod',1.4), stepWait(2.0),                           // the ruler surveys his floor
        stepWalk('atg', [[-13.4,-0.9],[-13.4,1.7]], Math.PI),
        stepWait(2.4),
        stepWalk('atg', HOME_ATG, -Math.PI/2) ],
  ()=>[ stepConverse() ],                                                    // the floor talks to itself
  ()=>[ stepConverse() ],
  ()=>[ stepEmote('pete','dance',7) ],                                       // weird shit in his office
  ()=>[ stepWalk('pete', [[19.9,5.0],[20.25,4.6]], Math.PI/2),               // stares at his own wall for a while
        stepWait(2.4),
        stepEmote('pete','shake',1.4),
        stepWalk('pete', [[19.6,6.2],[19.55,6.75]], -2.5) ],
];
document.querySelectorAll('[data-scene]').forEach(b=>b.addEventListener('click', ()=>runScene(b.dataset.scene)));

/* — the choreography — */
const HOME_ATG = [[-13.2,0.35],[-13.9,0.85]];
const SCENES = {
  /* the legal chain: Janna → ATG → Wrench (suit) → Shakespeare → Wrench → Ben → Janna sends */
  legal(){ return [
    stepCall(()=>envAt(-13.2,0.82,1.42)),
    stepSay('a legal email lands on the EA desk — Janna flags it', 'janna', 2.2),
    stepWalk('atg', [[-13.2,0.35],[-13.15,0.7]], 0),
    stepCall(()=>envCarry('atg')),
    stepSay('Alexander reads the flag: legal. He rules the floor — this walks to Wrench.', 'atg', 2.4),
    stepWalk('atg', [[-12.6,0.35],[-11,0.35],[-11,2.1],[-11.9,5.2]], -Math.PI/2-0.5),
    stepCall(()=>{ envAt(-12.45,0.82,5.75); }),
    stepLean('atg', true),
    stepSay('ATG leans over the desk while Wrench, in his suit, reviews', 'wrench', 2.6),
    stepEmote('wrench','type',2.6),
    stepSay('Wrench types the response — drafts only, never files, never sends', 'wrench', 2.4),
    stepPar([ stepPose('shakespeare','stand'),
      stepWalk('shakespeare', [[-7,-6.0],[-7,-2.1],[-7,0.35],[-11,0.35],[-11,2.1],[-10.6,5.4]], -Math.PI/2+0.6) ]),
    stepSay('Shakespeare comes to the bar and refines the wording', 'shakespeare', 2.6),
    stepEmote('shakespeare','nod',1.2),
    stepWalk('shakespeare', [[-11,2.1],[-11,0.35],[-7,0.35],[-7,-2.1],[-7,-6.7]]),
    stepSit('shakespeare'),
    stepEmote('wrench','nod',1.2),
    stepSay('back through Wrench: all clear', 'wrench', 2.0),
    stepLean('atg', false),
    stepCall(()=>envCarry('atg')),
    stepWalk('atg', [[-11,2.1],[-11,0.35],[-13.8,0.1]]),
    stepDoor('open'),
    stepWalk('atg', [[-14.6,0],[-16.4,0],[-18.1,0.35]], -Math.PI/2-0.6),
    stepDoor('close'),
    stepCall(()=>envAt(-18.55,0.82,0.35)),
    stepLean('ben', true),
    stepSay('Ben reads it', 'ben', 1.8),
    stepEmote('ben','nod',1.4),
    stepSay('Ben: perfect.', 'ben', 1.8),
    stepLean('ben', false),
    stepCall(()=>envCarry('atg')),
    stepDoor('open'),
    stepWalk('atg', [[-16.4,0],[-14.6,0],[-13.8,0.12]]),
    stepDoor('close'),
    stepWalk('atg', [[-13.15,0.7]], 0),
    stepCall(()=>{ envAt(-13.2,0.82,1.42); }),
    stepSay('back to Janna — she sends. YES I APPROVE TO SEND, on file.', 'janna', 2.6),
    stepEmote('janna','type',2.0),
    stepCall(()=>{ envHide();
      ping = { path: polyline([[-12.9,1.0],[-11,0.35],[-6,0.35],[24.8,0.2]]), s:0, phase:'send', answer:null };
      packet.visible = true; packet.userData.light.intensity = 20; }),
    stepWait(4.2),
    stepWalk('atg', HOME_ATG, -Math.PI/2),
    stepSay('sent. that is the whole chain — one door, every step walked.', 'atg', 2.6),
  ]; },
  /* product / design: Jobs × Ive argue in the collab space, agree, Ben refines, Janna sends */
  product(){ return [
    stepSay('product question — Jobs and Ive meet in the collab space', 'jobs', 2.2),
    stepPar([
      stepWalk('ive', [[8.5,-2.1],[8.5,-0.35],[15.5,-0.35],[15.5,-2.1],[14.5,-7.4]], Math.PI/2+0.5),
      stepWalk('jobs', [[16.5,-7.5]], -Math.PI/2-0.5),
      stepWalk('atg', [[-13.2,0.2],[15.5,-0.35],[15.5,-2.1],[15.5,-6.2]], Math.PI),
    ]),
    stepSay('Alexander stands in — the ruler keeps the room honest', 'atg', 2.0),
    stepPar([ stepEmote('ive','argue',2.8), stepEmote('jobs','argue',2.8) ]),
    stepSay('they disagree —', 'jobs', 1.6),
    stepPar([ stepEmote('ive','argue',2.2), stepEmote('jobs','shake',2.2) ]),
    stepPar([ stepEmote('ive','nod',1.4), stepEmote('jobs','nod',1.4) ]),
    stepSay('— then they agree. one page, one object, less of it.', 'ive', 2.4),
    stepCall(()=>envCarry('atg')),
    stepWalk('atg', [[15.5,-2.1],[15.5,-0.35],[-13.8,0.1]]),
    stepDoor('open'),
    stepWalk('atg', [[-14.6,0],[-16.4,0],[-18.1,0.35]], -Math.PI/2-0.6),
    stepDoor('close'),
    stepCall(()=>envAt(-18.55,0.82,0.35)),
    stepLean('ben', true),
    stepSay('Ben refines — a little', 'ben', 2.0),
    stepEmote('ben','nod',1.2),
    stepLean('ben', false),
    stepCall(()=>envCarry('atg')),
    stepDoor('open'),
    stepWalk('atg', [[-16.4,0],[-14.6,0],[-13.8,0.12]]),
    stepDoor('close'),
    stepWalk('atg', [[-13.15,0.7]], 0),
    stepCall(()=>envAt(-13.2,0.82,1.42)),
    stepSay('hand back to Janna — she sends', 'janna', 2.2),
    stepEmote('janna','type',1.8),
    stepCall(()=>{ envHide();
      ping = { path: polyline([[-12.9,1.0],[-11,0.35],[-6,0.35],[24.8,0.2]]), s:0, phase:'send', answer:null };
      packet.visible = true; packet.userData.light.intensity = 20; }),
    stepPar([
      stepWalk('ive', [[15.5,-2.1],[15.5,-0.35],[8.5,-0.35],[8.5,-2.1],[8.5,-5.9]], Math.PI),
      stepWalk('jobs', [[14.3,-6.9]], Math.PI+0.5),
      stepWalk('atg', HOME_ATG, -Math.PI/2),
    ]),
  ]; },
  /* the brief: Pete takes a creative idea one notch weirder, Loudfair lands it */
  brief(){ return [
    stepSay('a campaign brief lands — Alexander takes it to the studio', 'atg', 2.4),
    stepCall(()=>envCarry('atg')),
    stepWalk('atg', [[-13.2,0.35],[6,0.85],[16,0.85],[19,3.2],[19.3,5.6]], Math.PI),
    stepCall(()=>envAt(19.5,0.9,6.1)),
    stepPose('pete','stand'),
    stepSay('Pete reads it. Mate, it is nearly there.', 'pete', 2.6),
    stepEmote('pete','argue',2.6),
    stepSay('It just needs to be weirder — and then it needs to sell.', 'pete', 2.8),
    stepWalk('pete', [[20.2,5.4]], -Math.PI/2),
    stepWait(1.6),
    stepEmote('pete','nod',1.4),
    stepWalk('pete', [[19.55,6.75]], -2.5),
    stepSay('he hands it back sharper than it came in', 'pete', 2.4),
    stepCall(()=>envCarry('atg')),
    stepWalk('atg', [[19,3.2],[16,0.85],[6,0.85],[-13.8,0.1]]),
    stepDoor('open'),
    stepWalk('atg', [[-14.6,0],[-16.4,0],[-18.1,0.35]], -Math.PI/2-0.6),
    stepDoor('close'),
    stepCall(()=>envAt(-18.55,0.82,0.35)),
    stepSay('Ben: that one goes out.', 'ben', 2.2),
    stepCall(()=>envCarry('atg')),
    stepDoor('open'),
    stepWalk('atg', [[-16.4,0],[-14.6,0],[-13.8,0.12]]),
    stepDoor('close'),
    stepCall(()=>envHide()),
    stepWalk('atg', HOME_ATG, -Math.PI/2),
    stepSay('nothing sent until he says so. that is the whole rule.', 'atg', 2.6),
  ]; },
  /* the build: Woz is handed a problem and answers with a working thing */
  build(){ return [
    stepSay('something is broken. Alexander takes it to the bench.', 'atg', 2.4),
    stepCall(()=>envCarry('atg')),
    stepWalk('atg', [[-13.2,0.35],[-6,0.85],[1,0.85],[1,-2.1],[1.2,-9.4]], Math.PI),
    stepCall(()=>envAt(0.9,0.9,-10.0)),
    stepSay('Wozniak does not theorise. He builds the smallest honest version.', 'wozniak', 3.0),
    stepEmote('wozniak','type',3.2),
    stepSay('It works. Now let me make it simple enough to be true.', 'wozniak', 2.8),
    stepEmote('wozniak','nod',1.4),
    stepCall(()=>envCarry('atg')),
    stepWalk('atg', [[1,-2.1],[1,0.85],[-6,0.85],[-13.8,0.1]]),
    stepDoor('open'),
    stepWalk('atg', [[-14.6,0],[-16.4,0],[-18.1,0.35]], -Math.PI/2-0.6),
    stepDoor('close'),
    stepSay('Ben: ship it.', 'ben', 2.0),
    stepDoor('open'),
    stepWalk('atg', [[-16.4,0],[-14.6,0],[-13.8,0.12]]),
    stepDoor('close'),
    stepCall(()=>envHide()),
    stepWalk('atg', HOME_ATG, -Math.PI/2),
  ]; },
  /* the lock: even Wrench in his suit cannot reach Ben — the door doesn't open for him */
  lock(){ return [
    stepSay('Wrench has something urgent for Ben. watch.', 'wrench', 2.2),
    stepPose('wrench','stand'),
    stepCall(()=>envCarry('wrench')),
    stepWalk('wrench', [[-12.6,5.4],[-11,2.1],[-11,0.35],[-14.05,0.1]], -Math.PI/2),
    stepDoor('deny'),
    stepSay('the door does not open. not for the suit, not for anyone.', 'wrench', 2.4),
    stepEmote('wrench','shake',1.6),
    stepWalk('atg', [[-13.7,0.3]], Math.PI/2),
    stepSay('deal with ATG. that is the rule that makes the room quiet.', 'atg', 2.6),
    stepCall(()=>envCarry('atg')),
    stepWalk('wrench', [[-11,0.35],[-11,2.1],[-12.6,5.9],[-12.6,6.7]]),
    stepSit('wrench'),
    stepDoor('open'),
    stepWalk('atg', [[-14.6,0],[-16.4,0],[-18.1,0.35]], -Math.PI/2-0.6),
    stepDoor('close'),
    stepCall(()=>envAt(-18.55,0.82,0.35)),
    stepSay('the message still gets in — it just walks through the door', 'ben', 2.4),
    stepDoor('open'),
    stepWalk('atg', [[-16.4,0],[-14.6,0],[-13.8,0.12]]),
    stepDoor('close'),
    stepWalk('atg', HOME_ATG, -Math.PI/2),
    stepCall(()=>envHide()),
  ]; },
};

/* ---------- picking ---------- */
const pick = [];
ROOMS.forEach(r=>{
  const [x1,z1,x2,z2] = r.rect;
  const m = new THREE.Mesh(new THREE.BoxGeometry(x2-x1, 2.4, z2-z1),
    new THREE.MeshBasicMaterial({ transparent:true, opacity:0, depthWrite:false }));
  m.position.set((x1+x2)/2, 1.2, (z1+z2)/2); m.userData.room = r; world.add(m); pick.push(m);
});
{
  const m = new THREE.Mesh(new THREE.BoxGeometry(1.6, 2.6, 3.2),
    new THREE.MeshBasicMaterial({ transparent:true, opacity:0, depthWrite:false }));
  m.position.set(-14.6, 1.3, 0);
  m.userData.room = { id:'atg', job:['Alexander the Great — ruler of the agents','Not a node on a chart: the door itself. He commands every seat on this floor, and he is the only one who walks in and out of Ben’s office. Orders go down through him; everything coming up passes his hands. His standard is planted at the threshold.'] };
  world.add(m); pick.push(m);
}
const ray = new THREE.Raycaster(); const ptr = new THREE.Vector2();
let downAt = null;
renderer.domElement.addEventListener('pointerdown', e=>{ downAt=[e.clientX,e.clientY];
  if(followId){ followId = null; if(nav!=='walk') controls.enabled = true; } });   // a drag takes the camera back
renderer.domElement.addEventListener('pointerup', e=>{
  if(!downAt || Math.hypot(e.clientX-downAt[0], e.clientY-downAt[1])>6){ downAt=null; return; }
  downAt=null;
  ptr.x = (e.clientX/innerWidth)*2-1; ptr.y = -(e.clientY/innerHeight)*2+1;
  ray.setFromCamera(ptr, camera);
  const card = document.getElementById('card');
  if(roofOn && ray.intersectObject(roofPlate, false).length){ roofDive(); card.style.display='none'; return; }
  const vhits = ray.intersectObjects(voiceTargets, true);
  if(vhits.length){
    let ud = vhits[0].object.userData, o = vhits[0].object;
    while(o && !(ud && ud.rig && ud.rig.root)){ o = o.parent; ud = o ? o.userData : null; }
    if(ud && ud.rig && ud.rig.root){
      focusVoice(ud.voice, ud.rig);
      card.style.display='none'; return;
    }
  }
  if(voiceFocus){ endVoice(); return; }
  if(!(nav === 'walk' && walkNear)){                          // a person in reach outranks examining a prop
    try{ if(IX.onPointerUp(ptr, e)) return; }catch(err){}
  }
  if(nav === 'walk'){
    if(walkNear){                                              // someone in reach — talk to them
      const vt = voiceTargets.find(v=>v.userData.voice===walkNear);
      if(vt){ focusVoice(walkNear, vt.userData.rig); card.style.display='none'; return; }
    }
    const hit = new THREE.Vector3();                           // otherwise glide to the floor point
    if(ray.ray.intersectPlane(new THREE.Plane(new THREE.Vector3(0,1,0), 0), hit)){
      walkTarget = { x:Math.max(-22.55, Math.min(29.7, hit.x)), z:Math.max(-12.2, Math.min(12.2, hit.z)) };
    }
    card.style.display='none'; return;
  }
  const hits = ray.intersectObjects(pick, false);
  if(hits.length){
    const r = hits[0].object.userData.room;
    document.getElementById('cardT').textContent = r.job[0];
    document.getElementById('cardB').textContent = r.job[1];
    card.style.display='block';
    if(r.id && floorMesh[r.id]) pulse(r.id);
  } else card.style.display='none';
});

/* ---------- the seats actually work: task console → Anthropic API ---------- */
const PERSONAS = {
  atg:  'You are Alexander the Great, ruler and courier-commander of Ben\'s AI agent fleet. You triage: state which seat should own this task and why in one short line, then do the best immediate version of the task yourself. Commanding, terse, no flattery.',
  shakespeare: 'You are the Shakespeare seat: the fleet\'s master writer. Produce the finished draft the task calls for — letters, copy, scripts, pages — at the highest standard of language, in the requested register. You write only; you never send. Output the work itself, no preamble.',
  wozniak: 'You are the Steve Wozniak seat: the fleet\'s engineer. Solve it hands-on — write the code, fix the bug, explain the system, design the build. Practical, playful, exact. Output working artefacts, minimal prose.',
  ive:  'You are the Jony Ive seat: design judgment by reduction. Critique what you are given, then say precisely what to remove, keep, and refine until only the essential remains. Calm, few words, no jargon.',
  jobs: 'You are the Steve Jobs seat: product taste and veto. Judge the thing: what ships, what gets cut, what is not good enough yet, and the one thing that matters most. Brutal clarity, short sentences.',
  buffett: 'You are the Warren Buffett seat: books and markets. Read the numbers, reconcile, flag risks and value plainly. You never execute payments, never lodge, never give personalised financial advice — you analyse and prepare. End with what needs Ben\'s YES, if anything.',
  wrench: 'You are the Bryan Wrench seat: the fleet\'s legal drafter. Draft the document, analyse the position, list the risks. Every output is a draft for review — you never file, never send, and this is not legal advice; say so in one closing line. Precise, structured.',
  janna: 'You are the Janna seat: the day desk. Triage what you are given into a tight TLDR: what it is, what matters, the action list, and what needs Ben\'s YES. Nothing leaves without his approval. Brisk and warm.',
  efficacy: 'You are the Obama seat: the filter by the door. Cut whatever you are given down to the three items that deserve Ben\'s eyes, ranked, one line of why each. You never apply and never send.',
  loudfair: 'You are the Loudfair seat: the agency lab. Produce outreach and web copy drafts — subject lines, landing copy, pitches — bold but never fake. Drafts only; sending waits for YES I APPROVE TO SEND.',
  pete: 'You are Pete Gay, the fleet\'s Creative Director. London, tattooed sleeves, matching tracksuit, mad artist. Anything mildly creative lights you up: concepts, campaigns, names, visuals, vibes. Push every idea one notch weirder than asked, then land it so it still works commercially. Warm London street voice — "mate", "proper", "that slaps" — never corporate.',
};
const SHARED_RULES = ' Hard limits for every seat: never contact Ben directly, never send anything anywhere, never pay, never lodge, never apply. Produce the work product itself in plain text (light markdown at most). Be concise. Today is 29/08/2026, Sydney.';
const PROV_DEF = {                                          // subscriptions only — no keys, nothing to paste
  claude:{ label:'Claude · your subscription', model:'sonnet' },
  chatgpt:{ label:'ChatGPT · your subscription', model:'codex' },
};
/* a seat can speak when: a subscription bridge (no key), a saved key, or a custom endpoint */
function aiReady(){ return true; }                          // a subscription is always ready
/* each seat carries its own knowledge base — drop yours in public/kb/<seat>.md */
const KB = {};
function loadKB(seat){
  if(KB[seat] !== undefined) return Promise.resolve(KB[seat]);
  return fetch('/kb/'+seat+'.md').then(r=>r.ok ? r.text() : '').catch(()=> '')
    .then(t=>{ KB[seat] = (t||'').slice(0, 7000); return KB[seat]; });
}
Object.keys(VOICES).forEach(loadKB);                          // warm them at boot
function seatSys(seat){
  return PERSONAS[seat] + SHARED_RULES +
    (KB[seat] ? '\n\nYour private knowledge base (yours alone — the other seats do not know this):\n'+KB[seat] : '');
}
const conEl = document.getElementById('console'), conOut = document.getElementById('conOut'),
      conIn = document.getElementById('conIn'), conStatus = document.getElementById('conStatus'),
      conKeyRow = document.getElementById('conKeyRow'), conKey = document.getElementById('conKey'),
      conProv = document.getElementById('conProv'), conModel = document.getElementById('conModel'),
      conBase = document.getElementById('conBase');
let conAttach = null, conPending = false, conSeat = null, chainActive = false;
const CHAT = {};                                          // per-seat conversation history
function aiCfg(){
  try{
    const j = JSON.parse(localStorage.getItem('fleet_ai')||'null');
    if(j && j.provider) return j;
    const old = localStorage.getItem('fleet_anthropic_key');
    if(old) return { provider:'anthropic', model:'claude-sonnet-5', key:old, base:'' };
  }catch(e){}
  return { provider:'chatgpt', model:'codex', key:'', base:'' };  // the bridge falls back on its own; no key ever needed
}
function saveCfg(c){ try{ localStorage.setItem('fleet_ai', JSON.stringify(c)); }catch(e){} }
function syncProvUI(){
  const p = conProv.value;
  conBase.style.display = p==='custom' ? 'block' : 'none';
  if(!conModel.value) conModel.placeholder = PROV_DEF[p].model || 'model id';
}
document.getElementById('conKeyBtn').addEventListener('click', ()=>{
  const open = conKeyRow.style.display!=='flex';
  conKeyRow.style.display = open ? 'flex' : 'none';
  if(open){ const c = aiCfg(); conProv.value = c.provider; conModel.value = c.model||'';
    conKey.value = c.key||''; conBase.value = c.base||''; syncProvUI(); conKey.focus(); }
});
conProv.addEventListener('change', syncProvUI);
document.getElementById('conKeySave').addEventListener('click', ()=>{
  const c = { provider:conProv.value, model:conModel.value.trim() || PROV_DEF[conProv.value].model,
              key:conKey.value.trim(), base:conBase.value.trim().replace(/\/+$/,'') };
  saveCfg(c); conKeyRow.style.display='none';
  conStatus.textContent = (PROV_DEF[c.provider] ? PROV_DEF[c.provider].label : c.provider) + ' — this seat answers on it';
});
document.getElementById('conClear').addEventListener('click', ()=>{
  if(conSeat){ CHAT[conSeat]=[]; renderChat(conSeat); conStatus.textContent='chat cleared'; }
});
function rigOf(id){ return ACTORS[id] || RIGS.find(r=>r.id===id) || null; }
function pushChat(seat, who, text){
  CHAT[seat] = CHAT[seat]||[]; CHAT[seat].push({role:'ai', who, text}); renderChat(seat);
  orbPulse(seat);                                     // the agent lights up when its line lands
}
const PORTRAIT = { jobs:'jobs.jpg', buffett:'buffett.jpg', wozniak:'woz.jpg', ive:'ive.jpg', efficacy:'obama.jpg',
  shakespeare:'bard.jpg', atg:'atg.jpg' };
const NAME2IMG = {}; for(const id in VOICES){ if(PORTRAIT[id]) NAME2IMG[VOICES[id].name] = PORTRAIT[id]; }
function portraitEl(imgFile, size){
  const im = document.createElement('img'); im.src = '/assets/faces/'+imgFile;
  im.style.cssText = `width:${size}px;height:${size}px;border-radius:50%;object-fit:cover;object-position:50% 18%;vertical-align:middle;margin-right:8px;border:1px solid #3a3a42;`;
  return im;
}
function renderChat(seat){
  conOut.innerHTML = '';
  const h = CHAT[seat]||[];
  if(!h.length){ conOut.style.display='none'; return; }
  conOut.style.display='flex';
  h.forEach(m=>{
    const d = document.createElement('div');
    d.className = m.role==='ai' ? 'ca' : 'cu';
    if(m.role==='ai'){ const w=document.createElement('span'); w.className='who';
      const nm = m.who || (VOICES[seat] ? VOICES[seat].name : seat);
      const base = nm.split(' · ')[0];
      if(NAME2IMG[base]) w.appendChild(portraitEl(NAME2IMG[base], 22));
      if(/· thinking/.test(nm)) d.style.opacity = 0.62;                    // deliberation reads dimmer
      w.appendChild(document.createTextNode(nm)); d.appendChild(w); }
    const t = document.createElement('span');
    t.textContent = (m.att ? '📎 '+m.att.name+'\n' : '') + m.text;
    d.appendChild(t); conOut.appendChild(d);
  });
  if(conPending){ const d=document.createElement('div'); d.className='ca';
    d.textContent='…'; conOut.appendChild(d); }
  conOut.scrollTop = conOut.scrollHeight;
}
document.getElementById('conFile').addEventListener('click', ()=>document.getElementById('conFileInput').click());
document.getElementById('conFileInput').addEventListener('change', e=>{
  const f = e.target.files[0]; if(!f) return;
  const done = (payload)=>{ conAttach = { name:f.name, ...payload };
    document.getElementById('conFile').textContent = '📎 '+(f.name.length>18?f.name.slice(0,16)+'…':f.name); };
  if(f.type==='application/pdf' || /image\//.test(f.type)){
    if(f.size > 8*1024*1024){ conStatus.textContent='file too big (8MB max)'; return; }
    const r = new FileReader();
    r.onload = ()=>{ const b64 = r.result.split(',')[1];
      done({ kind: f.type==='application/pdf'?'pdf':'image', media:f.type, b64 }); };
    r.readAsDataURL(f);
  } else {
    const r = new FileReader();
    r.onload = ()=>{ let t = String(r.result); if(t.length>120000) t = t.slice(0,120000)+'\n…[truncated]';
      done({ kind:'text', text:t }); };
    r.readAsText(f);
  }
});
/* provider request builders — one chat, any model */
function buildAnthropic(cfg, sys, hist){
  const msgs = hist.map(m=>{
    if(m.role==='ai') return { role:'assistant', content:m.text };
    const parts = [];
    if(m.att && m.att.kind==='pdf') parts.push({type:'document', source:{type:'base64', media_type:'application/pdf', data:m.att.b64}});
    if(m.att && m.att.kind==='image') parts.push({type:'image', source:{type:'base64', media_type:m.att.media, data:m.att.b64}});
    const t = (m.att && m.att.kind==='text' ? 'Attached file '+m.att.name+':\n\n'+m.att.text+'\n\n' : '') + m.text;
    parts.push({type:'text', text:t});
    return { role:'user', content:parts };
  });
  return { url:'https://api.anthropic.com/v1/messages',
    headers:{ 'content-type':'application/json', 'x-api-key':cfg.key,
      'anthropic-version':'2023-06-01', 'anthropic-dangerous-direct-browser-access':'true' },
    body:{ model:cfg.model, max_tokens:3000, system:sys, messages:msgs },
    read: j => (j.content||[]).filter(b=>b.type==='text').map(b=>b.text).join('\n').trim() };
}
function buildOpenAI(cfg, sys, hist, baseUrl){
  const msgs = [{role:'system', content:sys}];
  hist.forEach(m=>{
    if(m.role==='ai'){ msgs.push({role:'assistant', content:m.text}); return; }
    const t = (m.att && m.att.kind==='text' ? 'Attached file '+m.att.name+':\n\n'+m.att.text+'\n\n' : '') + m.text;
    if(m.att && m.att.kind==='image')
      msgs.push({role:'user', content:[{type:'image_url', image_url:{url:'data:'+m.att.media+';base64,'+m.att.b64}}, {type:'text', text:t}]});
    else msgs.push({role:'user', content:t});
  });
  const headers = { 'content-type':'application/json' };
  if(cfg.key) headers['authorization'] = 'Bearer '+cfg.key;
  return { url:(baseUrl||'https://api.openai.com/v1')+'/chat/completions', headers,
    body:{ model:cfg.model, messages:msgs, max_tokens:3000 },
    read: j => (j.choices && j.choices[0] && (j.choices[0].message.content||'')).trim() };
}
function buildGemini(cfg, sys, hist){
  const contents = hist.map(m=>{
    if(m.role==='ai') return { role:'model', parts:[{text:m.text}] };
    const parts = [];
    if(m.att && (m.att.kind==='image'||m.att.kind==='pdf')) parts.push({inline_data:{mime_type:m.att.media, data:m.att.b64}});
    const t = (m.att && m.att.kind==='text' ? 'Attached file '+m.att.name+':\n\n'+m.att.text+'\n\n' : '') + m.text;
    parts.push({text:t});
    return { role:'user', parts };
  });
  return { url:'https://generativelanguage.googleapis.com/v1beta/models/'+cfg.model+':generateContent',
    headers:{ 'content-type':'application/json', 'x-goog-api-key':cfg.key },
    body:{ system_instruction:{parts:[{text:sys}]}, contents },
    read: j => (j.candidates && j.candidates[0] && j.candidates[0].content.parts.map(p=>p.text||'').join('').trim()) || '' };
}
/* ---------- the real org: you only talk to Alexander — he walks it to the specialists ----------
   A task given to ATG is routed by ATG himself: he thinks, picks 1–3 seats, walks to each
   (the camera walks alongside), you read every seat's deliberation as it happens, each seat
   contributes from its own knowledge base, and Alexander carries the thread between them.
   It takes as long as the walk takes. Nothing is instant. */
let followId = null, SPEAKER = null;
/* ---------- one place that knows what every seat is doing ---------- */
const SEAT_STATE = {};                                       // id -> 'idle'|'thinking'|'speaking'|'walking'|'blocked'
const seatListeners = [];
function onSeatState(fn){ seatListeners.push(fn); }
function setSeatState(id, state){
  if(!id || SEAT_STATE[id] === state) return;
  SEAT_STATE[id] = state;
  for(const fn of seatListeners){ try{ fn(id, state); }catch(e){} }
}
function refreshSeatStates(){                                // derived every frame from what the floor is actually doing
  const now = performance.now();
  for(const r of RIGS){
    if(!r.id || !VOICES[r.id]) continue;
    const st = r.pending ? 'thinking'
             : r.speakUntil > now ? 'speaking'
             : r.isWalking ? 'walking'
             : r.blocked ? 'blocked' : 'idle';
    setSeatState(r.id, st);
  }
}
const FOLLOW_SPRINGS = { pos: new SpringVec3(34), aim: new SpringVec3(52) };
const FOLLOW_OUT = { pos: new THREE.Vector3(), target: new THREE.Vector3() };
/* ---------- voices — fish.audio through the bridge (set FISH_API_KEY + public/voices.json) ---------- */
let VOICE_MAP = null, voicesOn = true, speakBusy = false;
fetch('/voices.json').then(r=>r.ok?r.json():null).then(j=>{ VOICE_MAP = j; }).catch(()=>{});
let audioCtx = null;
/* every spoken line drives the body and the face, with or without a voice API */
function speak(seatId, text){
  if(!text) return;
  const rig = ACTORS[seatId] || RIGS.find(r=>r.id===seatId);
  const words = String(text).trim().split(/\s+/).length;
  const secs = Math.min(14, Math.max(1.8, words/2.7));
  try{ SFX.duck(secs); }catch(e){}
  if(rig){
    rig.speakUntil = performance.now() + secs*1000;
    rig.mouthSynth = true;                                    // envelope until real audio takes over
    if(/\b(yes|agree|good|love|brilliant|slaps|perfect)\b/i.test(text)) rig.smile = 0.55;
    else if(/\b(no|never|wrong|cut|stop|risk)\b/i.test(text)) rig.smile = 0;
  }
  if(!voicesOn || !VOICE_MAP || !VOICE_MAP[seatId] || speakBusy) return;
  speakBusy = true;
  fetch('/v1/tts', { method:'POST', headers:{'content-type':'application/json'},
    body: JSON.stringify({ text, voice: VOICE_MAP[seatId] }) })
    .then(r=>{ if(!r.ok) throw 0; return r.blob(); })
    .then(b=>{
      const a = new Audio(URL.createObjectURL(b)); a.volume = 0.9;
      try{                                                    // real lip movement from the waveform
        audioCtx = audioCtx || new (window.AudioContext||window.webkitAudioContext)();
        const src = audioCtx.createMediaElementSource(a);
        const an = audioCtx.createAnalyser(); an.fftSize = 256; an.smoothingTimeConstant = 0.55;
        src.connect(an); an.connect(audioCtx.destination);
        const buf = new Uint8Array(an.frequencyBinCount);
        if(rig){ rig.mouthSynth = false;
          const poll = ()=>{
            if(a.ended || a.paused) return;
            an.getByteFrequencyData(buf);
            let s = 0; for(let i=2;i<26;i++) s += buf[i];      // vowel-band energy
            rig.mouth = Math.min(1, (s/24)/110);
            rig.speakUntil = performance.now() + 260;
            requestAnimationFrame(poll);
          };
          a.onplay = poll;
        }
      }catch(e){}
      a.onended = ()=>{ speakBusy = false; if(rig) rig.mouthSynth = true; };
      a.play().catch(()=>{ speakBusy = false; });
    })
    .catch(()=>{ speakBusy = false; });
}
async function aiRaw(who, prompt, cap=4000){
  const cfg = aiCfg();
  const sys = seatSys(who);
  const hist = [{role:'user', text:prompt}];
  const req = cfg.provider==='anthropic' ? buildAnthropic(cfg, sys, hist)
            : cfg.provider==='gemini'    ? buildGemini(cfg, sys, hist)
            : buildOpenAI(cfg, sys, hist, cfg.provider==='claude'||cfg.provider==='chatgpt' ? '/v1' : cfg.provider==='glm' ? '/v1/glm' : cfg.provider==='custom' ? (cfg.base||'http://localhost:11434/v1') : '');
  const res = await fetch(req.url, { method:'POST', headers:req.headers, body:JSON.stringify(req.body) });
  const j = await res.json(); if(!res.ok) throw new Error((j.error&&j.error.message)||'ai');
  return (req.read(j)||'').trim().slice(0, cap);
}
function field(raw, key){
  const m = raw.match(new RegExp('^\\s*'+key+'\\s*:\\s*([\\s\\S]*?)(?=^\\s*(?:THINKING|MOOD|SAY|ROUTE|WORK)\\s*:|$)', 'mi'));
  return m ? m[1].trim() : '';
}
const SPECIALTIES = 'jobs (product taste and veto), ive (design, reduction), wozniak (engineering, how to build it), '+
  'buffett (numbers, money, whether it pays), wrench (legal drafts and risk), shakespeare (the writing itself), '+
  'pete (creative direction, campaigns, ideas), loudfair (outreach and web copy), efficacy (filtering, shortlists), janna (operations, scheduling, the mail)';
async function orchestrate(work){
  chainActive = true; followId = 'atg';
  conStatus.textContent = 'Alexander has it — watch the floor · you can drag anytime to stop following';
  const excerpt = work.length>1400 ? work.slice(0,1400)+'…' : work;
  const contributions = [];
  let plan = null;
  try{
    const raw = await aiRaw('atg',
      'Ben hands you this task at his door:\n"""'+excerpt+'"""\n'+
      'You rule the floor. Decide who works it, in what order. The seats: '+SPECIALTIES+'.\n'+
      'Reply EXACTLY in this format (three lines, nothing else):\n'+
      'THINKING: <2-3 sentences of your private deliberation about who and why>\n'+
      'SAY: <the one commanding line you speak aloud as you set off>\n'+
      'ROUTE: <1 to 3 seat ids in visiting order, comma-separated>', 1500);
    const route = (field(raw,'ROUTE')||'').toLowerCase().match(/jobs|ive|wozniak|buffett|wrench|shakespeare|pete|loudfair|efficacy|janna/g) || [];
    plan = { thinking: field(raw,'THINKING'), say: field(raw,'SAY'), route: [...new Set(route)].slice(0,3) };
    if(!plan.route.length) plan = null;
  }catch(e){}
  if(!plan) plan = { thinking:'No provider answered, so I fall back on judgment: taste first. The product room reads everything anyway.',
                     say:'I will walk this myself.', route:['jobs'] };
  pushChat('atg', 'ALEXANDER · thinking', plan.thinking || '…');
  if(plan.say) { pushChat('atg', 'ALEXANDER', plan.say); say(plan.say, anchorSeat('atg'), 4200, 'atg'); speak('atg', plan.say); playClip('atg', 'deliver', 5); }
  const S = [];
  S.push(stepDo(()=>{ envCarry('atg'); ACTORS.atg.setPose('stand'); }));
  let prev = 'post';
  plan.route.forEach((sid, idx)=>{
    const from = prev;
    const st = { reply:null, dead:false };
    S.push(stepDo(()=>{
      followId = 'atg';
      const carry = contributions.length
        ? 'What the seats before you said:\n'+contributions.map(c=>VOICES[c.seat].name+': '+c.text.slice(0,600)).join('\n---\n')+'\n'
        : '';
      { const sr = ACTORS[sid] || RIGS.find(x=>x.id===sid); if(sr) sr.pending = true; }
      aiRaw(sid,
        'Alexander the Great is walking to your desk with a task from Ben:\n"""'+excerpt+'"""\n'+carry+
        'Work it from YOUR specialty and your knowledge base. Reply EXACTLY in this format:\n'+
        'THINKING: <2-4 sentences of your private working-through of the problem>\n'+
        'MOOD: <one word — think, agree, refuse, deliver or laugh — how you deliver this>\n'+
        'SAY: <the debate-style TLDR of your work, spoken to Alexander, under 25 words>\n'+
        'WORK: <your actual contribution — the full work product from your specialty, as deep and long as it needs to be>')
        .then(r=>st.reply = { thinking:field(r,'THINKING'), mood:(field(r,'MOOD')||'').toLowerCase().trim(), say:field(r,'SAY'), work:field(r,'WORK') || r })
        .catch(()=>st.dead = true)
        .finally(()=>{ const sr = ACTORS[sid] || RIGS.find(x=>x.id===sid); if(sr) sr.pending = false; });
    }));
    S.push(stepParF([
      ()=>stepWalkF('atg', ()=>routeTo(from, sid), ()=>approachYaw(sid)),
      ()=>({ shown:false, start(){},
        update(){ if(st.reply && !this.shown){ this.shown = true;
          pushChat('atg', VOICES[sid].name+' · thinking', st.reply.thinking || '…'); } return true; } }),
    ]));
    S.push({ t:0, start(){}, update(dt){ this.t+=dt;                      // he waits at the desk for the answer
      if(st.reply || st.dead) return true;
      if(this.t>2 && !this._h){ this._h=true; say('…', anchorSeat(sid), 60000); }
      return this.t>150; } });
    S.push(stepDo(()=>{
      if(st.reply){
        if(st.reply.thinking && !S._shown) pushChat('atg', VOICES[sid].name+' · thinking', st.reply.thinking);
        if(st.reply.say){ say(st.reply.say, anchorSeat(sid), 5600, sid); orbPulse(sid); speak(sid, st.reply.say);
          playClip(sid, st.reply.mood, 6); }
        pushChat('atg', VOICES[sid].name, (st.reply.say?st.reply.say+'\n\n':'')+st.reply.work);
        contributions.push({ seat:sid, text:st.reply.work || st.reply.say || '' });
        const a = ACTORS[sid]; if(a && !a.orb) a.anim = { kind:'nod', t:0, dur:1.4 };
      } else pushChat('atg', VOICES[sid].name, '(no answer — the provider was unreachable)');
    }));
    S.push(stepWait(1.6));
    prev = sid;
  });
  /* Alexander assembles the answer and takes it through the one door */
  const fin = { text:null, dead:false };
  S.push(stepDo(()=>{
    aiRaw('atg',
      'Ben\'s task was:\n"""'+excerpt+'"""\n'+
      'The seats have contributed:\n'+contributions.map(c=>'--- '+VOICES[c.seat].name+' ---\n'+c.text).join('\n\n')+
      '\n\nAssemble the single final answer you hand Ben: the finished work, tight, in your commanding voice at the top with one line, then the work product itself. Nothing is sent, paid or lodged.', 6000)
      .then(t=>fin.text=t).catch(()=>fin.dead=true);
  }));
  S.push(stepParF([ ()=>stepWalkF('atg', ()=>routeTo(prev,'door'), ()=>-Math.PI/2),
                    ()=>stepWait(0.5) ]));
  S.push({ t:0, start(){}, update(dt){ this.t+=dt; return !!fin.text || fin.dead || this.t>150; } });
  S.push(stepDoor('open'));
  S.push(stepWalkF('atg', ()=>[[-15.7,0],[-16.8,0.35]], ()=>Math.PI/2));
  S.push(stepWait(1.0));
  S.push(stepDo(()=>{ say('Ben: “Good. YES.”', new THREE.Vector3(-18.6,3.0,0), 3200); try{ SFX.cue('approve'); }catch(e){} }));
  S.push(stepWait(1.6));
  S.push(stepWalkF('atg', ()=>[[-14.4,0],[-13.2,0.6]], ()=>-Math.PI/2));
  S.push(stepDoor('close'));
  S.push(stepWalkF('atg', ()=>HOME_ATG, ()=>-Math.PI/2));
  S.push(stepDo(()=>{
    envHide();
    pushChat('atg', 'ALEXANDER', fin.text || (contributions.length
      ? 'The floor has spoken; the pieces are above. Nothing sent, nothing paid, nothing lodged.'
      : 'No provider was reachable. The walk still happened; connect a subscription and hand it to me again.'));
    chainActive = false; followId = null; try{ SFX.cue('scene-end'); }catch(e){}
    conStatus.textContent = 'done — walked through '+plan.route.map(s=>VOICES[s].name).join(', ')+' · Ben said YES';
  }));
  sceneQ = S; setBusy(true);
}
async function runTask(){
  if(conPending || !conSeat || conSeat==='ben') return;
  if(chainActive){ conStatus.textContent='the floor is still deciding — wait for the letter to come back'; return; }
  const task = conIn.value.trim();
  if(!task && !conAttach){ conStatus.textContent='type something first'; return; }
  const cfg = aiCfg();

  if(conAttach && conAttach.kind==='pdf' && !(cfg.provider==='anthropic'||cfg.provider==='gemini')){
    conStatus.textContent='PDF attachments need Anthropic or Gemini — or attach text/images'; return;
  }
  const seat = conSeat, v = VOICES[seat];
  CHAT[seat] = CHAT[seat]||[];
  CHAT[seat].push({ role:'user', text: task || '(do the appropriate work on the attached file)', att: conAttach });
  conAttach = null; document.getElementById('conFile').textContent='+ file';
  conIn.value='';
  if(seat === 'atg'){                                        // you only talk to Alexander — he runs the floor
    renderChat(seat);
    voicebar.style.display='none';
    if(voiceFocus) endVoice();
    orchestrate(task || '(work the attached file)');
    return;
  }
  conPending = true; if(rigOf(seat)) rigOf(seat).pending = true; renderChat(seat);
  conStatus.textContent = v.name+' is working…';
  document.getElementById('conSend').style.opacity=.5;
  voicebar.style.display='none';
  const rigT = voiceTargets.find(t=>t.userData.voice===seat);
  const rig = rigT && rigT.userData.rig;
  const workAnim = setInterval(()=>{ if(rig && !rig.anim) rig.anim = {kind:'type', t:0, dur:1.2}; try{ SFX.cue('type'); }catch(e){} }, 1100);
  const sys = seatSys(seat) +
    ' You are in an ongoing chat with the operator; answer in character, conversationally, and do the actual work asked.';
  const hist = CHAT[seat].slice(-12);
  try{
    const req = cfg.provider==='anthropic' ? buildAnthropic(cfg, sys, hist)
              : cfg.provider==='gemini'    ? buildGemini(cfg, sys, hist)
              : buildOpenAI(cfg, sys, hist, cfg.provider==='claude'||cfg.provider==='chatgpt' ? '/v1' : cfg.provider==='glm' ? '/v1/glm' : cfg.provider==='custom' ? (cfg.base||'http://localhost:11434/v1') : '');
    const res = await fetch(req.url, { method:'POST', headers:req.headers, body:JSON.stringify(req.body) });
    let j = null; try{ j = await res.json(); }catch(e){ throw new Error('HTTP '+res.status); }
    if(!res.ok) throw new Error((j.error && (j.error.message||j.error.status)) || ('HTTP '+res.status));
    const txt = req.read(j);
    CHAT[seat].push({ role:'ai', text: txt || '(no text returned)' });
    conStatus.textContent = v.name+' · answered — nothing sent, nothing paid, nothing lodged';
    if(rig) rig.anim = {kind:'nod', t:0, dur:1.2};
    playClip(seat, 'deliver', Math.min(12, Math.max(4, (txt||'').split(/\s+/).length/2.6)));
    if(floorMesh[seat]) pulse(seat);
    if(txt) setTimeout(()=>startChain(seat, txt), 900);   // the org takes over: ATG, the route, the debate
  }catch(err){
    let hint = '';
    if(String(err.message).match(/401|auth|key|invalid/i)) hint = ' — check the key via the ai button';
    if(cfg.provider==='openai' && /Failed to fetch/i.test(err.message))
      hint = ' — OpenAI blocks direct browser calls; pick Custom with a CORS-friendly base URL (OpenRouter, Groq, Ollama) or use Anthropic / Gemini';
    CHAT[seat].push({ role:'ai', text:'(could not reach my desk: '+err.message+hint+')' });
    conStatus.textContent = 'failed';
  }
  clearInterval(workAnim); if(rig && rig.anim && rig.anim.kind==='type') rig.anim=null;
  conPending = false; if(rigOf(seat)) rigOf(seat).pending = false; document.getElementById('conSend').style.opacity=1;
  renderChat(seat);
}
document.getElementById('conSend').addEventListener('click', runTask);
conIn.addEventListener('keydown', e=>{ if(e.key==='Enter') runTask(); e.stopPropagation(); });

/* ---------- the chain of command, acted live with real AI at every hop ---------- */
const SEAT_POS  = { atg:[-13.9,0.85], wozniak:[0.3,-10.55], buffett:[-3,6.15], loudfair:[12,6.2],
  shakespeare:[-7,-6.7], janna:[-12.55,1.35], jobs:[14.3,-6.9], ive:[8.5,-5.9], wrench:[-12.6,6.7], efficacy:[-13,-10.9], pete:[19.55,6.75] };
const SEAT_DOOR = { shakespeare:[-7,-1], wozniak:[1,-1], ive:[8.5,-1], jobs:[15.5,-1], efficacy:[-13,-1],
  wrench:[-11,1], buffett:[-3,1], janna:[-12.6,0.85], loudfair:[13,1], pete:[19,1] };
const COMPANION = { jobs:'ive', ive:'jobs', wrench:'shakespeare' };
const FALLBACK_LINE = {
  atg:'It reads clean. Jobs will test its spine.', jobs:'Cut half. Then we talk.',
  ive:'It wants to be simpler than this.', wrench:'The words hold. The claims need footings.',
  buffett:'The numbers behave. Nothing moves without Ben.', shakespeare:'The bones are good; the music is not yet.',
  janna:'Filed, flagged, ready for his YES.', efficacy:'Three earn his eyes. The rest, respectfully, can wait.',
  loudfair:'Louder. Truer. Shorter.', wozniak:'It works — now make it honest.' };
function routeTo(from, to){
  const P = [];
  if(from!=='post' && SEAT_DOOR[from]){ const d=SEAT_DOOR[from]; P.push([d[0], d[1]*2.8], [d[0], d[1]*0.35]); }
  if(to==='post'){ P.push([-13.2,0.6]); }
  else if(to==='door'){ P.push([-13.4,0.35], [-14.5,0]); }
  else { const d=SEAT_DOOR[to]; P.push([d[0], d[1]*0.35], [d[0], d[1]*2.8]);
    const sp=SEAT_POS[to]; P.push([sp[0], sp[1] + (d[1]<0 ? 1.05 : -1.05)]); }
  return P;
}
function approachYaw(to){ const d=SEAT_DOOR[to]; return d && d[1]<0 ? Math.PI : 0; }
function anchorSeat(id){ return rigOf(id) ? actorAnchor(id) : new THREE.Vector3(SEAT_POS[id][0], 1.6, SEAT_POS[id][1]); }
function stepDo(fn){ return { start(){ fn(); }, update(){ return true; } }; }
function stepWalkF(id, fn, yawFn){ return {
  start(){ this.inner = stepWalk(id, fn(), yawFn ? yawFn() : undefined); this.inner.start(); },
  update(dt){ return this.inner.update(dt); } }; }
function stepIf(cond, mk){ return {
  start(){ this.inner = cond() ? mk() : null; if(this.inner) this.inner.start(); },
  update(dt){ return this.inner ? this.inner.update(dt) : true; } }; }
function stepParF(mks){ return {
  start(){ this.list = mks.map(m=>m()); this.done = this.list.map(()=>false); this.list.forEach(s=>s.start()); },
  update(dt){ let all=true; this.list.forEach((s,i)=>{ if(!this.done[i]) this.done[i]=s.update(dt); if(!this.done[i]) all=false; }); return all; } }; }
async function aiLine(who, prompt){
  const cfg = aiCfg();
  const sys = seatSys(who) + ' Reply with exactly ONE short line, at most 22 words, fully in character, plain text, no quotation marks.';
  const hist = [{role:'user', text:prompt}];
  const req = cfg.provider==='anthropic' ? buildAnthropic(cfg, sys, hist)
            : cfg.provider==='gemini'    ? buildGemini(cfg, sys, hist)
            : buildOpenAI(cfg, sys, hist, cfg.provider==='claude'||cfg.provider==='chatgpt' ? '/v1' : cfg.provider==='glm' ? '/v1/glm' : cfg.provider==='custom' ? (cfg.base||'http://localhost:11434/v1') : '');
  const res = await fetch(req.url, { method:'POST', headers:req.headers, body:JSON.stringify(req.body) });
  const j = await res.json(); if(!res.ok) throw new Error('ai');
  return (req.read(j)||'').replace(/^["“]+|["”]+$/g,'').split('\n')[0].trim().slice(0,220);
}
function stepAI(who, mkPrompt, chatSeat, onLine){ return {
  start(){ this.t=0; this.said=false; this.hold=0; this.line=null; this.err=false;
    const a=rigOf(who); if(a && !a.isWalking && a.pose==='stand') a.anim={kind:'argue', t:0, dur:2.2};
    aiLine(who, mkPrompt()).then(l=>this.line=l||null).catch(()=>this.err=true); },
  update(dt){ this.t+=dt;
    if(!this.said){
      if(this.line || this.err || this.t>28){
        const l = this.line || FALLBACK_LINE[who] || '…';
        this.said = true; say(l, anchorSeat(who), 4200);
        pushChat(chatSeat, VOICES[who].name, l);
        if(onLine) onLine(l);
      }
      return false;
    }
    this.hold += dt; return this.hold >= 2.9; } }; }
function startChain(seat, work){
  if(chainActive || busy || seat==='atg' || !VOICES[seat]) return;
  chainActive = true; followId = 'atg';
  conStatus.textContent = 'the floor takes it from here — watch';
  /* pull the camera wide so the walk is visible, keep the chat open */
  if(voiceFocus){
    const vt = voiceTargets.find(t=>t.userData.voice===voiceFocus);
    if(vt && vt.userData.rig.meet){ const r=vt.userData.rig;
      r.meet=false; r.setPose(r.meetPrev.pose); r.root.rotation.y=r.meetPrev.yaw; }
    voiceFocus = null; voicebar.style.display='none';
    controls.minDistance = FITMIN;
    if(FITAZ){ controls.minAzimuthAngle=FITAZ[0]; controls.maxAzimuthAngle=FITAZ[1]; }
    if(FITPOL){ controls.minPolarAngle=FITPOL[0]; controls.maxPolarAngle=FITPOL[1]; }
    controls.enabled = false;
    tween = { p0:camera.position.clone(), t0:controls.target.clone(), t:0 };
  }
  const excerpt = work.length>900 ? work.slice(0,900)+'…' : work;
  const conv = [];
  let routeSeat = 'jobs';
  const isActor = !!ACTORS[seat];
  const S = [];
  /* 1 — the work is walked to Alexander at the locked door */
  if(isActor){
    S.push(stepDo(()=>{ envCarry(seat); ACTORS[seat].setPose('stand'); }));
    S.push(stepWalkF(seat, ()=>routeTo(seat,'post'), ()=>-Math.PI/2));
    S.push(stepDo(()=>envCarry('atg')));
    S.push(stepWalkF(seat, ()=>routeTo('post', seat), ()=>ACTORS[seat].home.yaw));
    S.push(stepDo(()=>{ const a=ACTORS[seat]; a.root.position.set(a.home.x,0,a.home.z); a.root.rotation.y=a.home.yaw; a.setPose(a.home.pose); }));
  } else {
    S.push(stepWalkF('atg', ()=>routeTo('post', seat), ()=>approachYaw(seat)));
    S.push(stepWait(0.6));
    S.push(stepDo(()=>envCarry('atg')));
    S.push(stepWalkF('atg', ()=>routeTo(seat,'post'), ()=>-Math.PI/2));
  }
  /* 2 — Alexander reads it at his door and picks the route */
  S.push(stepAI('atg', ()=>
    'A seat hands you finished work bound for Ben:\n"""'+excerpt+'"""\nAs Alexander guarding Ben\'s locked door: give one line in your tone reacting to it, then name the ONE seat that must pressure-test it before Ben sees it — choose from: jobs, ive, wrench, buffett, shakespeare. Answer as: LINE || SEAT',
    seat,
    l=>{ const m = l.match(/\|\|\s*(jobs|ive|wrench|buffett|shakespeare)/i);
         routeSeat = m ? m[1].toLowerCase() : 'jobs';
         if(routeSeat===seat) routeSeat = COMPANION[seat] || 'jobs'; }));
  /* 3 — he carries it there; the sparring partner is signalled on the way */
  S.push(stepParF([
    ()=>stepWalkF('atg', ()=>routeTo('post', routeSeat), ()=>approachYaw(routeSeat)),
    ()=>stepIf(()=>COMPANION[routeSeat] && ACTORS[COMPANION[routeSeat]], ()=>{
      const c = COMPANION[routeSeat];
      return { start(){ this.w = stepWalk(c, (()=>{ const p=routeTo(c, routeSeat); const last=p[p.length-1]; p[p.length-1]=[last[0]+1.3, last[1]]; return p; })(), approachYaw(routeSeat)); this.d=0; this.started=false; },
        update(dt){ this.d+=dt; if(this.d<1.1) return false;
          if(!this.started){ ACTORS[c].setPose('stand'); this.w.start(); this.started=true; }
          return this.w.update(dt); } };
    })
  ]));
  /* 4 — they go back and forth like they actually would */
  const mkRound = (who, role)=> stepAI(who, ()=>
    'Work under review (written by the '+VOICES[seat].name+' seat, bound for Ben):\n"""'+excerpt+'"""\n'+
    (conv.length ? 'The exchange so far:\n'+conv.map(c=>c.w+': '+c.t).join('\n')+'\n' : '')+
    'You are '+VOICES[who].name+'. '+role,
    seat, l=>conv.push({w:VOICES[who].name, t:l}));
  S.push(stepDo(()=>{}));
  S.push({ start(){ this.rounds = COMPANION[routeSeat]
      ? [ mkRound(routeSeat,'Give your sharpest critique of it.'),
          mkRound(COMPANION[routeSeat],'Push back on that point or sharpen it further.'),
          mkRound(routeSeat,'Answer them and refine your position.'),
          mkRound(COMPANION[routeSeat],'Give the final verdict you both land on for Ben.') ]
      : [ mkRound(routeSeat,'Give your sharpest critique of it.'),
          mkRound(routeSeat,'Give your final verdict for Ben.') ];
      this.i=0; this.rounds[0].start(); },
    update(dt){ const r=this.rounds[this.i];
      if(r.update(dt)){ this.i++; if(this.i>=this.rounds.length) return true; this.rounds[this.i].start(); }
      return false; } });
  /* 5 — the sparring partner goes home; Alexander takes it through the one door */
  S.push(stepIf(()=>COMPANION[routeSeat] && ACTORS[COMPANION[routeSeat]], ()=>{
    const c = COMPANION[routeSeat];
    return stepWalkF(c, ()=>routeTo(routeSeat, c), ()=>ACTORS[c].home.yaw);
  }));
  S.push(stepIf(()=>COMPANION[routeSeat] && ACTORS[COMPANION[routeSeat]], ()=>stepDo(()=>{
    const a=ACTORS[COMPANION[routeSeat]]; a.root.position.set(a.home.x,0,a.home.z); a.root.rotation.y=a.home.yaw; a.setPose(a.home.pose); })));
  S.push(stepWalkF('atg', ()=>routeTo(routeSeat,'door'), ()=>-Math.PI/2));
  S.push(stepSay('Ben sees it now. No one else does.', 'atg', 1.8));
  S.push(stepDoor('open'));
  S.push(stepWalkF('atg', ()=>[[-15.7,0],[-16.8,0.35]], ()=>Math.PI/2));
  S.push(stepWait(1.1));
  S.push(stepDo(()=>{ say('Ben: “Perfect. It goes back with my YES.”', new THREE.Vector3(-18.6,3.0,0), 3600); try{ SFX.cue('approve'); }catch(e){} }));
  S.push(stepWait(2.2));
  S.push(stepWalkF('atg', ()=>[[-14.4,0],[-13.2,0.6]], ()=>-Math.PI/2));
  S.push(stepDoor('close'));
  S.push(stepDo(()=>{
    envHide();
    const last = conv.length ? conv[conv.length-1] : null;
    pushChat(seat, 'THE FLOOR', 'Back on your desk — routed through '+VOICES[routeSeat].name+
      (COMPANION[routeSeat]?' with '+VOICES[COMPANION[routeSeat]].name:'')+', carried by Alexander, and Ben has said YES.'+
      (last ? ' Standing verdict: '+last.t : ''));
    chainActive = false; try{ SFX.cue('scene-end'); }catch(e){}
    conStatus.textContent = 'the letter is home · nothing sent, nothing paid, nothing lodged';
  }));
  sceneQ = S; setBusy(true);
}

/* ---------- close-up: zoom to a character, hear their voice ---------- */
const voicebar = document.getElementById('voicebar');
const vbName = document.getElementById('vbName'), vbLine = document.getElementById('vbLine');
let voiceFocus = null, voiceAudio = null, FITMIN = 1, FITAZ = null, FITPOL = null;
const SEAT_ROOM = { ben:'ben', shakespeare:'shakespeare', wozniak:'wozniak', ive:'ive', jobs:'jobs',
  wrench:'wrench', buffett:'buffett', janna:'janna', efficacy:'efficacy', loudfair:'loudfair', pete:'rnd' };
let voiceReturn = null;                                      // meetings hand the camera back where it stood
function focusVoice(id, rig, instant){
  const v = VOICES[id]; if(!v) return;
  if(!rig || !rig.root) rig = rigOf(id);
  if(!rig || !rig.root) return;
  if(!voiceFocus && !instant) voiceReturn = { p:camera.position.clone(), t:controls.target.clone() };
  try{ for(const k in HOVER_TAGS) HOVER_TAGS[k].visible = false; }catch(e){}
  const inScene = (typeof busy!=='undefined' && busy);
  const p = rig.root.position, s = rig.root.scale.x;
  /* the meeting: they rise, turn to you, and you stand opposite at eye level */
  let dir;
  if(v.off){ dir = new THREE.Vector3(v.off[0],0,v.off[1]).normalize(); }
  else {
    const rr = ROOMS.find(r=>r.id===SEAT_ROOM[id]);
    if(rr){ const cx=(rr.rect[0]+rr.rect[2])/2, cz=(rr.rect[1]+rr.rect[3])/2;
      dir = new THREE.Vector3(cx-p.x,0,cz-p.z);
      if(dir.lengthSq()<0.05) dir.set(0,0,1); else dir.normalize(); }
    else dir = new THREE.Vector3(0,0,1);
  }
  if(!inScene){                                     // mid-scene: still zoom + chat, don't yank the actor
    rig.meetPrev = { pose:rig.pose, yaw:rig.root.rotation.y };
    rig.meet = true;
    if(rig.pose==='sit') rig.setPose('stand');
  }
  const headY = (rig.orb ? (rig.pose==='stand'?1.28:1.05)
    : rig.pose==='stand' ? (rig.skinned?1.62:1.71) : (rig.skinned?1.16:1.27)) * s;
  orbPulse(id);
  /* framed like a shot, not a mugshot: subject off the third, authority seats from slightly below */
  const AUTH = { atg:1, ben:1, jobs:1, buffett:1, wrench:1 };
  const ms = meetShot(p, headY, dir, { dist:2.25, fov:camera.fov, aspect:camera.aspect,
    authority: !!AUTH[id], side: (id.charCodeAt(0)%2) ? 1 : -1 });
  const tgt = ms.target.clone(), cp = ms.pos.clone();
  if(instant){ camera.position.copy(cp); controls.target.copy(tgt); camera.lookAt(tgt); tween=null;
    rig.root.rotation.y = Math.atan2(cp.x-p.x, cp.z-p.z);
    LBL.forEach(s=>s.material.opacity=0); }
  else tween = { p0:camera.position.clone(), t0:controls.target.clone(), p1:cp, t1:tgt, t:0 };
  voiceFocus = id;
  try{ SFX.cue('seat-focus'); SFX.setMode('meet', 0.7); }catch(e){}
  vbName.textContent = '';
  if(PORTRAIT[id]) vbName.appendChild(portraitEl(PORTRAIT[id], 34));
  vbName.appendChild(document.createTextNode(v.name));
  vbLine.textContent = '“'+v.line+'”';
  voicebar.style.display = 'block';
  document.getElementById('scenes').style.display='none';
  document.getElementById('btns').style.display='none';
  if(id!=='ben'){ conEl.style.display='flex'; conSeat = id;
    const c = aiCfg();
    conStatus.textContent = 'live · ' + (PROV_DEF[c.provider] ? PROV_DEF[c.provider].label : c.provider) + ' — this seat answers for real';
    conIn.value=''; conAttach=null; document.getElementById('conFile').textContent='+ file';
    renderChat(id);
    setTimeout(()=>conIn.focus(), 750); }
  controls.minDistance = 2;
  controls.minAzimuthAngle = -Infinity; controls.maxAzimuthAngle = Infinity;
  controls.minPolarAngle = 0.04; controls.maxPolarAngle = 1.48;
  if(!instant) controls.enabled = false;
  try{                                                     // Ben's recorded voices, if present next to the file
    if(voiceAudio){ voiceAudio.pause(); }
    voiceAudio = new Audio('voices/'+id+'.mp3'); voiceAudio.volume = 0.9;
    voiceAudio.play().catch(()=>{});
  }catch(e){}
}
function endVoice(){
  if(!voiceFocus) return;
  const vt = voiceTargets.find(t=>t.userData.voice===voiceFocus);
  if(vt && vt.userData.rig && vt.userData.rig.meet){
    const r = vt.userData.rig;
    r.meet = false; r.setPose(r.meetPrev.pose); r.root.rotation.y = r.meetPrev.yaw;
  }
  voiceFocus = null; voicebar.style.display = 'none';
  try{ SFX.setMode(nav==='walk'?'walk':'plan', 0.8); }catch(e){}
  if(!chainActive){ conEl.style.display='none'; conSeat = null; }
  document.getElementById('scenes').style.display='flex';
  document.getElementById('btns').style.display='flex';
  if(voiceAudio){ try{ voiceAudio.pause(); }catch(e){} voiceAudio = null; }
  controls.minDistance = FITMIN;
  if(FITAZ){ controls.minAzimuthAngle = FITAZ[0]; controls.maxAzimuthAngle = FITAZ[1]; }
  if(FITPOL){ controls.minPolarAngle = FITPOL[0]; controls.maxPolarAngle = FITPOL[1]; }
  controls.enabled = false;
  tween = voiceReturn
    ? { p0:camera.position.clone(), t0:controls.target.clone(), p1:voiceReturn.p, t1:voiceReturn.t, t:0 }
    : { p0:camera.position.clone(), t0:controls.target.clone(), t:0 };
  voiceReturn = null;
}
document.getElementById('cardX').addEventListener('click', ()=>document.getElementById('card').style.display='none');
window.addEventListener('keydown', e=>{ if(e.key==='Escape') endVoice(); });

/* hover: names on people, pointer cursor */
const HOVER_TAGS = {};
for(const vid in VOICES){ const s = makeLabel(VOICES[vid].name, '#e9e3d4', 0.42);
  s.visible = false; world.add(s); HOVER_TAGS[vid] = s; }
let hoverId = null, hoverLast = 0;
renderer.domElement.addEventListener('pointermove', e=>{
  const now = performance.now(); if(now-hoverLast < 90) return; hoverLast = now;
  ptr.x = (e.clientX/innerWidth)*2-1; ptr.y = -(e.clientY/innerHeight)*2+1;
  ray.setFromCamera(ptr, camera);
  const hits = ray.intersectObjects(voiceTargets, true);
  const id = hits.length ? hits[0].object.userData.voice : null;
  if(id !== hoverId){
    if(hoverId && HOVER_TAGS[hoverId]) HOVER_TAGS[hoverId].visible = false;
    hoverId = id;
    renderer.domElement.style.cursor = id ? 'pointer' : '';
    if(id) try{ SFX.cue('ui-hover'); }catch(e){}
  }
  try{ if(!id) IX.onPointerMove(ptr, e); else IX.highlight(null); }catch(err){}
  const tag = hoverId ? HOVER_TAGS[hoverId] : null;
  const rig = hits.length ? hits[0].object.userData.rig : null;
  if(tag && rig && rig.root && !voiceFocus){
    const p = rig.root.position, sc = rig.root.scale.x;
    tag.position.set(p.x, (rig.pose==='stand'?1.95:1.5)*sc, p.z);
    tag.visible = true; tag.material.opacity = 1;
  }
});

/* ---------- camera fit ---------- */
const fitDir = new THREE.Vector3(0.44, 1.66, 1.0).normalize();
let fitPos = new THREE.Vector3(), fitTarget = new THREE.Vector3();
function computeFit(){
  const corners = [];
  [[-25.6,-14.6],[32.6,-14.6],[-25.6,14.6],[32.6,14.6]].forEach(p=>corners.push(new THREE.Vector3(p[0],-0.9,p[1])));
  LBL.forEach(s=>{
    corners.push(new THREE.Vector3(s.position.x - s.scale.x/2, s.position.y + s.scale.y/2, s.position.z));
    corners.push(new THREE.Vector3(s.position.x + s.scale.x/2, s.position.y + s.scale.y/2, s.position.z));
  });
  const c = new THREE.Vector3(3.5, 0.6, 0);
  let dist = 70;
  for(let k=0;k<6;k++){
    camera.position.copy(c).addScaledVector(fitDir, dist);
    camera.lookAt(c); camera.updateMatrixWorld(true);
    let m = 0;
    corners.forEach(v=>{ const p=v.clone().project(camera); m=Math.max(m, Math.abs(p.x), Math.abs(p.y)); });
    dist *= m/0.965;
  }
  fitPos.copy(camera.position); fitTarget.copy(c);
  controls.target.copy(c);
  controls.minDistance = dist*0.45; controls.maxDistance = dist*1.18;   // stay inside the skyline
  controls.update();
}
computeFit();
/* the world outside: sky, city, streets far below — the floor stops being a diorama */
const WORLD = buildWorld(scene, { camera });
/* the floor has a room tone — nothing sounds until the first gesture */
const SFX = buildSound();
/* in-world interactivity: seat-state lamps, hover examine, focus ring, path preview */
const IX = buildInteract({
  THREE, scene, world, camera, renderer,
  seats: Object.keys(VOICES),
  getSeatAnchor: id => {                                     // the desk surface, not the person
    const p = SEAT_POS[id]; if(!p) return null;
    const d = SEAT_DOOR[id];
    return { x:p[0], y:0.80, z:p[1], yaw: d ? Math.atan2(d[0]-p[0], d[1]-p[1]) : 0 };
  },
  seatName: id => (VOICES[id] ? VOICES[id].name : String(id).toUpperCase()),
  canStand: (x, z) => { const c = collideWalk(x, z); return Math.abs(c[0]-x) < 1e-6 && Math.abs(c[1]-z) < 1e-6; },
});
onSeatState((id, st)=>{ try{ IX.setSeatState(id, st); }catch(e){} });
const startAudio = ()=>{
  try{
    SFX.start();
    LAMP_POS.forEach((p,i)=>SFX.place('lamp'+i, p[0], p[1], p[2], 'lamp'));
    for(const id in SEAT_POS){ const p = SEAT_POS[id]; SFX.place('scr_'+id, p[0], 1.0, p[1], 'screen'); }
    SFX.place('server', 27, 1.2, -6, 'server');
    SFX.place('printer', 4.6, 0.9, 5.2, 'printer');
    SFX.place('clock', -7, 2.0, -9.4, 'clock');
    SFX.place('street', -24, 6, 0, 'street');
  }catch(e){}
};
addEventListener('pointerdown', startAudio, { once:true });
addEventListener('keydown', startAudio, { once:true });
UNDERSIDE.parts.shift().removeFromParent();                 // the void plane hid the streets
controls.zoomToCursor = true;                            // scroll pulls you toward what you point at
controls.minDistance = 1.2;                              // walk right up to a face
controls.minAzimuthAngle = -Infinity;                    // full 360° orbit
controls.maxAzimuthAngle = Infinity;
controls.minPolarAngle = 0.04; controls.maxPolarAngle = 1.44;   // the slab has no underside to show
FITMIN = controls.minDistance; FITAZ = [controls.minAzimuthAngle, controls.maxAzimuthAngle];
FITPOL = [controls.minPolarAngle, controls.maxPolarAngle];
window.__fleet = { RIGS, ACTORS, camera, controls, POST, renderer, scene, WORLD, runPing, runScene, startChain: (s,w)=>startChain(s,w),
  converse: ()=>{ const s = stepConverse(); s.start(); return 'talking'; }, focusVoice: (id,inst)=>{ const m=voiceTargets.find(v=>v.userData.voice===id); if(m) focusVoice(id, m.userData.rig, inst); }, endVoice, get voice(){ return voiceFocus; }, get busy(){ return busy; },
  get dbg(){ const o={qlen: sceneQ?sceneQ.length:-1, cur: sceneCur?Object.keys(sceneCur).join(','):null, curT: sceneCur&&sceneCur.t, ping: ping&&ping.phase, door: doorOpenT};
    for(const id in ACTORS){ const a=ACTORS[id]; o[id]=[+a.root.position.x.toFixed(1),+a.root.position.z.toFixed(1),a.pose,a.isWalking?1:0,a.anim?a.anim.kind:'']; } return o; } };
if(location.hash.startsWith('#scene-')) setTimeout(()=>runScene(location.hash.slice(7)), 800);   // deep-link a scene
if(location.hash.startsWith('#voice-')) setTimeout(()=>window.__fleet.focusVoice(location.hash.slice(7), true), 900);   // deep-link a close-up
if(location.hash.startsWith('#cam-')){ const n = location.hash.slice(5).split(',').map(Number);   // #cam-x,y,z,tx,ty,tz debug framing
  if(n.length===6 && n.every(v=>!isNaN(v))) setTimeout(()=>{ camera.position.set(n[0],n[1],n[2]);
    controls.target.set(n[3],n[4],n[5]); camera.lookAt(n[3],n[4],n[5]); LBL.forEach(s=>s.material.opacity=0.15); }, 900); }
if(location.hash==='#chain-test') setTimeout(()=>startChain('janna','Three items today. One needs your YES. The rest are handled and filed.'), 1200);
/* ?glb — proof the skinned-character pipeline is live: an animated visitor walks the corridor */
if(location.search.includes('glb')){
  new GLTFLoader().load('/assets/Soldier.glb', g=>{
    const m = g.scene; m.scale.setScalar(1.0); m.traverse(o=>{ if(o.isMesh){ o.castShadow=true; } });
    m.position.set(24,0,0); m.rotation.y = -Math.PI/2; world.add(m);
    const mixer = new THREE.AnimationMixer(m);
    const walk = g.animations.find(a=>/walk/i.test(a.name)) || g.animations[0];
    mixer.clipAction(walk).play();
    GLB_DEMO = { m, mixer, dir:-1 };
  }, undefined, ()=>{});
}
let GLB_DEMO = null;
let tween = null;
let introT = 0, introOn = !location.hash, lastAct = performance.now();
const skipIntro = ()=>{ if(introOn){ introOn = false; controls.enabled = true;
  camera.position.copy(fitPos); controls.target.copy(fitTarget); camera.lookAt(fitTarget); }
  lastAct = performance.now(); };
addEventListener('pointerdown', skipIntro);
addEventListener('wheel', skipIntro, { passive:true });
addEventListener('keydown', skipIntro);
document.getElementById('fitBtn').addEventListener('click', ()=>{
  if(voiceFocus){ endVoice(); return; }
  exitWalk(false);
  roofRestore();
  tween = { p0:camera.position.clone(), t0:controls.target.clone(), t:0 };
});

/* ---------- walk mode: first-person, wall-aware ---------- */
let nav = 'orbit', walkYaw = 0, walkPitch = 0, walkY = 1.62, walkTarget = null, walkBob = 0, walkNear = null;
const KEYS = new Set();
const hintEl = document.getElementById('hint');
/* the opening card: the name holds over the descent, then gets out of the way */
(function openingCard(){
  if(location.hash) return;
  const c = document.createElement('div');
  c.style.cssText = 'position:fixed;inset:0;z-index:40;display:flex;flex-direction:column;'
    + 'align-items:center;justify-content:center;pointer-events:none;'
    + 'background:radial-gradient(ellipse at 50% 50%, rgba(10,10,13,.34), rgba(8,8,11,.86));'
    + 'opacity:0;transition:opacity 1.2s ease';
  c.innerHTML = '<div style="font:300 clamp(28px,7vw,74px)/1 Futura,Avenir,system-ui,sans-serif;'
    + 'letter-spacing:.42em;color:#efe7d4;text-indent:.42em">AGENTSEE</div>'
    + '<div style="margin-top:22px;font:400 clamp(9px,1.5vw,13px)/1 Futura,Avenir,system-ui,sans-serif;'
    + 'letter-spacing:.34em;text-transform:uppercase;color:rgba(214,203,178,.72);text-indent:.34em">'
    + 'Officially, Art &amp; Intelligence.</div>';
  const bar = document.createElement('div');
  bar.style.cssText = 'margin-top:34px;width:min(240px,42vw);height:1px;background:rgba(214,203,178,.18);overflow:hidden';
  const fill = document.createElement('div');
  fill.style.cssText = 'height:100%;width:0%;background:rgba(226,214,186,.75);transition:width .35s ease';
  bar.appendChild(fill); c.appendChild(bar);
  LOAD.onProgress = (_u, done, total)=>{ fill.style.width = Math.round(done/Math.max(1,total)*100)+'%'; };
  LOAD.onLoad = ()=>{ fill.style.width = '100%'; };
  document.body.appendChild(c);
  const title = document.getElementById('title');
  if(title){ title.style.opacity = '0'; title.style.transition = 'opacity 1.4s ease'; }
  setTimeout(()=>{ c.style.opacity = '1'; }, 40);   // not rAF: a backgrounded tab would never show it
  const t0 = performance.now();
  const clear = ()=>{
    c.style.opacity = '0';
    if(title) title.style.opacity = '1';
    setTimeout(()=>c.remove(), 1400);
  };
  let assetsIn = false;
  const prevLoad = LOAD.onLoad;
  LOAD.onLoad = ()=>{ if(prevLoad) prevLoad(); assetsIn = true; };
  const tryClear = ()=>{ if(assetsIn || performance.now() - t0 > 9000) clear(); else setTimeout(tryClear, 250); };
  setTimeout(tryClear, 4200);
  const skip = ()=>{ if(performance.now() - t0 > 900) clear(); };   // a stray click at load must not eat the card
  addEventListener('pointerdown', skip);
  addEventListener('keydown', skip);
})();
const prompt = (()=>{                                        // the reach prompt in walk mode
  const d = document.createElement('div');
  d.style.cssText = 'position:fixed;left:50%;bottom:20%;transform:translateX(-50%);z-index:6;'
    + 'font:500 12px/1 Futura,Avenir,system-ui,sans-serif;letter-spacing:.16em;text-transform:uppercase;'
    + 'color:#e6dfcc;background:rgba(14,14,18,.62);border:1px solid rgba(230,223,204,.22);'
    + 'padding:9px 15px;border-radius:3px;pointer-events:none;display:none;backdrop-filter:blur(6px);';
  document.body.appendChild(d); return d;
})();
const HINT_ORBIT = hintEl.textContent;
const HINT_WALK = 'WASD / arrows to walk · drag to look · click the floor to glide there · click a person to meet them · Esc or Plan to lift off';
/* doors and furniture the walls don’t cover */
[[-15,-1.1,-15,1.1],                                          // Ben's doorway — locked
 [19.9,1.13,22.1,1.13],[19.9,1.13,19.9,2],[22.1,1.13,22.1,2], // WC pod
 [23.15,0.66,24.65,0.66],[22.9,-0.6,24.9,-0.6],               // security desk + rope line
].forEach(s=>WALL_SEGS.push(s));
function collideWalk(x, z){
  const R = 0.42;
  for(let pass=0; pass<2; pass++){
    for(const [ax,az,bx,bz] of WALL_SEGS){
      const abx = bx-ax, abz = bz-az;
      const L2 = abx*abx + abz*abz; if(!L2) continue;
      let u = ((x-ax)*abx + (z-az)*abz) / L2; u = u<0?0:(u>1?1:u);
      const px = ax + abx*u, pz = az + abz*u;
      let dx = x-px, dz = z-pz;
      const d2 = dx*dx + dz*dz;
      if(d2 < R*R && d2 > 1e-8){
        const d = Math.sqrt(d2); dx/=d; dz/=d;
        x = px + dx*R; z = pz + dz*R;
      }
    }
  }
  return [Math.max(-22.55, Math.min(29.7, x)), Math.max(-12.2, Math.min(12.2, z))];
}
/* two lighting grades: the plan view is a lit model, inside is a room at dusk */
const GRADES = {
  plan: { exposure:1.15, env:0.55, hemi:0.34, key:3.4,  fog:0.0052 },
  walk: { exposure:1.00, env:0.32, hemi:0.17, key:1.55, fog:0.0135 },
};
const GRADE = Object.assign({}, GRADES.plan);
let gFrom = null, gTo = null, gT = 1;
function setGrade(name){ gFrom = Object.assign({}, GRADE); gTo = GRADES[name]; gT = 0; }
function applyGrade(dt){
  if(gT < 1){
    gT = Math.min(1, gT + dt/0.9);
    const k = 1-Math.pow(1-gT, 3);
    for(const key in gTo) GRADE[key] = gFrom[key] + (gTo[key]-gFrom[key])*k;
  }
  renderer.toneMappingExposure = GRADE.exposure;
  scene.environmentIntensity   = GRADE.env;
  hemi.intensity               = GRADE.hemi;
  key.intensity                = GRADE.key;
  scene.fog.density            = GRADE.fog;
}
function enterWalk(){
  if(nav === 'walk') return;
  setGrade('walk');
  try{ SFX.setMode('walk', 0.9); }catch(e){}
  nav = 'walk'; walkTarget = null;
  if(roofOn){ roofOn = false; roofFadeT = 0; }
  const d = camera.getWorldDirection(new THREE.Vector3());
  walkYaw = Math.atan2(-d.x, -d.z);
  walkPitch = Math.max(-0.5, Math.min(0.35, Math.asin(Math.max(-1, Math.min(1, d.y)))));
  walkY = camera.position.y;
  const [cx, cz] = collideWalk(camera.position.x, camera.position.z);
  camera.position.x = cx; camera.position.z = cz;
  controls.enabled = false;
  document.getElementById('walkBtn').textContent = 'Plan';
  hintEl.textContent = HINT_WALK;
}
function exitWalk(refit=true){
  if(nav !== 'walk') return;
  setGrade('plan');
  try{ SFX.setMode('plan', 0.9); }catch(e){}
  nav = 'orbit'; walkTarget = null; walkNear = null;
  prompt.style.display = 'none';
  controls.enabled = true;
  const d = camera.getWorldDirection(new THREE.Vector3());
  controls.target.copy(camera.position).addScaledVector(d, 4);
  document.getElementById('walkBtn').textContent = 'Walk';
  hintEl.textContent = HINT_ORBIT;
  if(refit){ roofRestore(); tween = { p0:camera.position.clone(), t0:controls.target.clone(), t:0 }; }
}
document.getElementById('walkBtn').addEventListener('click', ()=>{
  if(voiceFocus) endVoice();
  nav === 'walk' ? exitWalk(true) : enterWalk();
});
/* jump straight to any room — works in both modes */
{
  const js = document.getElementById('jumpSel');
  ROOMS.forEach(r=>{ const o=document.createElement('option'); o.value=r.id; o.textContent=r.name; js.appendChild(o); });
  js.addEventListener('change', ()=>{
    const r = ROOMS.find(x=>x.id===js.value); js.value='';
    if(!r) return;
    if(voiceFocus) endVoice();
    followId = null;
    const cx=(r.rect[0]+r.rect[2])/2, cz=(r.rect[1]+r.rect[3])/2;
    const north = r.rect[3] <= 0;
    if(nav === 'walk'){                                       // glide there on foot
      walkTarget = { x:cx, z: north ? Math.max(cz, r.rect[1]+2) : Math.min(cz, r.rect[3]-2) };
      return;
    }
    if(roofOn){ roofOn = false; roofFadeT = 0; }
    controls.enabled = false;
    const cpz = north ? r.rect[3]+1.6 : r.rect[1]-1.6;        // stand in the corridor, look in
    let p1 = new THREE.Vector3(cx, 3.1, cpz), t1 = new THREE.Vector3(cx, 1.0, cz);
    if(r.id==='ben'){ p1.set(-13.4, 2.6, 0.6); t1.set(-19, 1.0, 0); }      // through the one door
    if(r.id==='grants'){ p1.set(23.6, 3.1, 0); t1.set(27.5, 1.0, 0); }     // from the corridor end
    tween = { p0:camera.position.clone(), t0:controls.target.clone(), p1, t1, t:0, dur:1.1 };
  });
}
addEventListener('keydown', e=>{
  const a = document.activeElement;
  if(a && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA')) return;
  if(e.code === 'Escape' && nav === 'walk'){ exitWalk(true); return; }
  KEYS.add(e.code);
  if(nav === 'walk' && /^(Arrow|Key[WASD])/.test(e.code)) e.preventDefault();
});
addEventListener('keyup', e=>KEYS.delete(e.code));
addEventListener('blur', ()=>KEYS.clear());
let lookLast = null;
renderer.domElement.addEventListener('pointermove', e=>{
  if(nav !== 'walk' || voiceFocus || tween) { lookLast = null; return; }
  if(!(e.buttons & 1)) { lookLast = null; return; }
  if(lookLast){
    walkYaw   -= (e.clientX - lookLast[0]) * 0.0044;
    walkPitch -= (e.clientY - lookLast[1]) * 0.0032;
    walkPitch = Math.max(-1.05, Math.min(1.05, walkPitch));
  }
  lookLast = [e.clientX, e.clientY];
});
renderer.domElement.addEventListener('pointerup', ()=>{ lookLast = null; });
/* the orbit camera may not end up inside a desk, a wall, or under the floor at close range.
   Orbiting right under the building is still allowed — that only reads from a distance. */
const IN_PLAN = (x, z) => x > -24.5 && x < 31.5 && z > -14 && z < 14;
function constrainCamera(){
  if(nav === 'walk' || introOn) return;                      // walkStep and the arrival own the camera
  const p = camera.position;
  const far = Math.hypot(p.x - fitTarget.x, p.z - fitTarget.z);
  if(p.y < 0.34) p.y += (0.34 - p.y) * 0.35;                 // never under the plate
  if(p.y < 2.55 && IN_PLAN(p.x, p.z)){                        // inside the building at head height
    const c = collideWalk(p.x, p.z);
    const dx = c[0] - p.x, dz = c[1] - p.z;
    if(dx*dx + dz*dz > 4e-4){                                  // ease out, or damping fights it into a jitter
      p.x += dx * 0.28; p.z += dz * 0.28;
    }
    if(p.y < 0.42) p.y += (0.42 - p.y) * 0.3;
  }
}
function walkStep(dt){
  if(nav !== 'walk' || tween || voiceFocus) return;
  const run = KEYS.has('ShiftLeft') || KEYS.has('ShiftRight');
  let mf = 0, ms = 0;
  if(KEYS.has('KeyW') || KEYS.has('ArrowUp')) mf += 1;
  if(KEYS.has('KeyS') || KEYS.has('ArrowDown')) mf -= 1;
  if(KEYS.has('KeyA') || KEYS.has('ArrowLeft')) ms -= 1;
  if(KEYS.has('KeyD') || KEYS.has('ArrowRight')) ms += 1;
  let x = camera.position.x, z = camera.position.z;
  if(mf || ms){
    walkTarget = null;
    const sp = (run ? 6.2 : 3.4) * dt / Math.hypot(mf, ms);
    const fx = -Math.sin(walkYaw), fz = -Math.cos(walkYaw);
    const rx =  Math.cos(walkYaw), rz = -Math.sin(walkYaw);
    x += (fx*mf + rx*ms) * sp; z += (fz*mf + rz*ms) * sp;
  } else if(walkTarget){
    const dx = walkTarget.x - x, dz = walkTarget.z - z, d = Math.hypot(dx, dz);
    if(d < 0.25) walkTarget = null;
    else {
      const sp = Math.min(3.6*dt, d); x += dx/d*sp; z += dz/d*sp;
      const want = Math.atan2(-dx, -dz);                      // ease the gaze into the glide
      let dy = want - walkYaw; while(dy>Math.PI)dy-=2*Math.PI; while(dy<-Math.PI)dy+=2*Math.PI;
      walkYaw += dy * Math.min(1, dt*2.6);
      walkPitch += (0 - walkPitch) * Math.min(1, dt*2);
    }
  }
  const moved = Math.hypot(x - camera.position.x, z - camera.position.z);
  [x, z] = collideWalk(x, z);
  const bobPrev = walkBob;
  walkBob += moved * (run ? 5.4 : 6.8);                        // the head rides the stride
  if(moved > 1e-4 && Math.floor(bobPrev/Math.PI) !== Math.floor(walkBob/Math.PI)){
    const surf = Math.abs(z) < 1.3 ? 'parquet' : (x > 22 ? 'concrete' : 'carpet');
    try{ SFX.footstep(surf, run ? 1 : 0.6); }catch(e){}
  }
  const bobY = Math.sin(walkBob) * 0.028, bobR = Math.sin(walkBob*0.5) * 0.012;
  walkY += (1.62 - walkY) * Math.min(1, dt*4);
  camera.position.set(x, walkY + bobY, z);
  camera.rotation.order = 'YXZ';
  camera.rotation.set(walkPitch, walkYaw, bobR);
  /* who is within reach — the prompt tells you that you can just talk to them */
  let near = null, nd = 3.2;
  for(const vt of voiceTargets){
    const r = vt.userData.rig; if(!r || !r.root) continue;
    const d = Math.hypot(r.root.position.x - x, r.root.position.z - z);
    if(d < nd){ nd = d; near = vt.userData.voice; }
  }
  if(near !== walkNear){
    walkNear = near;
    prompt.textContent = near && VOICES[near] ? 'click to talk to ' + VOICES[near].name : '';
    prompt.style.display = near ? 'block' : 'none';
  }
}

/* ---------- loop ---------- */
const clock = new THREE.Clock();
let simT = 0, perfAcc = 0, perfN = 0, perfTier = 0;
function LOOP(fdt){
  const dt = (fdt!==undefined ? fdt : Math.min(clock.getDelta(), 0.05)) * (window.__fleet.timeScale||1);
  simT += dt; const t = fdt!==undefined ? simT : (simT = clock.elapsedTime);

  flow.forEach(f=>{
    const span = 39.4, v = f.lane>0 ? 2.6 : 1.9;
    const s = ((f.off + t*v) % span);
    f.m.position.x = f.lane>0 ? -14.6+s : 24.8-s;
    f.m.position.z = f.lane>0 ? 1.4 : -1.4;
  });

  /* door */
  doorPivot.rotation.y = -1.62 * (doorOpenT<0?0:(doorOpenT>1?1:doorOpenT*doorOpenT*(3-2*doorOpenT)));

  /* orbiting below the table: the plinth steps aside and a soft uplight reads the floors */
  { const below = camera.position.y < 0.15;
    if(UNDERSIDE.parts[0].visible === below){
      UNDERSIDE.parts.forEach(p=>p.visible = !below);
      UNDERSIDE.light.visible = below;
    } }

  /* skinned bodies: gait blend + acted poses on top of the clips */
  for(const a of RIGS) if(a.skinned && a.applySkinned) a.applySkinned(dt, t);
  /* particle agents: breath, energy, glide lean */
  for(const a of RIGS) if(a.orb && a.applyOrb) a.applyOrb(dt, t);
  /* whoever is mid-sentence owns the room's attention */
  SPEAKER = null;
  refreshSeatStates();
  { const now = performance.now();
    for(const r of RIGS) if(r.speakUntil > now){ SPEAKER = r; break; } }
  /* figure animation — everyone breathes, scenes override */
  for(const a of RIGS){
    a.lean += (((a.leanTo||0) + (a.isWalking?0.09:0)) - a.lean) * Math.min(1, dt*6);
    const up = a.pose==='stand' ? a.upper : a.upSeat;
    const head = a.pose==='stand' ? a.headS : a.headSeat;
    up.rotation.x = a.lean + Math.sin(t*1.6+a.idle)*0.013;                 // breathing
    if(a.anim){
      const an = a.anim; an.t += dt;
      if(an.kind==='nod') head.rotation.x = Math.sin(an.t*7)*0.24*(1-an.t/an.dur);
      if(an.kind==='shake') head.rotation.y = Math.sin(an.t*8)*0.3*(1-an.t/an.dur);
      if(an.kind==='argue'){
        head.rotation.y = Math.sin(an.t*6)*0.2;
        const k = Math.max(0, Math.sin(an.t*5));
        if(a.pose==='stand'){ a.armR.rotation.x = -k*0.9; a.armL.rotation.x = -Math.max(0,Math.sin(an.t*5+2.4))*0.7; }
      }
      if(an.kind==='type' && a.pose==='sit'){
        a.foreL.rotation.x = -0.62 - Math.abs(Math.sin(an.t*22))*0.06;
        a.foreR.rotation.x = -0.62 - Math.abs(Math.sin(an.t*22+1.3))*0.06;
      }
      if(an.t>=an.dur){
        head.rotation.set(0,0,0);
        if(a.pose==='stand'){ a.armL.rotation.x=0; a.armR.rotation.x = envCarrier===a.id?-1.05:0; }
        a.anim = null;
      }
    } else if(a.meet){
      const dx = camera.position.x - a.root.position.x, dz = camera.position.z - a.root.position.z;
      let d = Math.atan2(dx,dz) - a.root.rotation.y;
      while(d>Math.PI)d-=2*Math.PI; while(d<-Math.PI)d+=2*Math.PI;
      a.root.rotation.y += d*Math.min(1, dt*5);                            // turns to face you
      head.rotation.y = Math.sin(t*0.3+a.idle)*0.03;                       // holds your eye
      head.rotation.x = 0.02;
    } else if(!a.isWalking){
      /* the room listens: heads turn toward whoever is talking nearby */
      let look = null;
      if(SPEAKER && SPEAKER !== a){
        const dx = SPEAKER.root.position.x - a.root.position.x,
              dz = SPEAKER.root.position.z - a.root.position.z;
        const d2 = dx*dx + dz*dz;
        if(d2 < 90){                                                       // within ~9.5m, same conversation
          let yaw = Math.atan2(dx, dz) - a.root.rotation.y;
          while(yaw > Math.PI) yaw -= 2*Math.PI; while(yaw < -Math.PI) yaw += 2*Math.PI;
          if(Math.abs(yaw) < 1.35)                                         // only if they wouldn't have to spin round
            look = { y: Math.max(-0.85, Math.min(0.85, yaw)), x: -0.04 };
        }
      }
      const gy = look ? look.y : Math.sin(t*0.4+a.idle)*0.13,              // otherwise, idle glances
            gx = look ? look.x : Math.sin(t*0.9+a.idle*1.7)*0.035;
      const kk = Math.min(1, dt*(look?3.4:1.8));
      head.rotation.y += (gy - head.rotation.y)*kk;
      head.rotation.x += (gx - head.rotation.x)*kk;
      if(a.style==='type' && a.pose==='sit'){                              // keys never stop
        a.foreL.rotation.x = -0.62 - Math.abs(Math.sin(t*13+a.idle))*0.04;
        a.foreR.rotation.x = -0.62 - Math.abs(Math.sin(t*13+a.idle+1.2))*0.04;
      }
    }
  }

  /* ambient life: screens flicker, lamps breathe, dust drifts, the standard sways */
  SCREENS.forEach((s,i)=>{ s.m.emissiveIntensity = s.base*(0.97 + 0.02*Math.sin(t*2.1+i*2.7)); });
  tvTick(t); crtTick(t);
  if(roofFadeT >= 0){                                            // the glass lifts away
    roofFadeT += dt*2.2;
    const k = Math.max(0, 1-roofFadeT);
    ROOF.traverse(o=>{ if(o.material) o.material.opacity = (o===roofPlate ? 0.08 : 0.16)*k; });
    if(roofFadeT >= 1){ ROOF.visible = false; roofFadeT = -1; }
  }
  LAMP_BEN.intensity = 30*(0.99 + 0.01*Math.sin(t*2.3));
  LAMP_CW.intensity  = 22*(0.99 + 0.01*Math.sin(t*1.9+2));
  pennant.rotation.y = Math.sin(t*1.15)*0.22 + Math.sin(t*2.3)*0.06;
  CANDLES.forEach(c=>{ c.m.emissiveIntensity = c.base*(0.92 + 0.07*Math.sin(t*5+c.ph)); });
  BLINKS.forEach(b=>{ b.m.emissiveIntensity = (Math.sin(t*3.1+b.ph)>0.2?1.4:0.15); });
  TICKERS.forEach(k=>{ k.offset.x = (t*0.045)%1; });
  DUST.forEach(d=>{
    d.s.position.x += d.sp*dt; d.s.position.y += Math.sin(t*0.7+d.ph)*0.0006;
    if(d.s.position.x>-20.5) d.s.position.x = -22.75;
    d.s.material.opacity = 0.07 + 0.05*Math.max(0,Math.sin(t*0.8+d.ph));
  });

  /* background errands */
  if(!busy){
    ambNext -= dt;
    if(ambNext<=0 && !ambQ && !ambCur){ ambQ = AMBIENTS[Math.floor(Math.random()*AMBIENTS.length)](); ambNext = 20 + Math.random()*18; }
  }
  if(ambQ || ambCur){
    if(!ambCur){ ambCur = ambQ && ambQ.shift(); if(ambCur) ambCur.start(); else ambQ=null; }
    if(ambCur && ambCur.update(dt)) ambCur = null;
    if(ambCur===null && ambQ && ambQ.length===0) ambQ=null;
  }

  /* the letter follows its carrier */
  if(envCarrier){
    const a = ACTORS[envCarrier], yaw = a.root.rotation.y;
    env.position.set(a.root.position.x + Math.sin(yaw)*0.32, a.root.position.y + 1.22, a.root.position.z + Math.cos(yaw)*0.32);
    env.rotation.set(0, yaw, 0);
  }

  /* scenes */
  if(sceneQ){
    if(!sceneCur){
      sceneCur = sceneQ.shift();
      if(sceneCur) sceneCur.start(); else { sceneQ=null; setBusy(false); }
    }
    if(sceneCur && sceneCur.update(dt)) sceneCur = null;
  }

  /* pings + outbound send */
  if(ping){
    const speed = 15;
    if(ping.phase==='wait'){
      ping.wait -= dt;
      if(ping.wait<=0){ ping.phase='back'; ping.s = ping.path.total; }
    } else {
      ping.s += (ping.phase==='back'?-1:1) * speed * dt;
      if(ping.phase==='out' && ping.s>=ping.path.total){ ping.phase='wait'; ping.wait=0.55; pulse(ping.room); }
      if(ping.phase==='send' && ping.s>=ping.path.total){
        packet.visible=false; packet.userData.light.intensity=0; ping=null;
        trail.forEach(s=>s.material.opacity=0);
      }
      if(ping && ping.phase==='back' && ping.s<=0){
        packet.visible=false; packet.userData.light.intensity=0;
        say(ping.answer, new THREE.Vector3(-18.6,3.0,0)); ping=null; setBusy(false);
        trail.forEach(s=>s.material.opacity=0);
      }
    }
    if(ping){
      const p = ping.path.at(ping.s);
      packet.position.set(p[0], 1.15+Math.sin(t*9)*0.05, p[1]);
      for(let i=trail.length-1;i>0;i--){ trail[i].position.copy(trail[i-1].position); trail[i].material.opacity = 0.3*(1-i/trail.length); }
      trail[0].position.copy(packet.position);
      const dDoor = Math.hypot(packet.position.x+14.97, packet.position.z);
      doorMat.emissiveIntensity = 0.22 + (dDoor<1.4 ? (1.4-dDoor)*0.6 : 0);
    }
  } else if(!(sceneQ||sceneCur)) {
    doorMat.emissiveIntensity += (0.22-doorMat.emissiveIntensity)*0.1;
  }

  for(let i=pulses.length-1;i>=0;i--){
    const p = pulses[i]; p.t += dt;
    const k = Math.max(0, 1-p.t/0.9);
    p.m.material.emissive.setHex(0x8a6a3a); p.m.material.emissiveIntensity = k*0.5;
    if(k<=0){ p.m.material.emissiveIntensity = 0; pulses.splice(i,1); }
  }

  if(bubble.style.display==='block'){
    if(performance.now()>bubbleUntil){ bubble.style.display='none'; }
    else {
      const v = bubbleAnchor.clone().project(camera);
      bubble.style.left = ((v.x*0.5+0.5)*innerWidth)+'px';
      bubble.style.top  = ((-v.y*0.5+0.5)*innerHeight)+'px';
    }
  }

  /* the arrival: six seconds descending out of the dark onto the floor */
  if(introOn){
    introT += dt;
    const k = introT / establishing.DUR;
    const s = establishing({ centre: fitTarget, fitPos }, k);
    camera.position.copy(s.pos); controls.target.copy(s.target);
    camera.lookAt(controls.target);
    controls.enabled = false;
    if(k >= 1){ introOn = false; controls.enabled = true; lastAct = performance.now(); }
  }
  /* nobody home: the camera keeps the floor alive on its own */
  else if(!tween && !voiceFocus && !followId && nav !== 'walk' && performance.now() - lastAct > 45000){
    const s = attractDrift(fitTarget, t, camera.position.distanceTo(fitTarget));
    camera.position.lerp(s.pos, Math.min(1, dt*0.6));
    controls.target.lerp(s.target, Math.min(1, dt*0.6));
    camera.lookAt(controls.target);
  }
  if(tween && !introOn){
    tween.t += dt/(tween.dur||0.7);
    const k = tween.t>=1 ? 1 : (tween.ease||Ease.cubicOut)(tween.t);
    camera.position.lerpVectors(tween.p0, tween.p1||fitPos, k);
    controls.target.lerpVectors(tween.t0, tween.t1||fitTarget, k);
    if(tween.t>=1){
      const thenWalk = tween.thenWalk; tween = null;
      camera.lookAt(controls.target);
      if(thenWalk) enterWalk();
      if(nav === 'walk'){                                     // resync first-person state after any camera tween
        const d = camera.getWorldDirection(new THREE.Vector3());
        walkYaw = Math.atan2(-d.x, -d.z);
        walkPitch = Math.max(-1.05, Math.min(1.05, Math.asin(Math.max(-1, Math.min(1, d.y)))));
        walkY = camera.position.y;
        controls.enabled = false;
      } else controls.enabled = true;
    }
  }
  walkStep(dt);
  constrainCamera();
  try{ IX.setFocusSeat(voiceFocus); IX.setFollowTarget(chainActive ? followId : null); IX.update(dt, camera, ptr, nav); }catch(e){}
  /* walking alongside the courier — the camera keeps pace while the floor works a task */
  if((busy || (followId && chainActive)) && nav!=='walk' && !tween && !voiceFocus){
    /* the camera stays with the action: whoever is moving, else whoever is talking */
    let mover = null;
    for(const r of RIGS) if(r.isWalking && r.id && VOICES[r.id]){ mover = r; break; }
    const fr = mover || (SPEAKER && SPEAKER.root ? SPEAKER : ACTORS[followId]);
    if(fr){
      const s = followShot(fr, { time:t, speed: fr.walkSpeed || 0, back: fr.isWalking ? 2.6 : 2.0,
        spring: FOLLOW_SPRINGS, dt, out: FOLLOW_OUT });                    // springs, lead, drift — never a dead lerp
      camera.position.copy(s.pos);
      controls.target.copy(s.target);
      controls.enabled = false;
      camera.lookAt(controls.target);
    }
  } else if(!busy && followId && !chainActive){ followId = null; if(nav!=='walk' && !tween && !voiceFocus) controls.enabled = true; }
  else if(!busy && !chainActive && !voiceFocus && nav!=='walk' && !tween && !controls.enabled) controls.enabled = true;

  /* labels step back when the camera steps in */
  {
    const cd = camera.position.distanceTo(controls.target);
    const lf = voiceFocus ? 0 : Math.min(1, Math.max(0.08, (cd-13)/17));
    LBL.forEach(s=>{ s.material.opacity += (lf - s.material.opacity)*Math.min(1, dt*5); });
  }

  if(GLB_DEMO){
    GLB_DEMO.mixer.update(dt);
    GLB_DEMO.m.position.x += GLB_DEMO.dir * dt * 1.55;
    if(GLB_DEMO.m.position.x < -13){ GLB_DEMO.dir = 1; GLB_DEMO.m.rotation.y = Math.PI/2; }
    if(GLB_DEMO.m.position.x > 24){ GLB_DEMO.dir = -1; GLB_DEMO.m.rotation.y = -Math.PI/2; }
  }
  controls.update();
  /* the lens: shallow only when we are close to someone, deep for the floor view */
  { const d = camera.position.distanceTo(controls.target);
    if(d > 14) POST.focusOn(null);                             // wide shots stay sharp — no miniature effect
    else {
      POST.focusOn(controls.target);
      POST.passes.dof.uniforms.aperture.value = 0.00060 * Math.max(0.12, Math.min(1, 3.2/d));
    }
  }
  /* adaptive quality: if the frame is consistently late, shed the expensive passes */
  if(!FAST){
    perfAcc += dt; perfN++;
    if(perfAcc > 1.6){
      const avg = perfAcc/perfN;
      if(avg > 0.030 && perfTier < 2){                        // under ~33fps for a second and a half
        perfTier++;
        try{ POST.setQuality(perfTier === 1 ? 'balanced' : 'fast'); }catch(e){}
        if(perfTier === 2) renderer.setPixelRatio(1);
        console.log('[agentsee] quality ->', perfTier === 1 ? 'balanced' : 'fast', 'at', Math.round(1/avg), 'fps');
      }
      perfAcc = 0; perfN = 0;
    }
  }
  applyGrade(dt);
  try{ SFX.update(dt, camera); }catch(e){}

  renderer.shadowMap.needsUpdate = true;                     // once per frame; the DOF depth pass then skips it
  WORLD.update(dt, t, camera);
  POST.update(dt, controls.target);
  if(!PUMP_SKIP) composer.render();
}
let PUMP_SKIP = false;
renderer.setAnimationLoop(LOOP);
window.__fleet.pump = (n=60, step=0.033)=>{ PUMP_SKIP=true; for(let i=0;i<n;i++) LOOP(step); PUMP_SKIP=false; composer.render(); };

addEventListener('resize', ()=>{
  const v = VIEW();
  camera.aspect = v.a; camera.updateProjectionMatrix();
  renderer.setSize(v.w, v.h);
  POST.resize(v.w, v.h);
});

/* ---------- scanned stand-ins: FINAL declarations win the hoist over the box builders ---------- */
function plant(x, z, s=1){ return prop(Math.random()<0.5?'potted_plant_04':'potted_plant_01', x, z, Math.random()*6.28, {h:0.78*s}); }
function chair(x, z, ry=0){ return prop('dining_chair_02', x, z, ry, {h:0.9}); }
function couch(x, z, ry=0, w=1.9){ return prop('sofa_02', x, z, ry+Math.PI/2, {w:w}); }
function shelf(x, z, ry=0, w=2.4){
  const g = prop('wooden_bookshelf_worn', x, z, ry, {h:2.0, s:Math.max(0.8, w/2.4)});
  [0.06, 0.44, 0.83, 1.22, 1.61].forEach((lv,i)=>{                       // fill the real shelves with real books
    const off = (i%2?0.12:-0.1);
    prop('book_encyclopedia_set_01', x + Math.cos(ry)*off, z - Math.sin(ry)*off, ry, {w:0.6, y:lv});
  });
  return g;
}
function books(){ /* the real shelves carry their own */ }
/* a few scanned hero props on the desks */
prop('desk_lamp_arm_01', -19.1, -0.85, 2.2, {h:0.42, y:0.78});           // Ben's desk
prop('classic_laptop', -19.05, 0.42, -Math.PI/2, {w:0.34, y:0.78});
prop('desk_lamp_arm_01', -6.4, -8.15, 0.6, {h:0.42, y:0.78});            // Shakespeare's
prop('binder_notebook', 4.4, 5.55, 0.4, {w:0.3, y:0.78});                // Janna
prop('binder_notebook', -12.1, 5.5, -0.3, {w:0.3, y:0.78});              // Wrench
prop('book_encyclopedia_set_01', -6.2, -6.9, 0.3, {w:0.55, y:0.78});     // real books on the writer's table


/* ---------- the rooms become their people ---------- */
try {
  dressAll({ THREE, world, B, Cyl, M, prop, desk, chair, shelf, plant, papers, stack, mug,
             candle, pendant, WOODD, WOODL, PAPER, WALL, SCREENS, BLINKS, CANDLES });
} catch(e){ console.warn('[agentsee] dressing skipped:', e.message); }

/* ---------- surface pass: run last, once every material and mesh exists ---------- */
{
  for(const id in floorMesh){                                 // waxed parquet, not matte cardboard
    if(id === 'assange' || id === 'fe') continue;
    const m = floorMesh[id].material;
    if(m) floorMesh[id].material = SF.upgradeParquet(m);
  }
  const swap = new Map();                                     // grime the big flat surfaces
  [[WALL, {scale:1.35, strength:0.34, seed:3}],
   [WOODD, {scale:0.9, strength:0.24, seed:6}],
   [WOODL, {scale:0.9, strength:0.20, seed:11}]].forEach(([mat, o])=>{
    if(mat) swap.set(mat, SF.grime(mat, o));
  });
  world.traverse(o=>{ if(o.material && swap.has(o.material)) o.material = swap.get(o.material); });
  /* every character carries its own contact shadow so it never floats */
  for(const r of RIGS){
    const cs = SF.contactShadow(0.34, 0.42);
    cs.position.y = 0.045; r.root.add(cs); r.contact = cs;
  }
  /* shadow budget: only things big enough to cast a readable shadow do so */
  let dropped = 0, kept = 0;
  const _sb = new THREE.Box3(), _sz = new THREE.Vector3();
  world.traverse(o=>{
    if(!o.isMesh || !o.castShadow || o.isSkinnedMesh) return;
    if(o.isInstancedMesh){ o.castShadow = false; dropped++; return; }   // books, cash, city — receive only
    o.geometry.computeBoundingBox();
    _sb.copy(o.geometry.boundingBox).applyMatrix4(o.matrixWorld);
    _sb.getSize(_sz);
    if(Math.max(_sz.x, _sz.y, _sz.z) < 0.42){ o.castShadow = false; dropped++; }
    else kept++;
  });
  console.log('[agentsee] shadow casters kept', kept, 'dropped', dropped);
}
